/* eslint-disable no-unused-vars, no-await-in-loop */
import { createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { callApi } from 'src/utils/apiCall';
import { APPOINTMENT_STAGE, fetchAppointmentStageCounts } from 'src/utils/appointmentStages';
import { hasFeatureAccess } from 'src/utils/featurePermission';
// Shared multi-practice helpers — promoted out of this file in phase-1
// of the All Practices rollout so lead tables, widgets, campaigns, etc.
// can use the same `practice__in` suffix pattern.
import { toPracticeIds, practiceInSuffix } from 'src/utils/practiceInSuffix';
import {
    REPORT_STATES, STATE_LOG_KEY, buildStateParams, buildPerStateUrl,
    fetchPerStateGroupedRows, foldGroupedStatusRows, fetchPeriodStateTotals,
} from 'src/utils/perStateReport';
import { normalizeLandingUrl } from 'src/utils/normalizeLandingUrl';
import { getReportLeadContactCount } from 'src/utils/reportLead';
import { updateError } from './user';
import { hasPractitionerRole } from '../../utils/practiceUserRoles';
import { selectPracticeUsersForPractice } from './practiceUser';

export { fetchPeriodStateTotals };

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

// Parse API dates safely — backend mixes ISO ("2024-12-18T17:08:37Z") and
// DD-MM-YYYY with microseconds + Z ("18-12-2024 17:08:37.194995Z"). Two
// gotchas in dayjs:
//   1) The `Z` token in a customParseFormat string is treated as a UTC
//      offset parser, not a literal — so `DD-MM-YYYY HH:mm:ss.SSSZ` does
//      NOT match "...216Z". This was causing every archived lead to be
//      dropped and the popup to show 0 when the widget said N.
//   2) customParseFormat only handles up to 3 fractional digits (SSS).
// Fix: strip the trailing Z + anything past millisecond precision, then
// parse as UTC explicitly (utc plugin) so we get the right epoch ms.
const API_DATE_FORMATS_UTC = [
    'YYYY-MM-DDTHH:mm:ss.SSS',
    'YYYY-MM-DDTHH:mm:ss',
    'YYYY-MM-DD HH:mm:ss.SSS',
    'YYYY-MM-DD HH:mm:ss',
    'YYYY-MM-DD',
    'DD-MM-YYYY HH:mm:ss.SSS',
    'DD-MM-YYYY HH:mm:ss',
    'DD-MM-YYYY',
    'DD/MM/YYYY',
];
// Keep the exported name for other modules (popup components read dates via
// the slice's helpers) — these formats are only reached when no UTC suffix.
const API_DATE_FORMATS = API_DATE_FORMATS_UTC;
export const parseApiDate = (dateStr) => {
    if (!dateStr) return null;
    if (typeof dateStr !== 'string') {
        const d = dayjs(dateStr);
        return d.isValid() ? d : null;
    }
    // Strip everything that customParseFormat can't grok: trailing Z,
    // timezone offsets like "+00:00", and microseconds beyond milliseconds.
    const trimmed = dateStr
        .replace(/\.(\d{3})\d+/, '.$1')
        .replace(/Z$/, '')
        .replace(/[+-]\d{2}:?\d{2}$/, '');
    // Treat as UTC — backend always serialises to UTC. We iterate the format
    // list ourselves instead of passing an array to dayjs.utc() because
    // dayjs has a bug where passing an array + strict + a format containing
    // `.SSS` falls back to local-time parsing, producing an off-by-TZ-offset
    // result. Single-string format parse avoids the bug.
    const matched = API_DATE_FORMATS_UTC
        .map((fmt) => dayjs.utc(trimmed, fmt, true))
        .find((d) => d.isValid());
    if (matched) return matched;
    const fallback = dayjs.utc(trimmed);
    if (fallback.isValid()) return fallback;
    const localFallback = dayjs(dateStr);
    return localFallback.isValid() ? localFallback : null;
};

// Valid archive reasons — MUST mirror backend `ARCHIVE_REASON_NOTES` in
// apps/reports/views/archived_lead_summary.py. Keep this list sorted
// alphabetically so drift is easy to spot in review.
//
// Why this matters: this set drives three behaviours:
//   1. Filtering the raw /reports/leads/?group_by=lead_notes__note rows
//      into just archive-related reasons (line ~251, ~1015 below).
//   2. Picking the archive reason off a lead's lead_notes[] array in
//      byReason breakdowns and Lost Leads detail (line ~3132, ~3160).
//   3. Matching the UI dropdown the user can filter by.
//
// Drift symptom: if the backend group_by returns a reason this set
// doesn't include, the frontend drops the rows silently — the widget
// total disagrees with the sum of the by_reason buckets. The phase-5
// review caught exactly that: backend added NOT_INTERESTED, DUPLICATE,
// and SPAM; frontend kept FOLLOW_UP (which backend deliberately excludes
// because it's a follow-up note type, not an archive reason).
export const VALID_ARCHIVE_REASONS = [
    'ANOTHER_PRACTICE',
    'APPOINTMENT_RELATED',
    'AWAITING_RESPONSE',
    'CHANGED_MIND',
    'CLINICALLY_NOT_SUITABLE',
    'DUPLICATE_ENTRY',
    'NHS_WAITING_LIST',
    'NO_RESPONSE',
    'NOT_INTERESTED',
    'OTHER',
    'PRACTICE_TOO_FAR',
    'SPAM',
    'TOO_EXPENSIVE',
    'TREATMENT_RELATED',
    'UNABLE_TO_CONTACT',
    'WANT_NHS',
];

// Helper to format archive reason label
const formatArchiveReasonLabel = (reason) => {
    if (reason === 'WANT_NHS') return 'Want NHS';
    if (reason === 'NHS_WAITING_LIST') return 'NHS Waiting List';
    return reason
        .split('_')
        .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
        .join(' ');
};

// A failed count request must surface as an error, never as a confident "0"
// (same convention as the archived count read).
const failedRes = (res) => !res || res.ok === false;
const requireOverviewRes = (res, label) => {
    if (failedRes(res)) throw new Error(`${label} request failed`);
    return res;
};

// Backend limitation (verified 2026-04-17 against staging):
//   - /archived/ caps page size at 50 and silently ignores every date filter we tested
//     (archived_at__gte/lte/range/gt/lt, created_at__gte/lte, *_after/*_before).
//   - /reports/leads/?is_archived=true only filters by created_at, which is semantically
//     wrong for "Lost in period" (a lead created in Jan but archived in Apr belongs to the
//     April Lost Leads report, not January's).
//   - /reports/archived-lead-summary/?time_after=X&time_before=Y DOES respect archived_at
//     and returns an accurate count, but no per-lead detail.
// Strategy: use the summary endpoint for counts, and paginate /archived/ ordered by
// archived_at desc for per-lead data, breaking early once we pass dateRange.start.
// maxPages caps the paginated fetch — default 40 pages (2000 leads) is plenty for
// building byReason/byTreatment/bySource breakdowns. The detailed Lost Leads page
// and popups can opt in to more via { maxPages }. KPI counts should come from
// fetchArchivedCountByArchivedAt / fetchArchivedSummaryWithBreakdown so they stay
// accurate regardless of this cap.
//
// NOTE: no early-termination on "hit an older-than-range lead". The backend
// silently ignores `ordering=-archived_at` and returns 1000+ rows sharing a
// placeholder date ("15-08-2024 08:24:24.311744Z") first, so breaking early
// would drop real in-range leads. Instead we fetch up to `maxPages` and filter
// client-side — slower but correct. Exported so the popup can share the
// same helper and keep counts consistent across widget / popup / breakdown.
// archived_at is a DATETIME. A bare `archived_at__lte=2026-10-06` means
// 2026-10-06 00:00:00, silently dropping every lead archived later on the end
// date (single-day presets showed 0). Start goes out as 00:00:00; the END goes out as
// a BARE date, which the BE treats as the whole day (no 23:59:59 cut-off to drift from).
export const archivedAtRangeQuery = (dateRange) => {
    const day = (v) => String(v).split(' ')[0];
    const startDt = `${day(dateRange.start)} 00:00:00`;
    const endDt = day(dateRange.end);
    return `&archived_at__gte=${encodeURIComponent(startDt)}&archived_at__lte=${encodeURIComponent(endDt)}`;
};

export const fetchArchivedLeadsInPeriod = async (practiceId, dateRange, { maxPages = 40, reasonIn = null, onProgress = null } = {}) => {
    if (!practiceId || !dateRange?.start || !dateRange?.end) return [];
    const startMs = dayjs(dateRange.start, API_DATE_FORMATS).valueOf();
    const endMs = dayjs(dateRange.end, API_DATE_FORMATS).valueOf();
    if (Number.isNaN(startMs) || Number.isNaN(endMs)) return [];
    const reasonFilter = reasonIn ? `&archived_reason__in=${encodeURIComponent(reasonIn)}` : '';
    const pageSize = 50;

    // Send archived_at filter + ordering on the URL (the BE may or may not honour
    // them); every page is also filtered client-side, so the result is correct either way.
    const dateFilter = archivedAtRangeQuery(dateRange);
    const ordering = '&ordering=-archived_at';
    const buildUrl = (offset) =>
        `/api/v1/practices/${practiceId}/archived/?is_archived=true&exclude_report_noise=true&limit=${pageSize}&offset=${offset}${reasonFilter}${dateFilter}${ordering}`;

    const filterInRange = (rows) => rows.filter((lead) => {
        const parsed = parseApiDate(lead.archived_at);
        if (!parsed) return false;
        const ms = parsed.valueOf();
        return ms >= startMs && ms <= endMs;
    });

    const checkPage = (res) => {
        // A failed page must surface as an error, never as "no archived leads".
        if (!res || res.ok === false) {
            // Status and practice go in the MESSAGE because errorHandler builds the
            // Sentry title from error.cause.message.
            const err = new Error(`Archived leads request failed (practice ${practiceId}, status ${res?.status ?? 'none'})`);
            // TEMPLATED on purpose. errorHandler puts endpoint in the Sentry
            // fingerprint, so interpolating the practice id here would open a
            // separate issue per practice for one underlying fault. The specific
            // practice stays visible in the message above (and so in the title).
            err.endpoint = '/api/v1/practices/{practice_id}/archived/';
            throw err;
        }
        return res?.data?.results || [];
    };

    // Page 1 gives totalCount. Whether or not the server honoured the archived_at
    // filter, we paginate to the FULL count (capped at maxPages * pageSize) and
    // filter client-side: a "first page all in range" heuristic cannot tell a
    // server-filtered list from a lucky page, and truncating at page 1 dropped rows
    // when the count was 51-100.
    const first = await callApi(buildUrl(0));
    const firstResults = checkPage(first);
    const totalCount = first?.data?.count ?? firstResults.length;
    const inRange = filterInRange(firstResults);
    const cap = Math.min(totalCount, maxPages * pageSize);

    if (typeof onProgress === 'function') {
        onProgress({ loaded: Math.min(firstResults.length, cap), total: cap, kept: inRange.length });
    }
    if (firstResults.length < pageSize || firstResults.length >= cap) return inRange;

    // Fetch the remaining pages in parallel batches (6-wide).
    const remainingOffsets = [];
    for (let off = pageSize; off < cap; off += pageSize) remainingOffsets.push(off);
    const CONCURRENCY = 6;
    let loaded = firstResults.length;
    for (let i = 0; i < remainingOffsets.length; i += CONCURRENCY) {
        const batch = remainingOffsets.slice(i, i + CONCURRENCY);
        // eslint-disable-next-line no-await-in-loop
        const pages = await Promise.all(batch.map((off) => callApi(buildUrl(off))));
        const batchRows = pages.flatMap(checkPage);
        loaded += batchRows.length;
        inRange.push(...filterInRange(batchRows));
        if (typeof onProgress === 'function') {
            onProgress({ loaded: Math.min(loaded, cap), total: cap, kept: inRange.length });
        }
    }
    return inRange;
};

// By Treatment funnel "Archived" drill-down: archived leads CREATED in the period that
// carry an in-category treatment — the exact population behind the funnel number
// (reports/leads is_archived=true & created_at range & treatment filter).
// BE contract: GET /practices/{pk}/archived/ honours created_at__gte/lte,
// lead_treatments__treatment__in, practice__in and exclude_report_noise=true
// (drops DUPLICATE_ENTRY + spam, like the report). Isolated in this one function so a
// contract change only touches the URL builder below.
export const buildArchivedByCreatedUrl = ({ practiceIds, start, end, treatmentIds, limit = 50, offset = 0 }) => {
    const [primary] = practiceIds;
    const params = [
        'is_archived=true',
        'exclude_report_noise=true',
        `created_at__gte=${encodeURIComponent(start)}`,
        `created_at__lte=${encodeURIComponent(end)}`,
        'ordering=-created_at,-id',
        `limit=${limit}`,
        `offset=${offset}`,
    ];
    if (practiceIds.length > 1) params.push(`practice__in=${practiceIds.join(',')}`);
    if (treatmentIds?.length) params.push(`lead_treatments__treatment__in=${treatmentIds.join(',')}`);
    return `/api/v1/practices/${primary}/archived/?${params.join('&')}`;
};

export const fetchArchivedLeadsByCreated = async (practiceIds, { start, end, treatmentIds = null }, { onProgress = null } = {}) => {
    if (!practiceIds?.length || !start || !end) return [];
    const pageSize = 50;
    const url = (offset) => buildArchivedByCreatedUrl({ practiceIds, start, end, treatmentIds, limit: pageSize, offset });
    const first = await callApi(url(0));
    const firstRows = first?.data?.results || [];
    const total = first?.data?.count ?? firstRows.length;
    let rows = [...firstRows];
    if (typeof onProgress === 'function') onProgress({ loaded: rows.length, total });
    const offsets = [];
    for (let off = firstRows.length || pageSize; off < Math.min(total, 5000); off += pageSize) offsets.push(off);
    const CONCURRENCY = 6;
    for (let i = 0; i < offsets.length; i += CONCURRENCY) {
        // eslint-disable-next-line no-await-in-loop
        const pages = await Promise.all(offsets.slice(i, i + CONCURRENCY).map((off) => callApi(url(off))));
        rows = rows.concat(pages.flatMap((pg) => pg?.data?.results || []));
        if (typeof onProgress === 'function') onProgress({ loaded: rows.length, total });
    }
    // Stable ordering above prevents page-boundary dupes; dedupe by id as a safety net.
    const seen = new Set();
    return rows.filter((l) => (seen.has(l.id) ? false : (seen.add(l.id), true)));
};

// Multi-practice helpers (toPracticeIds, practiceInSuffix) moved to
// src/utils/practiceInSuffix.js in phase-1 of the All Practices rollout
// — see the import near the top of this file.

// Count leads ARCHIVED in the period, keyed on archived_at (the event date) —
// i.e. "how many leads did we lose during this window". This is the correct
// basis for a Lost-Leads KPI and matches the /archived drilldown popup. Used
// for the PREVIOUS-period comparison count (the current period gets count +
// breakdowns from fetchArchivedSummaryWithBreakdown).
//
// We hit /archived/ with archived_at bounds + limit=1 to read the `count`
// cheaply. (The /reports/archived-lead-summary/ endpoint now supports an
// archived_at basis via archived_after/before too — see
// fetchArchivedSummaryWithBreakdown — but this older limit=1 read still works
// and is kept for the prev-period number.) /archived/ is a per-practice
// (non-report) view, so for multi-practice we sum per-practice counts.
export const fetchArchivedCountByArchivedAt = async (practiceIdOrIds, dateRange) => {
    if (!dateRange?.start || !dateRange?.end) return 0;
    const ids = toPracticeIds(practiceIdOrIds);
    if (!ids.length) return 0;
    const counts = await Promise.all(ids.map((pid) =>
        callApi(`/api/v1/practices/${pid}/archived/?is_archived=true&exclude_report_noise=true${archivedAtRangeQuery(dateRange)}&limit=1`)
            .then((r) => {
                // A failed read must NOT look like "0 archived last period" (that fabricates a delta).
                if (!r || r.ok === false) throw new Error('Archived count request failed');
                return r?.data?.count ?? 0;
            })
    ));
    return counts.reduce((a, b) => a + b, 0);
};

// Resolve a lead's canonical archive reason code. Prefer the explicit field;
// fall back to a valid-reason archive note. Returns null if none/invalid.
const resolveArchiveReason = (lead, validSet) => {
    const direct = lead?.reason_for_archiving || lead?.archived_reason;
    if (direct && validSet.has(direct)) return direct;
    const note = (lead?.lead_notes || []).find((n) => validSet.has(n?.note))?.note;
    return note || (direct || null);
};

// Build byReason / byTreatment / bySource breakdowns from an archived-lead list
// (already archived_at-filtered). Single source → count == breakdown sums ==
// the drilldown popup, all on the archived_at basis.
export const NO_REASON_RECORDED = 'NO_REASON_RECORDED';
export const UNKNOWN_SOURCE = 'Unknown';

// Overview "Leads by Source": every lead lands in a bucket. Rows with a null/empty
// lead_source__type are merged into one "Unknown" bucket (previously dropped, so the
// card total undercounted vs the real lead total).
export const bucketOverviewSources = (sourceStats = []) => {
    const map = {};
    (sourceStats || []).forEach((s) => {
        const type = s.lead_source__type || UNKNOWN_SOURCE;
        if (!map[type]) map[type] = { type, leads: 0, revenue: 0 };
        map[type].leads += s.count_id || 0;
        map[type].revenue += s.sum_lead_treatments__custom_price || 0;
    });
    return Object.values(map).sort((a, b) => b.leads - a.leads);
};

export const summarizeArchivedLeads = (leads) => {
    const validSet = new Set(VALID_ARCHIVE_REASONS);
    const reasonMap = {}; const treatmentMap = {}; const sourceMap = {};
    leads.forEach((lead) => {
        const reason = resolveArchiveReason(lead, validSet);
        // Every archived lead lands in exactly one bucket so reasons sum to the total;
        // missing / unrecognised reasons go to "No reason recorded".
        const reasonKey = reason && validSet.has(reason) ? reason : NO_REASON_RECORDED;
        reasonMap[reasonKey] = (reasonMap[reasonKey] || 0) + 1;
        const lt = (lead.lead_treatments || [])[0];
        const tName = typeof lt?.treatment === 'string' ? lt.treatment : (lt?.treatment?.name || lt?.treatment_name);
        if (tName) {
            if (!treatmentMap[tName]) treatmentMap[tName] = { count: 0, revenue: 0 };
            treatmentMap[tName].count += 1;
            treatmentMap[tName].revenue += (lead.lead_treatments || []).reduce((s, t) => s + (parseFloat(t.custom_price ?? t.price) || 0), 0);
        }
        // Every lead lands in a source bucket ("Unknown" when it has no source).
        const rawSrc = lead.lead_source;
        const src = (typeof rawSrc === 'string' ? rawSrc : (rawSrc?.type || rawSrc?.name)) || UNKNOWN_SOURCE;
        sourceMap[src] = (sourceMap[src] || 0) + 1;
    });
    const byReason = Object.entries(reasonMap)
        .map(([reason, count]) => ({ reason, label: reason === NO_REASON_RECORDED ? 'No reason recorded' : formatArchiveReasonLabel(reason), count }))
        .sort((a, b) => b.count - a.count);
    const byTreatment = Object.entries(treatmentMap)
        .map(([name, v]) => ({ name, count: v.count, revenue: v.revenue }))
        .sort((a, b) => b.count - a.count);
    const bySource = Object.entries(sourceMap)
        .map(([type, count]) => ({ type, count }))
        .sort((a, b) => b.count - a.count);
    return { byReason, byTreatment, bySource };
};

/**
 * Fetch the full archived-lead summary (count + byReason + byTreatment +
 * bySource) for a period in ONE cached, server-side query — the archived_at
 * basis, so the widget total and breakdown sums all agree with the drilldown.
 *
 * This replaces the old per-lead /archived/ pagination
 * (fetchArchivedLeadsInPeriod + summarizeArchivedLeads). That serializer is
 * ~160ms/row, so a few hundred leads blew past callApi's 10s timeout —
 * zeroing the Lost Leads KPI and making date-filter clicks take ~29s. The
 * backend now returns just the numbers (`breakdown=full`).
 *
 * Returns { count, byReason, byTreatment, bySource } (each an array, sorted
 * desc by count). The /archived/ row-pull is still used, lazily, for the
 * Lost Leads drilldown popup where per-lead detail is genuinely needed.
 */
export const fetchArchivedSummaryWithBreakdown = async (practiceIdOrIds, dateRange) => {
    const empty = { count: 0, byReason: [], byTreatment: [], bySource: [] };
    if (!dateRange?.start || !dateRange?.end) return empty;
    const ids = toPracticeIds(practiceIdOrIds);
    const primary = ids[0];
    if (!primary) return empty;
    const startDate = String(dateRange.start).split(' ')[0];
    const endDate = String(dateRange.end).split(' ')[0];
    try {
        const res = await callApi(
            `/api/v1/practices/${primary}/reports/archived-lead-summary/?archived_after=${startDate}&archived_before=${endDate}&breakdown=full&exclude_report_noise=true${practiceInSuffix(ids)}`
        );
        if (failedRes(res)) throw new Error('Archived summary request failed');
        const d = res?.data || {};
        const VALID = new Set(VALID_ARCHIVE_REASONS);
        const byReason = (Array.isArray(d.by_reason) ? d.by_reason : [])
            .filter((row) => row && VALID.has(row.reason))
            .map((row) => ({
                reason: row.reason,
                label: formatArchiveReasonLabel(row.reason),
                count: row.count || 0,
            }))
            .sort((a, b) => b.count - a.count);
        const byTreatment = (Array.isArray(d.by_treatment) ? d.by_treatment : [])
            .map((row) => ({ name: row.name, count: row.count || 0, revenue: row.revenue || 0 }))
            .sort((a, b) => b.count - a.count);
        const bySource = (Array.isArray(d.by_source) ? d.by_source : [])
            .map((row) => ({ type: row.type, count: row.count || 0 }))
            .sort((a, b) => b.count - a.count);
        // The BE breakdowns omit leads with no reason / no source, so they undershoot the
        // total. Add the remainder so every lead lands in exactly one bucket (same rule as
        // summarizeArchivedLeads).
        const total = d.count ?? 0;
        const reasonRest = total - byReason.reduce((sum, r) => sum + r.count, 0);
        if (reasonRest > 0) {
            byReason.push({ reason: NO_REASON_RECORDED, label: 'No reason recorded', count: reasonRest });
            byReason.sort((a, b) => b.count - a.count);
        }
        const sourceRest = total - bySource.reduce((sum, r) => sum + r.count, 0);
        if (sourceRest > 0) {
            const unknown = bySource.find((r) => r.type === UNKNOWN_SOURCE);
            if (unknown) unknown.count += sourceRest;
            else bySource.push({ type: UNKNOWN_SOURCE, count: sourceRest });
            bySource.sort((a, b) => b.count - a.count);
        }
        return { count: total, byReason, byTreatment, bySource };
    } catch (err) {
        // Surface the failure rather than masking it as a confident "0 lost
        // leads" (the original bug this work fixed was a silently-zeroed KPI).
        // eslint-disable-next-line no-console
        console.warn('[overview] archived-lead-summary failed:', err?.message || err);
        throw err;
    }
};

// Note: fanOutAndAggregate / mergeGroupedCounts / mergeTrend were removed in
// phase 5 cleanup. Every thunk that used to fan out across practices now uses
// the backend's `?practice__in=1,2,3` aggregation, so the client-side fold
// helpers are no longer reachable. See analyticsReports phase commits for the
// thunk-by-thunk migration.

const INITIAL_STATE = {
    isLoading: false,
    error: null,
    // Cache keys to prevent redundant re-fetches when navigating between tabs
    _overviewFetchKey: null,
    _clinicianFetchKey: null,
    _treatmentFetchKey: null,
    _tcoFetchKey: null,
    _sourceFetchKey: null,
    _referralFetchKey: null,
    _landingUrlFetchKey: null,
    // Fetch-dedup key for the All Practices Clinician Conversion section —
    // independent from _clinicianFetchKey (the By Practitioner page slot) so
    // the two never clobber each other's cache.
    _allPracticesClinicianFetchKey: null,
    dateRange: {
        preset: 'thisMonth',
        startDate: null,
        endDate: null,
    },
    // Practice override for analytics tabs (null/empty = use global practice from header).
    // Array so analytics can aggregate across multiple practices for multi-practice users.
    // Hydrated from localStorage so the selection survives reload — wrapped in
    // a try/catch because access fails in some private-mode browsers.
    reportPracticeIds: (() => {
        try {
            const raw = localStorage.getItem('reportPracticeIds');
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            if (!Array.isArray(parsed) || parsed.length === 0) return null;
            const ints = parsed.map(Number).filter((n) => Number.isFinite(n) && n > 0);
            return ints.length ? ints : null;
        } catch {
            return null;
        }
    })(),
    // Single-practice override shared only by the Google Ads and Meta Ads
    // reports. This must remain separate from reportPracticeIds, which drives
    // multi-practice aggregation across the other analytics pages.
    adsReportingPracticeId: (() => {
        try {
            const value = Number(localStorage.getItem('adsReportingPracticeId'));
            return Number.isInteger(value) && value > 0 ? value : null;
        } catch {
            return null;
        }
    })(),
    adsReportingPermissionsPracticeId: null,
    adsReportingPermissions: {},
    adsReportingPermissionsStatus: 'idle',
    adsReportingPermissionsError: null,
    // Overview data
    kpis: {
        totalLeads: { value: 0, previousValue: 0 },
        consultationsBooked: { value: 0, previousValue: 0 },
        attended: { value: 0, previousValue: 0 },
        treatmentStarted: { value: 0, previousValue: 0 },
        lostLeads: { value: 0, previousValue: 0 },
    },
    funnel: [],
    treatmentBreakdown: [],
    topPractitioners: [],
    monthlyTrend: [],

    // Archived leads data
    archivedLeads: {
        total: 0,
        byReason: [], // [{ reason: string, label: string, count: number, leadIds: [] }]
    },

    // Lead popup state
    leadPopup: {
        open: false,
        practiceId: null,
        leadIds: [],
        title: '',
    },

    // Overview source/referral data
    overviewSources: [],
    overviewReferrals: [],

    // By Treatment data
    selectedTreatment: null,
    treatmentTotalLeads: 0, // ungrouped unique lead count for KPI (avoids double-counting multi-treatment leads)
    treatmentList: [],
    treatmentKpis: {},
    treatmentFunnel: [],
    treatmentMonthly: [],
    treatmentPractitioners: [],

    // Monthly enquiries by treatment
    monthlyEnquiries: {
        data: [],
        isLoading: false,
        selectedTreatmentId: null,
    },

    // Treatment status summary (all treatments with status breakdown)
    treatmentStatusSummary: {
        data: [],
        isLoading: false,
    },

    // Treatment funnel via state_transition_params (consistent with Overview funnel methodology)
    treatmentFunnelData: null,

    // By Practitioner data
    practitioners: [],
    practitionersPage: 1,
    practitionersSortBy: 'revenue',
    practitionersSearch: '',
    // Enhanced practitioner stats with date filtering
    practitionerStats: {
        isLoading: false,
        // Leads assigned to each practitioner in the date range
        leadsByPractitioner: [],
        // Consultation performance per practitioner
        consultationsByPractitioner: [],
        // Treatment performance per practitioner
        treatmentsByPractitioner: [],
        // Team averages for comparison
        teamAverages: {
            avgLeads: 0,
            avgConsultations: 0,
            avgConversions: 0,
            avgRevenue: 0,
            avgConversionRate: 0,
            avgShowUpRate: 0,
        },
    },

    // By Source data
    sources: [],
    campaigns: [],
    sourceInsights: {},

    // Source status breakdown (for By Source page)
    sourceStatusData: {
        data: [],
        isLoading: false,
    },
    referralStatusData: {
        data: [],
        isLoading: false,
    },
    // Landing-page URL breakdown — buckets leads by their `entire_url`
    // metadata (set at form submit). entire_url can live in either
    // lead.lead_metadata[].value (key='entire_url') or
    // lead.lead_source.metadata.entire_url; the thunk checks both.
    // Leads with no entire_url fall under a synthetic "(no landing URL)" row.
    // `progress` tracks paginated-fetch state so the UI can render
    // "Loading N of M leads..." rather than a featureless spinner during
    // the worst-case 5000-lead scan.
    landingUrlData: {
        data: [],
        isLoading: false,
        progress: { loaded: 0, total: 0 },
    },

    // Trends data
    selectedMetric: 'leads',
    trendData: [],
    yoyData: [],
    seasonality: [],
    forecast: [],
    milestones: [],

    // WhatsApp data
    whatsapp: {
        isLoading: false,
        totalConversations: 0,
        activeConversations: 0,
        closedConversations: 0,
        avgResponseTime: 0,          // in minutes
        within24hr: 0,               // percentage
        closeReasons: [],            // [{ reason, label, count, threadIds }]
        responseTimeDistribution: [], // [{ bucket, count, label }]
        leadConversion: {
            totalWhatsAppLeads: 0,
            consultationsBooked: 0,
            treatmentStarted: 0,
        },
    },

    // Clinician Conversion Tracker data
    clinicianConversion: {
        isLoading: false,
        data: [], // Processed practitioner conversion data
        treatments: [], // List of treatments for column headers
        teamTotals: {}, // Aggregated totals across all practitioners
    },

    // Clinician Conversion Tracker data for the Analytics Reports "All
    // Practices" page — a SEPARATE slot from `clinicianConversion` above (that
    // one belongs to the By Practitioner page) so the two never overwrite each
    // other. Scoped to ONE practice at a time (chosen via a dropdown on the
    // All Practices page), independent of the page's multi-practice selection.
    allPracticesClinicianConversion: {
        isLoading: false,
        data: [],
        treatments: [],
        teamTotals: {},
        error: null,
        // The request key (practiceId|start|end) the current `error` belongs to.
        // Render logic only shows the error card when this matches the
        // CURRENTLY desired key — otherwise (e.g. after switching practice) it
        // renders loading instead of a stale error from a different practice.
        errorRequestKey: null,
        // Per-practice coverage for the multi-practice union — populated only
        // for a SELECTED practice that failed to contribute data (401/403 =
        // 'access', hard fetch error = 'error'); a practice that is honestly
        // empty (authorized, zero rows) is NOT included here. selectedCount /
        // coveredCount let the UI show "Showing X of Y selected practices"
        // truthfully instead of silently presenting a partial union as
        // complete. Cleared on every fresh fetch-start and by
        // resetAllPracticesClinicianSlot so stale partial info never survives
        // a new selection.
        failedPractices: [],
        selectedCount: 0,
        coveredCount: 0,
    },

    // TCO/User Performance Tracker data
    tcoPerformance: {
        isLoading: false,
        data: [], // Processed TCO/User performance data
        teamTotals: {}, // Aggregated totals
    },

    // Lost Leads data
    lostLeads: {
        isLoading: false,
        kpis: {
            totalLost: 0,
            topReason: '',
            avgDaysToArchive: 0,
            lostRevenue: 0,
            archiveRate: 0,
            momChange: null,
        },
        byReason: [],
        byTreatment: [],
        bySource: [],
        byTeamMember: [],
        trend: [],
        detailedLeads: [],
    },

    // Click Tracking data (phone + WhatsApp click events from practice website)
    clickTracking: {
        events: [],
        kpis: { totalClicks: 0, phoneClicks: 0, whatsappClicks: 0, topPage: null, topSource: null, topConvertingPage: null },
        // Per-page form-submission aggregate from ONLINE_FORM leads in the
        // selected date range. Lives at the page level — NOT joined onto
        // every click event. Each row is { pagePath, pageLabel, submissions,
        // clicks, conversionRate }. Consumed by the standalone
        // FormSubmissionsByPageCard, not by the click events table.
        formSubmissionsByPage: [],
        isLoading: false,
        error: null,
        _fetchKey: '',
    },
};

const slice = createSlice({
    name: 'analyticsReports',
    initialState: INITIAL_STATE,
    reducers: {
        setLoading: (state, action) => {
            state.isLoading = action.payload;
        },
        setError: (state, action) => {
            state.error = action.payload;
        },
        setFetchKey: (state, action) => {
            const { key, value } = action.payload;
            state[key] = value;
        },
        // Null all fetch-dedup keys. Used by error-recovery flows so the
        // next effect pass re-dispatches the thunk without having to wait
        // for the user to change a filter / date preset.
        clearFetchKeys: (state) => {
            state._overviewFetchKey = null;
            state._clinicianFetchKey = null;
            state._treatmentFetchKey = null;
            state._tcoFetchKey = null;
            state._sourceFetchKey = null;
            state._referralFetchKey = null;
            state._landingUrlFetchKey = null;
            state._allPracticesClinicianFetchKey = null;
            state.allPracticesClinicianConversion.error = null;
            state.allPracticesClinicianConversion.errorRequestKey = null;
            state.allPracticesClinicianConversion.failedPractices = [];
            state.allPracticesClinicianConversion.selectedCount = 0;
            state.allPracticesClinicianConversion.coveredCount = 0;
            state.clickTracking._fetchKey = '';
            state.clickTracking.error = null;
            state.error = null;
        },
        setDateRange: (state, action) => {
            state.dateRange = action.payload;
        },
        setKpis: (state, action) => {
            state.kpis = action.payload;
        },
        setFunnel: (state, action) => {
            state.funnel = action.payload;
        },
        setTreatmentBreakdown: (state, action) => {
            state.treatmentBreakdown = action.payload;
        },
        setTopPractitioners: (state, action) => {
            state.topPractitioners = action.payload;
        },
        setMonthlyTrend: (state, action) => {
            state.monthlyTrend = action.payload;
        },
        setSelectedTreatment: (state, action) => {
            state.selectedTreatment = action.payload;
        },
        setTreatmentTotalLeads: (state, action) => {
            state.treatmentTotalLeads = action.payload;
        },
        setTreatmentList: (state, action) => {
            state.treatmentList = action.payload;
        },
        setTreatmentKpis: (state, action) => {
            state.treatmentKpis = action.payload;
        },
        setTreatmentFunnel: (state, action) => {
            state.treatmentFunnel = action.payload;
        },
        setTreatmentMonthly: (state, action) => {
            state.treatmentMonthly = action.payload;
        },
        setTreatmentPractitioners: (state, action) => {
            state.treatmentPractitioners = action.payload;
        },
        setMonthlyEnquiries: (state, action) => {
            state.monthlyEnquiries = { ...state.monthlyEnquiries, ...action.payload };
        },
        setMonthlyEnquiriesLoading: (state, action) => {
            state.monthlyEnquiries.isLoading = action.payload;
        },
        setTreatmentStatusSummary: (state, action) => {
            state.treatmentStatusSummary = { ...state.treatmentStatusSummary, ...action.payload };
        },
        setTreatmentStatusSummaryLoading: (state, action) => {
            state.treatmentStatusSummary.isLoading = action.payload;
        },
        setTreatmentFunnelData: (state, action) => {
            state.treatmentFunnelData = action.payload;
        },
        setReportPracticeIds: (state, action) => {
            const {payload} = action;
            let next;
            if (payload === null || payload === undefined) {
                next = null;
            } else if (Array.isArray(payload)) {
                next = payload.length ? payload : null;
            } else {
                next = [payload];
            }
            state.reportPracticeIds = next;
            // Persist the selection so it survives page reloads. Keyed
            // generically — the hook initialises from this on mount.
            try {
                if (next === null) localStorage.removeItem('reportPracticeIds');
                else localStorage.setItem('reportPracticeIds', JSON.stringify(next));
            } catch {
                // ignore quota errors / private-mode
            }
        },
        setAdsReportingPracticeId: (state, action) => {
            const value = Number(action.payload);
            const next = Number.isInteger(value) && value > 0 ? value : null;
            if (state.adsReportingPracticeId !== next) {
                state.adsReportingPermissionsPracticeId = null;
                state.adsReportingPermissions = {};
                state.adsReportingPermissionsStatus = 'idle';
                state.adsReportingPermissionsError = null;
            }
            state.adsReportingPracticeId = next;
            try {
                if (next === null) localStorage.removeItem('adsReportingPracticeId');
                else localStorage.setItem('adsReportingPracticeId', String(next));
            } catch {
                // In-memory selection still works when localStorage is unavailable.
            }
        },
        beginAdsReportingPermissions: (state, action) => {
            state.adsReportingPermissionsPracticeId = Number(action.payload) || null;
            state.adsReportingPermissions = {};
            state.adsReportingPermissionsStatus = 'loading';
            state.adsReportingPermissionsError = null;
        },
        resolveAdsReportingPermissions: (state, action) => {
            const practiceId = Number(action.payload?.practiceId) || null;
            if (practiceId !== state.adsReportingPracticeId) return;
            state.adsReportingPermissionsPracticeId = practiceId;
            state.adsReportingPermissions = action.payload?.permissions || {};
            state.adsReportingPermissionsStatus = 'ready';
            state.adsReportingPermissionsError = null;
        },
        failAdsReportingPermissions: (state, action) => {
            const practiceId = Number(action.payload?.practiceId) || null;
            if (practiceId !== state.adsReportingPracticeId) return;
            state.adsReportingPermissionsPracticeId = practiceId;
            state.adsReportingPermissions = {};
            state.adsReportingPermissionsStatus = 'error';
            state.adsReportingPermissionsError = action.payload?.error || 'Failed to load reporting permissions';
        },
        setPractitioners: (state, action) => {
            state.practitioners = action.payload;
        },
        setPractitionersPage: (state, action) => {
            state.practitionersPage = action.payload;
        },
        setPractitionersSortBy: (state, action) => {
            state.practitionersSortBy = action.payload;
        },
        setPractitionersSearch: (state, action) => {
            state.practitionersSearch = action.payload;
        },
        setPractitionerStats: (state, action) => {
            state.practitionerStats = { ...state.practitionerStats, ...action.payload };
        },
        setPractitionerStatsLoading: (state, action) => {
            state.practitionerStats.isLoading = action.payload;
        },
        setSources: (state, action) => {
            state.sources = action.payload;
        },
        setCampaigns: (state, action) => {
            state.campaigns = action.payload;
        },
        setSourceInsights: (state, action) => {
            state.sourceInsights = action.payload;
        },
        setSelectedMetric: (state, action) => {
            state.selectedMetric = action.payload;
        },
        setTrendData: (state, action) => {
            state.trendData = action.payload;
        },
        setYoyData: (state, action) => {
            state.yoyData = action.payload;
        },
        setSeasonality: (state, action) => {
            state.seasonality = action.payload;
        },
        setForecast: (state, action) => {
            state.forecast = action.payload;
        },
        setMilestones: (state, action) => {
            state.milestones = action.payload;
        },
        setArchivedLeads: (state, action) => {
            state.archivedLeads = action.payload;
        },
        setLeadPopup: (state, action) => {
            state.leadPopup = action.payload;
        },
        setOverviewSources: (state, action) => {
            state.overviewSources = action.payload;
        },
        setOverviewReferrals: (state, action) => {
            state.overviewReferrals = action.payload;
        },
        setWhatsAppData: (state, action) => {
            state.whatsapp = { ...state.whatsapp, ...action.payload };
        },
        setWhatsAppLoading: (state, action) => {
            state.whatsapp.isLoading = action.payload;
        },
        setSourceStatusData: (state, action) => {
            state.sourceStatusData = { ...state.sourceStatusData, ...action.payload };
        },
        setSourceStatusLoading: (state, action) => {
            state.sourceStatusData.isLoading = action.payload;
        },
        setReferralStatusData: (state, action) => {
            state.referralStatusData = { ...state.referralStatusData, ...action.payload };
        },
        setReferralStatusLoading: (state, action) => {
            state.referralStatusData.isLoading = action.payload;
        },
        setLandingUrlData: (state, action) => {
            state.landingUrlData = { ...state.landingUrlData, ...action.payload };
        },
        setLandingUrlLoading: (state, action) => {
            state.landingUrlData.isLoading = action.payload;
        },
        setLandingUrlProgress: (state, action) => {
            state.landingUrlData.progress = action.payload;
        },
        setClinicianConversion: (state, action) => {
            state.clinicianConversion = { ...state.clinicianConversion, ...action.payload };
        },
        setClinicianConversionLoading: (state, action) => {
            state.clinicianConversion.isLoading = action.payload;
        },
        setAllPracticesClinicianConversion: (state, action) => {
            state.allPracticesClinicianConversion = { ...state.allPracticesClinicianConversion, ...action.payload };
        },
        setAllPracticesClinicianConversionLoading: (state, action) => {
            state.allPracticesClinicianConversion.isLoading = action.payload;
        },
        // Slot-specific retry/reset for the All Practices Clinician Conversion
        // section — clears ONLY this slot's error, errorRequestKey, and fetch
        // key so the Retry button re-triggers a fetch without touching the By
        // Practitioner slot, any other analytics fetch key, or page data.
        // (clearFetchKeys above is the whole-page recovery equivalent and also
        // nulls this slot's error + key for that broader flow.)
        resetAllPracticesClinicianSlot: (state) => {
            state.allPracticesClinicianConversion.error = null;
            state.allPracticesClinicianConversion.errorRequestKey = null;
            state.allPracticesClinicianConversion.failedPractices = [];
            state.allPracticesClinicianConversion.selectedCount = 0;
            state.allPracticesClinicianConversion.coveredCount = 0;
            state._allPracticesClinicianFetchKey = null;
        },
        setTcoPerformance: (state, action) => {
            state.tcoPerformance = { ...state.tcoPerformance, ...action.payload };
        },
        setTcoPerformanceLoading: (state, action) => {
            state.tcoPerformance.isLoading = action.payload;
        },
        setLostLeadsData: (state, action) => {
            state.lostLeads = { ...state.lostLeads, ...action.payload, isLoading: false };
        },
        setLostLeadsLoading: (state, action) => {
            state.lostLeads.isLoading = action.payload;
        },
        // Click Tracking reducers
        clickTrackingLoading: (state) => {
            state.clickTracking.isLoading = true;
            state.clickTracking.error = null;
        },
        clickTrackingSuccess: (state, action) => {
            const { events, kpis, _fetchKey, formSubmissionsByPage } = action.payload;
            state.clickTracking.events = events;
            state.clickTracking.kpis = kpis;
            state.clickTracking._fetchKey = _fetchKey;
            state.clickTracking.formSubmissionsByPage = formSubmissionsByPage || [];
            state.clickTracking.isLoading = false;
            state.clickTracking.error = null;
        },
        clickTrackingError: (state, action) => {
            state.clickTracking.isLoading = false;
            state.clickTracking.error = action.payload;
        },
        // Clears the dedup key so the next effect pass re-fires the thunk.
        // Used by the Retry button on the full-error state.
        clearClickTrackingFetchKey: (state) => {
            state.clickTracking._fetchKey = '';
            state.clickTracking.error = null;
        },
    },
});

export const {
    setLoading,
    setError,
    setDateRange,
    setKpis,
    setFunnel,
    setTreatmentBreakdown,
    setTopPractitioners,
    setMonthlyTrend,
    setSelectedTreatment,
    setTreatmentTotalLeads,
    setTreatmentList,
    setTreatmentKpis,
    setTreatmentFunnel,
    setTreatmentMonthly,
    setTreatmentPractitioners,
    setMonthlyEnquiries,
    setMonthlyEnquiriesLoading,
    setTreatmentStatusSummary,
    setTreatmentStatusSummaryLoading,
    setTreatmentFunnelData,
    setPractitioners,
    setPractitionersPage,
    setPractitionersSortBy,
    setPractitionersSearch,
    setPractitionerStats,
    setPractitionerStatsLoading,
    setSources,
    setCampaigns,
    setSourceInsights,
    setSelectedMetric,
    setTrendData,
    setYoyData,
    setSeasonality,
    setForecast,
    setMilestones,
    setArchivedLeads,
    setLeadPopup,
    setOverviewSources,
    setOverviewReferrals,
    setWhatsAppData,
    setWhatsAppLoading,
    setSourceStatusData,
    setSourceStatusLoading,
    setReferralStatusData,
    setReferralStatusLoading,
    setLandingUrlData,
    setLandingUrlLoading,
    setLandingUrlProgress,
    setClinicianConversion,
    setClinicianConversionLoading,
    setAllPracticesClinicianConversion,
    setAllPracticesClinicianConversionLoading,
    resetAllPracticesClinicianSlot,
    setTcoPerformance,
    setTcoPerformanceLoading,
    setLostLeadsData,
    setLostLeadsLoading,
    clickTrackingLoading,
    clickTrackingSuccess,
    clickTrackingError,
    clearClickTrackingFetchKey,
    setFetchKey,
    clearFetchKeys,
    setReportPracticeIds,
    setAdsReportingPracticeId,
    beginAdsReportingPermissions,
    resolveAdsReportingPermissions,
    failAdsReportingPermissions,
} = slice.actions;

// Back-compat export — accepts a single id and wraps it.
export const setReportPracticeId = (id) => setReportPracticeIds(id != null ? [id] : null);

// Helper functions - exported for use in components
export const calculateTrend = (current, previous) => ({
    direction: current >= previous ? 'up' : 'down',
    percentage: previous > 0 ? Math.abs(((current - previous) / previous) * 100).toFixed(1) : 0,
});

export const calculateConversion = (from, to) => (from > 0 ? ((to / from) * 100).toFixed(1) : 0);

// Thunks
// Calculate previous period dates for comparison
export function calculatePreviousPeriod(dateRange, comparisonType) {
    // Whole-day arithmetic on the calendar dates (no timestamp maths, so DST and
    // the 23:59:59 end bound cannot shift a day): the previous window has
    // EXACTLY the same number of days as the current one and ends the day
    // before it starts. (The old ms-based version produced 30- vs 31-day windows.)
    const startDay = dayjs(String(dateRange.start).split(' ')[0]).startOf('day');
    const endDay = dayjs(String(dateRange.end).split(' ')[0]).startOf('day');
    let prevStart;
    let prevEnd;

    if (comparisonType === 'yoy') {
        // Same dates, one year back
        prevStart = startDay.subtract(1, 'year');
        prevEnd = endDay.subtract(1, 'year');
    } else {
        const lengthDays = endDay.diff(startDay, 'day') + 1;
        prevEnd = startDay.subtract(1, 'day');
        prevStart = prevEnd.subtract(lengthDays - 1, 'day');
    }

    return {
        start: `${prevStart.format('YYYY-MM-DD')} 00:00:00`,
        end: `${prevEnd.format('YYYY-MM-DD')} 23:59:59`,
    };
}

export function fetchOverviewData(practiceIdOrIds, dateRange, comparisonType = 'period') {
    const ids = toPracticeIds(practiceIdOrIds);
    const practiceId = ids[0];
    // Multi-practice aggregation: when more than one practice is selected, the
    // backend's `practice__in` aggregates everything in a single round-trip.
    // `pSuffix` appends `&practice__in=1,2,3` or "" when only one practice is
    // selected (the URL's practice_pk already scopes it).
    const pSuffix = practiceInSuffix(ids);
    return async (dispatch) => {
        if (!practiceId) return { skipped: true };
        if (!hasFeatureAccess('analytics-reports')) return { skipped: true };
        dispatch(setLoading(true));
        const baseUrl = `/api/v1/practices/${practiceId}/reports`;

        try {
            // Period vs YoY comparison determines which API strategy to use
            const useSummaryEndpoint = comparisonType === 'period';

            // Shared calls (same for both period-vs-period and year-over-year paths).
            // Archived data is NOT included here — see fetchArchivedSummaryWithBreakdown /
            // fetchArchivedCountByArchivedAt below. The backend's /reports/leads/ endpoint
            // only filters archived leads by created_at, which is semantically wrong
            // for a "Lost in period" KPI.
            const sharedCalls = [
                callApi(`${baseUrl}/practice-user/${ids.length > 1 ? `?practice__in=${ids.join(',')}` : ''}`),
                callApi(`/api/v1/practices/${practiceId}/treatments/?enabled=true&ordering=treatment_item__name&fields={id,practice,price,colour,icon,treatment_item{id,name,treatment_category{id,name}}}`),
                callApi(`${baseUrl}/leads/?group_by[lead_source__type]&aggregate[Count]=id&aggregate[Sum]=lead_treatments__custom_price&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}${pSuffix}`),
                callApi(`${baseUrl}/leads/?group_by[lead_source__referral]&aggregate[Count]=id&aggregate[Sum]=lead_treatments__custom_price&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}${pSuffix}`),
                callApi(`${baseUrl}/leads/?group_by[lead_treatments__treatment__treatment_item__treatment_category__name]&aggregate[Count]=id&aggregate[Sum]=lead_treatments__custom_price&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}${pSuffix}`),
            ];

            // Overview loads fast. Two archive reads:
            //   - KPI count: /reports/archived-lead-summary/ (archived_at
            //     accurate, ≤100ms)
            //   - byReason breakdown: fast group_by keyed on created_at
            //     (backend caveat). This can legitimately disagree with
            //     the widget on narrow windows; the Lost Leads PAGE uses
            //     archived_at pagination for strict parity.
            // DO NOT paginate /archived/ here — on practices with 1000+
            // archived leads Overview blocks on 20+ sequential calls and
            // hangs on the spinner.
            // archivedDataPromise fetches current + previous count and a
            // per-reason breakdown. When the backend supports
            // `?breakdown=reason` (feature_reporting_phase_2, now
            // universally deployed) the summary endpoint returns
            // `by_reason` and we use that directly — archived_at
            // filter, so widget total and reason sums always agree.
            //
            // The legacy group_by fallback is fetched LAZILY below only
            // if the summary didn't include a breakdown. Previously it
            // ran in parallel on every Overview load and its result was
            // discarded — an extra round-trip per render for a fallback
            // path that's dormant in production. Review phase 5 flagged
            // this as dead-weight perf cost.
            const prevPeriodForArchive = calculatePreviousPeriod(dateRange, comparisonType);
            // Lost Leads = leads ARCHIVED in the period (archived_at). The
            // archived-lead-summary endpoint (breakdown=full) returns the count
            // + byReason in one fast, cached, server-side query on the correct
            // archived_at basis — no more paginating hundreds of full archived
            // leads to the browser (which timed out at 10s and zeroed the KPI).
            const archivedDataPromise = Promise.all([
                fetchArchivedSummaryWithBreakdown(ids, dateRange),
                // null = previous period unavailable -> the KPI delta is hidden, not shown against 0.
                fetchArchivedCountByArchivedAt(ids, prevPeriodForArchive).catch(() => null),
            ]);

            // Settled-state guard: if a leads read throws first, this must not surface as an unhandled rejection.
            archivedDataPromise.catch(() => {});

            let totalLeads; let consultationsBooked; let attended; let treatmentStarted; let completed; let fta;
            // Funnel uses historical counts (how many ever passed through each stage)
            let funnelBooked; let funnelAttended; let funnelStarted; let funnelCompleted; let funnelFta;
            let prevTotalLeads; let prevConsultationsBooked; let prevAttended; let prevTreatmentStartedCount;
            let practitionerStatsRes; let treatmentDetailsRes;
            let leadSourceRes; let referralRes; let treatmentRevenueRes;

            if (useSummaryEndpoint) {
                // Period comparison: uses lead-status-summary for current KPIs + direct calls for previous period
                // Funnel uses state_transition_params for historical counts (how many ever passed through each stage)
                const prevPeriod = calculatePreviousPeriod(dateRange, comparisonType);
                const prevTreatmentStartedParams = encodeURIComponent(`{"state":"TREATMENT_STARTED","timestamp_gte":"${prevPeriod.start}","timestamp_lte":"${prevPeriod.end}"}`);
                const prevConsultationBookedParams = encodeURIComponent(`{"state":"CONSULTATION_BOOKED","timestamp_gte":"${prevPeriod.start}","timestamp_lte":"${prevPeriod.end}"}`);

                // Funnel-specific: state_transition_params for current period (historical counts)
                const funnelBookedParams = encodeURIComponent(`{"state":"CONSULTATION_BOOKED","timestamp_gte":"${dateRange.start}","timestamp_lte":"${dateRange.end}"}`);
                const funnelStartedParams = encodeURIComponent(`{"state":"TREATMENT_STARTED","timestamp_gte":"${dateRange.start}","timestamp_lte":"${dateRange.end}"}`);
                const funnelClosedParams = encodeURIComponent(`{"state":"CLOSED","timestamp_gte":"${dateRange.start}","timestamp_lte":"${dateRange.end}"}`);
                // Attended / FTA are appointment-dated (spec 2026-10-21 §3.1): one
                // strict appointment-stages call per window covers both stages.
                const apptCurrentCall = fetchAppointmentStageCounts(ids, { start: dateRange.start, end: dateRange.end });
                const apptPrevCall = fetchAppointmentStageCounts(ids, { start: prevPeriod.start, end: prevPeriod.end, stages: [APPOINTMENT_STAGE.ATTENDED] });

                const [
                    totalLeadsRes,
                    prevTotalLeadsRes,
                    prevConsultationBookedRes,
                    prevTreatmentStartedRes,
                    funnelBookedRes,
                    funnelStartedRes,
                    funnelClosedRes,
                    apptCurrent,
                    apptPrev,
                    practitionerStatsResLocal,
                    treatmentDetailsResLocal,
                    leadSourceResLocal,
                    referralResLocal,
                    treatmentRevenueResLocal,
                ] = await Promise.all([
                    callApi(`${baseUrl}/leads/?aggregate[Count]=id&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}${pSuffix}`),
                    callApi(`${baseUrl}/leads/?aggregate[Count]=id&created_at__gte=${prevPeriod.start}&created_at__lte=${prevPeriod.end}${pSuffix}`),
                    callApi(`${baseUrl}/leads/?group_by[lead_treatments__state_transition_logs__state]&aggregate[Count]=id&state_transition_params=${prevConsultationBookedParams}${pSuffix}`),
                    callApi(`${baseUrl}/leads/?group_by[lead_treatments__state_transition_logs__state]&aggregate[Count]=id&state_transition_params=${prevTreatmentStartedParams}${pSuffix}`),
                    // Funnel: Booked / Started / Closed keep the transition basis (§10)
                    callApi(`${baseUrl}/leads/?group_by[lead_treatments__state_transition_logs__state]&aggregate[Count]=id&state_transition_params=${funnelBookedParams}${pSuffix}`),
                    callApi(`${baseUrl}/leads/?group_by[lead_treatments__state_transition_logs__state]&aggregate[Count]=id&state_transition_params=${funnelStartedParams}${pSuffix}`),
                    callApi(`${baseUrl}/leads/?group_by[lead_treatments__state_transition_logs__state]&aggregate[Count]=id&state_transition_params=${funnelClosedParams}${pSuffix}`),
                    apptCurrentCall,
                    apptPrevCall,
                    ...sharedCalls,
                ]);

                practitionerStatsRes = practitionerStatsResLocal;
                treatmentDetailsRes = treatmentDetailsResLocal;
                leadSourceRes = leadSourceResLocal;
                referralRes = referralResLocal;
                treatmentRevenueRes = treatmentRevenueResLocal;

                // Use created_at count for Total Leads (accurate count of leads created in period)
                // Current-period reads gate the whole KPI row (a failed one must not render as 0);
                // previous-period reads only feed the delta, so a failure there yields null (delta hidden).
                [totalLeadsRes, funnelBookedRes, funnelStartedRes, funnelClosedRes]
                    .forEach((r) => requireOverviewRes(r, 'Overview KPI'));
                requireOverviewRes(apptCurrent, 'Overview KPI (appointment stages)');
                totalLeads = totalLeadsRes?.data?.data?.count_id || 0;
                prevTotalLeads = failedRes(prevTotalLeadsRes) ? null : (prevTotalLeadsRes?.data?.data?.count_id || 0);

                // Extract historical counts from state_transition_params responses
                // These count leads that PASSED THROUGH each status in the date range
                // (not just leads currently sitting in that status)
                const extractCount = (res, state) => (res?.data?.data || []).find(
                    (x) => x.lead_treatments__state_transition_logs__state === state
                )?.count_id || 0;
                funnelBooked = extractCount(funnelBookedRes, 'CONSULTATION_BOOKED');
                funnelAttended = apptCurrent.counts[APPOINTMENT_STAGE.ATTENDED];
                funnelStarted = extractCount(funnelStartedRes, 'TREATMENT_STARTED');
                funnelCompleted = extractCount(funnelClosedRes, 'CLOSED');
                funnelFta = apptCurrent.counts[APPOINTMENT_STAGE.FTA];

                // KPI cards use same historical transition counts as funnel for consistency
                consultationsBooked = funnelBooked;
                attended = funnelAttended;
                treatmentStarted = funnelStarted;
                completed = funnelCompleted;
                fta = funnelFta;

                // Use direct API calls for previous period status KPIs (lead-status-summary previous data unreliable)
                prevConsultationsBooked = failedRes(prevConsultationBookedRes) ? null : (prevConsultationBookedRes?.data?.data || []).find(
                    (x) => x.lead_treatments__state_transition_logs__state === 'CONSULTATION_BOOKED'
                )?.count_id || 0;
                prevAttended = failedRes(apptPrev) ? null : apptPrev.counts[APPOINTMENT_STAGE.ATTENDED];
                prevTreatmentStartedCount = failedRes(prevTreatmentStartedRes) ? null : (prevTreatmentStartedRes?.data?.data || []).find(
                    (x) => x.lead_treatments__state_transition_logs__state === 'TREATMENT_STARTED'
                )?.count_id || 0;
            } else {
                // YoY comparison: legacy multi-call approach
                const treatmentStartedParams = encodeURIComponent(`{"state":"TREATMENT_STARTED","timestamp_gte":"${dateRange.start}","timestamp_lte":"${dateRange.end}"}`);
                const consultationBookedParams = encodeURIComponent(`{"state":"CONSULTATION_BOOKED","timestamp_gte":"${dateRange.start}","timestamp_lte":"${dateRange.end}"}`);
                const closedParams = encodeURIComponent(`{"state":"CLOSED","timestamp_gte":"${dateRange.start}","timestamp_lte":"${dateRange.end}"}`);

                const prevPeriod = calculatePreviousPeriod(dateRange, comparisonType);
                const prevTreatmentStartedParams = encodeURIComponent(`{"state":"TREATMENT_STARTED","timestamp_gte":"${prevPeriod.start}","timestamp_lte":"${prevPeriod.end}"}`);
                const prevConsultationBookedParams = encodeURIComponent(`{"state":"CONSULTATION_BOOKED","timestamp_gte":"${prevPeriod.start}","timestamp_lte":"${prevPeriod.end}"}`);
                // Attended / FTA are appointment-dated (spec 2026-10-21 §3.1): one
                // strict appointment-stages call per window covers both stages.
                const apptCurrentCall = fetchAppointmentStageCounts(ids, { start: dateRange.start, end: dateRange.end });
                const apptPrevCall = fetchAppointmentStageCounts(ids, { start: prevPeriod.start, end: prevPeriod.end, stages: [APPOINTMENT_STAGE.ATTENDED] });

                const [
                    totalLeadsRes,
                    consultationBookedRes,
                    treatmentStartedRes,
                    closedRes,
                    practitionerStatsResLocal,
                    treatmentDetailsResLocal,
                    leadSourceResLocal,
                    referralResLocal,
                    treatmentRevenueResLocal,
                    prevTotalLeadsRes,
                    prevConsultationBookedRes,
                    prevTreatmentStartedRes,
                    apptCurrent,
                    apptPrev,
                ] = await Promise.all([
                    callApi(`${baseUrl}/leads/?aggregate[Count]=id&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}${pSuffix}`),
                    callApi(`${baseUrl}/leads/?group_by[lead_treatments__state_transition_logs__state]&aggregate[Count]=id&state_transition_params=${consultationBookedParams}${pSuffix}`),
                    callApi(`${baseUrl}/leads/?group_by[lead_treatments__state_transition_logs__state]&aggregate[Count]=id&state_transition_params=${treatmentStartedParams}${pSuffix}`),
                    callApi(`${baseUrl}/leads/?group_by[lead_treatments__state_transition_logs__state]&aggregate[Count]=id&state_transition_params=${closedParams}${pSuffix}`),
                    ...sharedCalls,
                    callApi(`${baseUrl}/leads/?aggregate[Count]=id&created_at__gte=${prevPeriod.start}&created_at__lte=${prevPeriod.end}${pSuffix}`),
                    callApi(`${baseUrl}/leads/?group_by[lead_treatments__state_transition_logs__state]&aggregate[Count]=id&state_transition_params=${prevConsultationBookedParams}${pSuffix}`),
                    callApi(`${baseUrl}/leads/?group_by[lead_treatments__state_transition_logs__state]&aggregate[Count]=id&state_transition_params=${prevTreatmentStartedParams}${pSuffix}`),
                    apptCurrentCall,
                    apptPrevCall,
                ]);

                practitionerStatsRes = practitionerStatsResLocal;
                treatmentDetailsRes = treatmentDetailsResLocal;
                leadSourceRes = leadSourceResLocal;
                referralRes = referralResLocal;
                treatmentRevenueRes = treatmentRevenueResLocal;

                [totalLeadsRes, consultationBookedRes, treatmentStartedRes, closedRes]
                    .forEach((r) => requireOverviewRes(r, 'Overview KPI'));
                requireOverviewRes(apptCurrent, 'Overview KPI (appointment stages)');
                totalLeads = totalLeadsRes?.data?.data?.count_id || 0;
                const consultationBookedData = consultationBookedRes?.data?.data || [];
                const treatmentStartedData = treatmentStartedRes?.data?.data || [];

                consultationsBooked = consultationBookedData.find(
                    (x) => x.lead_treatments__state_transition_logs__state === 'CONSULTATION_BOOKED'
                )?.count_id || 0;
                attended = apptCurrent.counts[APPOINTMENT_STAGE.ATTENDED];
                treatmentStarted = treatmentStartedData.find(
                    (x) => x.lead_treatments__state_transition_logs__state === 'TREATMENT_STARTED'
                )?.count_id || 0;

                const closedData = closedRes?.data?.data || [];
                completed = closedData.find(
                    (x) => x.lead_treatments__state_transition_logs__state === 'CLOSED'
                )?.count_id || 0;
                fta = apptCurrent.counts[APPOINTMENT_STAGE.FTA];

                // YoY path: KPI values are already from state_transition_params (historical), reuse for funnel
                funnelBooked = consultationsBooked;
                funnelAttended = attended;
                funnelStarted = treatmentStarted;
                funnelCompleted = completed;
                funnelFta = fta;

                prevTotalLeads = failedRes(prevTotalLeadsRes) ? null : (prevTotalLeadsRes?.data?.data?.count_id || 0);
                prevConsultationsBooked = failedRes(prevConsultationBookedRes) ? null : (prevConsultationBookedRes?.data?.data || []).find(
                    (x) => x.lead_treatments__state_transition_logs__state === 'CONSULTATION_BOOKED'
                )?.count_id || 0;
                prevAttended = failedRes(apptPrev) ? null : apptPrev.counts[APPOINTMENT_STAGE.ATTENDED];
                prevTreatmentStartedCount = failedRes(prevTreatmentStartedRes) ? null : (prevTreatmentStartedRes?.data?.data || []).find(
                    (x) => x.lead_treatments__state_transition_logs__state === 'TREATMENT_STARTED'
                )?.count_id || 0;
            }

            // Process shared data (same for both paths)
            const practitionerStatsRaw = practitionerStatsRes?.data;
            const practitionerStats = Array.isArray(practitionerStatsRaw)
                ? practitionerStatsRaw
                : (practitionerStatsRaw?.results || []);
            const treatmentDetails = treatmentDetailsRes?.data?.results || [];
            const sourceStats = leadSourceRes?.data?.data || [];
            const referralStats = referralRes?.data?.data || [];
            const treatmentRevenueData = treatmentRevenueRes?.data?.data || [];

            // Current summary returns {count, byReason, ...}; previous is just a
            // count (the MoM comparison only needs the headline number). Count +
            // byReason share the archived_at basis, so the KPI, the funnel
            // "Archived" stage, the reason widget, and the drilldown popup agree.
            const [archivedSummary, prevLostLeads] = await archivedDataPromise;
            const lostLeads = archivedSummary.count;
            const archivedByReason = archivedSummary.byReason;

            dispatch(setArchivedLeads({
                total: lostLeads,
                byReason: archivedByReason,
            }));

            // Set KPIs with comparison data
            dispatch(setKpis({
                totalLeads: { value: totalLeads, previousValue: prevTotalLeads },
                consultationsBooked: { value: consultationsBooked, previousValue: prevConsultationsBooked },
                attended: { value: attended, previousValue: prevAttended },
                treatmentStarted: { value: treatmentStarted, previousValue: prevTreatmentStartedCount },
                lostLeads: { value: lostLeads, previousValue: prevLostLeads },
            }));

            // Build funnel data
            // Funnel uses historical counts (state_transition_params) so bar matches popup
            dispatch(setFunnel([
                // Funnel palette — one unique colour per stage, keyed off the lead-status chip palette:
                // blue → purple → amber/yellow → green → grey → orange → red.
                { stage: 'Total Leads', value: totalLeads, color: 'linear-gradient(90deg, #3b82f6, #60a5fa)' },
                { stage: 'Consultations Booked', value: funnelBooked, color: 'linear-gradient(90deg, #8b5cf6, #a78bfa)' },
                { stage: 'Attended', value: funnelAttended, color: 'linear-gradient(90deg, #f59e0b, #fbbf24)' },
                { stage: 'Treatment Started', value: funnelStarted, color: 'linear-gradient(90deg, #10b981, #34d399)' },
                { stage: 'Completed Treatment', value: funnelCompleted, color: 'linear-gradient(90deg, #64748b, #94a3b8)' },
                { stage: 'Failed to Attend', value: funnelFta, color: 'linear-gradient(90deg, #f97316, #fb923c)', branch: true },
                { stage: 'Archived', value: lostLeads, color: 'linear-gradient(90deg, #ef4444, #f87171)', branch: true, caption: 'Leads archived in this period' },
            ]));

            // Build treatment breakdown from treatment details with actual revenue
            const treatmentCategoryMap = {};
            treatmentDetails.forEach((t) => {
                const catId = t.treatment_item?.treatment_category?.id;
                const catName = t.treatment_item?.treatment_category?.name;
                if (catId && catName) {
                    if (!treatmentCategoryMap[catId]) {
                        treatmentCategoryMap[catId] = {
                            id: catId,
                            name: catName,
                            color: t.colour || '#6366f1',
                            // LEAD count, filled from the grouped revenue query below.
                            // (This used to count enabled treatments in the category
                            // and show that as a lead count for empty categories.)
                            count: 0,
                            revenue: 0,
                        };
                    }
                }
            });

            // Merge in actual revenue and lead counts from treatment revenue data
            treatmentRevenueData.forEach((revenueItem) => {
                const catName = revenueItem.lead_treatments__treatment__treatment_item__treatment_category__name;
                if (catName) {
                    // Find matching category by name
                    const matchingCategory = Object.values(treatmentCategoryMap).find(
                        (c) => c.name?.toLowerCase() === catName?.toLowerCase()
                    );
                    if (matchingCategory) {
                        matchingCategory.count = revenueItem.count_id || 0;
                        matchingCategory.revenue = revenueItem.sum_lead_treatments__custom_price || 0;
                    } else {
                        // Add category if not found in treatment details
                        const newId = `cat_${catName}`;
                        treatmentCategoryMap[newId] = {
                            id: newId,
                            name: catName,
                            color: '#6366f1',
                            count: revenueItem.count_id || 0,
                            revenue: revenueItem.sum_lead_treatments__custom_price || 0,
                        };
                    }
                }
            });
            dispatch(setTreatmentBreakdown(Object.values(treatmentCategoryMap)));

            // Process and dispatch source data
            const sources = bucketOverviewSources(sourceStats);
            dispatch(setOverviewSources(sources));

            // Process and dispatch referral data
            const referrals = referralStats
                .filter((r) => r.lead_source__referral) // Filter out null referrals
                .map((r) => ({
                    name: r.lead_source__referral,
                    leads: r.count_id || 0,
                    revenue: r.sum_lead_treatments__custom_price || 0,
                }))
                .sort((a, b) => b.leads - a.leads);
            dispatch(setOverviewReferrals(referrals));

            // Set top practitioners - dentists only (Practitioner as any role)
            const dentistsOnly = practitionerStats.filter((p) => hasPractitionerRole(p));
            const sortedPractitioners = [...dentistsOnly]
                .sort((a, b) => (b.conversions_value || 0) - (a.conversions_value || 0))
                .slice(0, 5)
                .map((p, idx) => ({
                    rank: idx + 1,
                    id: p.user?.id,
                    name: `${p.user?.first_name || ''} ${p.user?.last_name || ''}`.trim(),
                    avatar: p.user?.avatar,
                    conversions: p.conversions_count || 0,
                    revenue: p.conversions_value || 0,
                    conversionRate: p.consultations_count > 0 ? ((p.conversions_count / p.consultations_count) * 100).toFixed(1) : 0,
                }));
            dispatch(setTopPractitioners(sortedPractitioners));

            dispatch(setFetchKey({ key: '_overviewFetchKey', value: `${practiceId}|${dateRange.start}|${dateRange.end}|${comparisonType}` }));
            dispatch(setLoading(false));
        } catch (error) {
            console.error('Failed to fetch overview analytics', error);
            dispatch(setLoading(false));
            dispatch(setError(error.message));
            dispatch(updateError({ message: error.message || 'Analytics load failed', success: false }));
        }
    };
}

export function fetchPractitionersData(practiceIdOrIds) {
    const ids = toPracticeIds(practiceIdOrIds);
    const practiceId = ids[0];
    return async (dispatch) => {
        if (!practiceId) return;
        if (!hasFeatureAccess('analytics-reports')) return;
        dispatch(setLoading(true));
        const baseUrl = `/api/v1/practices/${practiceId}/reports`;
        // Multi-practice aggregation — PracticeUserConversionReport accepts
        // practice__in and unions practitioners across every selected practice.
        const practiceInQuery = ids.length > 1 ? `?practice__in=${ids.join(',')}` : '';

        try {
            // Note: The practice-user endpoint returns all-time stats
            // Date filtering is not supported for practitioner statistics
            const practitionerStatsRes = await callApi(`${baseUrl}/practice-user/${practiceInQuery}`);

            // Handle practitioner stats - could be array directly or have results property
            const practitionerStatsRaw = practitionerStatsRes?.data;
            const allPractitionerStats = Array.isArray(practitionerStatsRaw)
                ? practitionerStatsRaw
                : (practitionerStatsRaw?.results || []);

            // Dentists only (Practitioner as primary or additional role)
            const practitionerStats = allPractitionerStats.filter((p) => hasPractitionerRole(p));

            const practitioners = practitionerStats.map((p, idx) => ({
                id: p.user?.id,
                rank: idx + 1,
                name: `${p.user?.first_name || ''} ${p.user?.last_name || ''}`.trim(),
                avatar: p.user?.avatar,
                email: p.user?.email,
                conversions: p.conversions_count || 0,
                revenue: p.conversions_value || 0,
                consultations: p.consultations_count || 0,
                conversionRate: p.consultations_count > 0
                    ? ((p.conversions_count / p.consultations_count) * 100).toFixed(1)
                    : 0,
            }));

            // Sort by revenue by default
            practitioners.sort((a, b) => b.revenue - a.revenue);
            practitioners.forEach((p, idx) => { p.rank = idx + 1; });

            dispatch(setPractitioners(practitioners));
            dispatch(setLoading(false));
        } catch (error) {
            console.error('Failed to fetch practitioners data', error);
            dispatch(setLoading(false));
            dispatch(setError(error.message));
        }
    };
}

export function fetchSourcesData(practiceIdOrIds, dateRange) {
    const ids = toPracticeIds(practiceIdOrIds);
    const practiceId = ids[0];
    const pSuffix = practiceInSuffix(ids);
    return async (dispatch) => {
        if (!practiceId) return;
        if (!hasFeatureAccess('analytics-reports')) return;
        dispatch(setLoading(true));
        const baseUrl = `/api/v1/practices/${practiceId}/reports`;

        try {
            const [leadSourceRes, referralRes] = await Promise.all([
                callApi(`${baseUrl}/leads/?group_by[lead_source__type]&aggregate[Count]=id&aggregate[Sum]=lead_treatments__custom_price&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}${pSuffix}`),
                callApi(`${baseUrl}/leads/?group_by[lead_source__referral]&aggregate[Count]=id&aggregate[Sum]=lead_treatments__custom_price&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}${pSuffix}`),
            ]);

            const sourceStats = leadSourceRes?.data?.data || [];
            const referralStats = referralRes?.data?.data || [];

            const sources = sourceStats.map((s) => ({
                type: s.lead_source__type || 'Unknown',
                leads: s.count_id || 0,
                revenue: s.sum_lead_treatments__custom_price || 0,
                conversionRate: 0, // Would need additional calculation
            }));

            dispatch(setSources(sources));

            // Process referrals as campaigns
            const campaigns = referralStats
                .filter((r) => r.lead_source__referral)
                .map((r) => ({
                    name: r.lead_source__referral,
                    leads: r.count_id || 0,
                    revenue: r.sum_lead_treatments__custom_price || 0,
                }));

            dispatch(setCampaigns(campaigns));
            dispatch(setLoading(false));
        } catch (error) {
            console.error('Failed to fetch sources data', error);
            dispatch(setLoading(false));
            dispatch(setError(error.message));
        }
    };
}

// Latest-wins guard for the grouped tables (a slow older response must not
// overwrite a newer one after a quick date/practice change).
const groupedFetchTokens = {};
export const nextGroupedFetchToken = (key) => {
    groupedFetchTokens[key] = (groupedFetchTokens[key] || 0) + 1;
    return groupedFetchTokens[key];
};
const isCurrentGroupedFetch = (key, token) => groupedFetchTokens[key] === token;

// Fetch source status breakdown (for By Source page)
// Shared engine for the By-Source "status breakdown" tables (Source + Referral).
// Both tables are the SAME report under a different group_by dimension, so the
// only differences are captured in `opts`. Preserves the reporting contract
// exactly (per reportMetricSpecs):
//   - Enquiries column: unit=lead, basis=created_at, dayBounds, archived+dupes included
//   - status columns:   unit=treatment-transition, basis=state_transition (same)
//   - revenue:          realised (CLOSED transition group) — unchanged
// opts:
//   groupByField   — backend group_by key (e.g. 'lead_source__type')
//   idKey          — row property carrying the group value ('sourceType' | 'referral')
//   nullGroupValue — value for a null/empty group; if null, SKIP that group
//   loadingAction / dataAction — slice actions; fetchKeyName — dedupe key
function fetchGroupedStatusData(practiceIdOrIds, dateRange, opts) {
    const { groupByField, idKey, nullGroupValue, loadingAction, dataAction, fetchKeyName } = opts;
    const ids = toPracticeIds(practiceIdOrIds);
    const practiceId = ids[0];
    const pSuffix = practiceInSuffix(ids);
    // Resolve a row's group value; '' signals "skip this row" (null handling).
    const groupOf = (item) => {
        const raw = item[groupByField];
        if (raw) return raw;
        return nullGroupValue == null ? '' : nullGroupValue;
    };
    return async (dispatch) => {
        if (!practiceId) return;
        if (!hasFeatureAccess('analytics-reports')) return;
        // Stale-response guard: only the newest request per table may write.
        const token = nextGroupedFetchToken(fetchKeyName);
        dispatch(loadingAction(true));
        const baseUrl = `/api/v1/practices/${practiceId}/reports`;

        try {
            // Enquiries — unique leads CREATED in the period (created_at basis),
            // plus ONE query per state with the state pinned in
            // state_transition_params. Without a pinned state the backend counts
            // every state log of any lead that had one in the window ("every
            // stage ever": Booked 56 vs the true 42).
            const [enquiryRes, perState] = await Promise.all([
                callApi(
                    `${baseUrl}/leads/?group_by[${groupByField}]&aggregate[Count]=id&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}${pSuffix}`
                ),
                fetchPerStateGroupedRows({
                    baseUrl, groupByFields: [groupByField], start: dateRange.start, end: dateRange.end, suffix: pSuffix,
                }),
            ]);
            if (!isCurrentGroupedFetch(fetchKeyName, token)) return;
            // A failed enquiries read must not render as zero enquiries.
            if (!enquiryRes || enquiryRes.ok === false) throw new Error(`Enquiries request failed for ${idKey}`);

            const rows = foldGroupedStatusRows({
                enquiryRows: enquiryRes?.data?.data || [], perState, groupOf, idKey,
            });

            dispatch(setFetchKey({ key: fetchKeyName, value: `${ids.join(',')}|${dateRange.start}|${dateRange.end}` }));
            dispatch(dataAction({ data: rows, isLoading: false }));
        } catch (error) {
            console.error(`Failed to fetch ${idKey} status data`, error);
            if (!isCurrentGroupedFetch(fetchKeyName, token)) return;
            dispatch(dataAction({ data: [], isLoading: false }));
            dispatch(updateError({ message: error?.message || `Failed to load ${idKey} report`, success: false }));
            // Release the cache key so a retry isn't deduped away.
            dispatch(setFetchKey({ key: fetchKeyName, value: null }));
        }
    };
}

export function fetchSourceStatusData(practiceIdOrIds, dateRange) {
    // Source rows: null source type → bucketed as 'Unknown' (kept visible).
    return fetchGroupedStatusData(practiceIdOrIds, dateRange, {
        groupByField: 'lead_source__type',
        idKey: 'sourceType',
        nullGroupValue: 'Unknown',
        loadingAction: setSourceStatusLoading,
        dataAction: setSourceStatusData,
        fetchKeyName: '_sourceFetchKey',
    });
}

// Fetch referral status breakdown (for By Source page)
export function fetchReferralStatusData(practiceIdOrIds, dateRange) {
    // Referral rows: null/empty referral → SKIPPED (not shown as its own row).
    return fetchGroupedStatusData(practiceIdOrIds, dateRange, {
        groupByField: 'lead_source__referral',
        idKey: 'referral',
        nullGroupValue: null,
        loadingAction: setReferralStatusLoading,
        dataAction: setReferralStatusData,
        fetchKeyName: '_referralFetchKey',
    });
}

// Report windows are naive "YYYY-MM-DD HH:mm:ss" strings in UK local time (the BE reads
// them as Europe/London). Log timestamps are UTC instants, so convert the bounds from
// London wall-clock to epoch ms before comparing (BST: 00:00 London = 23:00 UTC the day before).
export const londonBoundMs = (value) => dayjs.tz(value, 'YYYY-MM-DD HH:mm:ss', 'Europe/London').valueOf();

// Distinct-lead count of a state within the window: 1 if ANY treatment of the
// lead has an in-range transition log into `state`, else 0 (never the number of
// log events — a lead with 3 re-entries is still one lead).
export const countLeadStateEntries = (lead, state, startMs, endMs) => {
    const treatments = Array.isArray(lead?.lead_treatments) ? lead.lead_treatments : [];
    const hit = treatments.some((lt) => (lt?.state_transition_logs || []).some((log) => {
        if (log?.state !== state) return false;
        const t = parseApiDate(log.timestamp);
        const ms = t && t.isValid() ? t.valueOf() : NaN;
        return !Number.isNaN(ms) && ms >= startMs && ms <= endMs;
    }));
    return hit ? 1 : 0;
};

// Completed revenue of a lead: each treatment's custom_price ONCE if it was
// closed in the window (not multiplied by the number of CLOSED log events).
export const closedRevenueForLead = (lead, startMs, endMs) =>
    (Array.isArray(lead?.lead_treatments) ? lead.lead_treatments : []).reduce((sum, lt) => {
        const closed = (lt?.state_transition_logs || []).some((log) => {
            if (log?.state !== 'CLOSED') return false;
            const t = parseApiDate(log.timestamp);
            const ms = t && t.isValid() ? t.valueOf() : NaN;
            return !Number.isNaN(ms) && ms >= startMs && ms <= endMs;
        });
        return closed ? sum + (parseFloat(lt?.custom_price || 0) || 0) : sum;
    }, 0);

// Fetch landing-page breakdown — buckets leads by their `entire_url` metadata
// (the form-submission landing URL captured at submit time, same field
// rendered on the lead-detail Meta source view). `entire_url` can live in
// either `lead.lead_metadata` (array of {key,value}) or `lead.lead_source.metadata`
// (JSON object) depending on how the form integration wrote it — check both.
//
// Aggregate group_by on a metadata sub-key isn't reliable, so we paginate
// /leads/ for the period and bucket client-side. 6-wide concurrency mirrors
// TreatmentLeadPopup.
//
// Leads with no entire_url (organic / direct / embeds that didn't capture it)
// fall under a synthetic "(no landing URL)" row so coverage is visible.
export function fetchLandingUrlData(practiceIdOrIds, dateRange) {
    const ids = toPracticeIds(practiceIdOrIds);
    const practiceId = ids[0];
    const pSuffix = practiceInSuffix(ids);
    const NO_URL = '(no landing URL)';
    // Capture cache key up-front so we can (a) reserve the slot before any
    // network call, blocking duplicate concurrent dispatches, and (b) verify
    // the request is still current at each await boundary — otherwise a slow
    // 5000-lead paginated run could clobber the data for a more recent
    // practice/date selection.
    const cacheKey = `${ids.join(',')}|${dateRange.start}|${dateRange.end}`;
    return async (dispatch, getState) => {
        if (!practiceId) return;
        if (!hasFeatureAccess('analytics-reports')) return;
        const isStale = () => getState().analyticsReports._landingUrlFetchKey !== cacheKey;
        // Reserve the key BEFORE any network call so identical in-flight
        // dispatches see _landingUrlFetchKey === cacheKey and stop early.
        // The CrossPracticeTable / CompareTab dispatchers already do a
        // pre-check; this guard keeps us safe even if a caller forgets.
        dispatch(setFetchKey({ key: '_landingUrlFetchKey', value: cacheKey }));
        dispatch(setLandingUrlLoading(true));
        const baseUrl = `/api/v1/practices/${practiceId}`;
        const dateFilter = `&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}`;
        // Trim payload by limiting fields. We need entire_url which lives in
        // EITHER lead_metadata[].value (key='entire_url') OR lead_source.metadata.entire_url.
        // Note: we DON'T use a `lead_source{metadata}` sub-selector — that
        // syntax isn't reliably honoured by the DRF fields parser on every
        // version. Request the flat `lead_source` field and rely on the
        // serializer to include `metadata` by default.
        // state_transition_logs needed for TRANSITION-basis status columns (when a
        // treatment ENTERED each state in range), matching the popup + Source/Referral.
        const fields = 'fields={id,lead_metadata,lead_source,lead_treatments{status,custom_price,state_transition_logs}}';
        // Reporting contract: archived leads INCLUDED (same as every other report
        // aggregate + the popup, which sends is_archived_included=true). Without this
        // the table silently dropped ALL archived leads (~27 of 43 for some practices),
        // so the Enquiries column under-counted vs its popup ("(no landing URL)" 9 vs 25).
        const archived = '&is_archived_included=true';

        // Paginate /leads/ for a query, DEDUPED by lead id. Dedup is essential for
        // the transition query: /leads/?state_transition_params returns one ROW PER
        // MATCHING transition log, so a lead with several in-range transitions repeats
        // (measured 64 rows / 41 leads). Counting rows over-counted status (24 vs the
        // server's ~10). Returns null if a newer dispatch supersedes mid-run.
        const paginate = async (query) => {
            const seen = new Map();
            let fetched = 0;
            const firstResp = await callApi(`${baseUrl}/leads/?${fields}${archived}${query}${pSuffix}&limit=50`);
            if (isStale()) return null;
            const firstRaw = firstResp?.data;
            const firstPage = Array.isArray(firstRaw) ? firstRaw : (firstRaw?.results || []);
            const totalCount = firstRaw?.count ?? firstPage.length;
            const pageSize = firstPage.length || 50;
            const cap = Math.min(totalCount, 5000);
            firstPage.forEach((l) => { if (l && l.id != null) seen.set(l.id, l); });
            fetched = firstPage.length;
            dispatch(setLandingUrlProgress({ loaded: seen.size, total: cap }));
            if (fetched < cap) {
                const remaining = [];
                for (let off = pageSize; off < cap; off += pageSize) remaining.push(off);
                const CONCURRENCY = 6;
                for (let i = 0; i < remaining.length; i += CONCURRENCY) {
                    if (isStale()) return null;
                    const batch = remaining.slice(i, i + CONCURRENCY);
                    // eslint-disable-next-line no-await-in-loop
                    const pages = await Promise.all(batch.map((off) =>
                        callApi(`${baseUrl}/leads/?${fields}${archived}${query}${pSuffix}&limit=${pageSize}&offset=${off}`)
                    ));
                    if (isStale()) return null;
                    const batchRows = pages.flatMap((p) => {
                        const r = p?.data;
                        return Array.isArray(r) ? r : (r?.results || []);
                    });
                    batchRows.forEach((l) => { if (l && l.id != null) seen.set(l.id, l); });
                    fetched += batchRows.length;
                    dispatch(setLandingUrlProgress({ loaded: seen.size, total: cap }));
                    if (fetched >= cap) break;
                }
            }
            return Array.from(seen.values());
        };

        try {
            // TWO passes (mirrors Source/Referral's two aggregate queries):
            //   - enquiryLeads:    CREATED in the period → Enquiries (lead-unit, created_at)
            //   - transitionLeads: ANY state transition in the period → status + revenue
            //     (transition basis). A lead created BEFORE the range but which booked/
            //     attended/etc DURING it belongs in the status columns — it's only in
            //     this set. Both archived + duplicates included.
            const stp = encodeURIComponent(JSON.stringify({ timestamp_gte: dateRange.start, timestamp_lte: dateRange.end }));
            const enquiryLeads = await paginate(dateFilter);
            if (isStale() || enquiryLeads == null) return;
            const transitionLeads = await paginate(`&state_transition_params=${stp}`);
            if (isStale() || transitionLeads == null) return;

            // Bucket by normalised entire_url. Canonical blank/null/missing bucket =
            // '(no landing URL)' (NO_URL) — the same key the popup's landingUrlFilter
            // treats as "empty". Tracking-param-only differences collapse via normalize.
            const urlMap = {};
            const ensureBucket = (url) => {
                if (!urlMap[url]) {
                    urlMap[url] = {
                        id: url,
                        landingUrl: url,
                        enquiries: 0,
                        CONSULTATION_BOOKED: 0,
                        CONSULTATION_ATTENDED: 0,
                        TREATMENT_STARTED: 0,
                        FAILED_TO_ATTEND_CONSULTATION: 0,
                        CLOSED: 0,
                        revenue: 0,
                    };
                }
                return urlMap[url];
            };
            const resolveEntireUrl = (lead) => {
                const fromArray = (Array.isArray(lead?.lead_metadata) ? lead.lead_metadata : [])
                    .find((m) => m?.key === 'entire_url')?.value;
                if (fromArray) return String(fromArray).trim();
                const fromSource = lead?.lead_source?.metadata?.entire_url;
                return fromSource ? String(fromSource).trim() : '';
            };
            const urlOf = (lead) => {
                const raw = resolveEntireUrl(lead);
                return raw ? (normalizeLandingUrl(raw) || NO_URL) : NO_URL;
            };

            // Transition-in-range check — same basis as the popup + Source/Referral.
            const rangeStartMs = londonBoundMs(dateRange.start);
            const rangeEndMs = londonBoundMs(dateRange.end);
            const TRANSITION_STATES = ['CONSULTATION_BOOKED', 'CONSULTATION_ATTENDED', 'TREATMENT_STARTED', 'FAILED_TO_ATTEND_CONSULTATION', 'CLOSED'];
            // DISTINCT-LEAD semantics: a lead counts once per state if any of its
            // treatments has an in-range transition log into that state (the whole
            // report suite counts leads, not events). See countLeadStateEntries.
            const countTransitions = (lead, state) =>
                countLeadStateEntries(lead, state, rangeStartMs, rangeEndMs);

            // Pass 1 — Enquiries: lead-unit, created_at.
            enquiryLeads.forEach((lead) => {
                ensureBucket(urlOf(lead)).enquiries += 1;
            });

            // Pass 2 — status columns + revenue, one count per DISTINCT lead.
            transitionLeads.forEach((lead) => {
                const bucket = ensureBucket(urlOf(lead));
                TRANSITION_STATES.forEach((state) => {
                    bucket[state] += countTransitions(lead, state);
                });
                bucket.revenue += closedRevenueForLead(lead, rangeStartMs, rangeEndMs);
            });

            const landingUrlRows = Object.values(urlMap).map((row) => ({
                ...row,
                conversionRate: row.enquiries > 0
                    ? ((row.TREATMENT_STARTED / row.enquiries) * 100).toFixed(1)
                    : 0,
            }));
            landingUrlRows.sort((a, b) => b.enquiries - a.enquiries);

            if (isStale()) return;
            dispatch(setLandingUrlData({
                data: landingUrlRows,
                isLoading: false,
            }));
        } catch (error) {
            console.error('Failed to fetch landing URL data', error);
            // Don't write to the slot if we're stale — a newer dispatch is in
            // flight and owns the state now.
            if (isStale()) return;
            dispatch(setLandingUrlData({
                data: [],
                isLoading: false,
            }));
            dispatch(updateError({
                success: false,
                message: error?.message || 'Failed to fetch landing URL data',
            }));
            // Release the fetchKey so a subsequent retry isn't deduped away.
            dispatch(setFetchKey({ key: '_landingUrlFetchKey', value: null }));
        }
    };
}

export function fetchTreatmentData(practiceIdOrIds, dateRange, treatmentId = null) {
    const ids = toPracticeIds(practiceIdOrIds);
    const practiceId = ids[0];
    const pSuffix = practiceInSuffix(ids);
    return async (dispatch) => {
        if (!practiceId) return;
        if (!hasFeatureAccess('analytics-reports')) return;
        const requestKey = `${ids.join(',')}|${dateRange.start}|${dateRange.end}|${treatmentId || ''}`;
        const token = beginDispatch('treatmentData', requestKey);
        if (token === null) return;
        dispatch(setLoading(true));
        const baseUrl = `/api/v1/practices/${practiceId}/reports`;

        try {
            const [treatmentDetailsRes, treatmentRevenueRes, totalLeadsRes] = await Promise.all([
                // /treatments/ is not a report endpoint — scopes by URL practice_pk.
                // For multi-practice, fan out and concat. Dedup by treatment_item name
                // downstream so two practices offering "Invisalign" show one row.
                ids.length > 1
                    ? Promise.all(ids.map((pid) => callApi(`/api/v1/practices/${pid}/treatments/?enabled=true&limit=500&ordering=treatment_item__name&fields={id,practice,price,colour,icon,treatment_item{id,name,treatment_category{id,name}}}`)))
                    : callApi(`/api/v1/practices/${practiceId}/treatments/?enabled=true&limit=500&ordering=treatment_item__name&fields={id,practice,price,colour,icon,treatment_item{id,name,treatment_category{id,name}}}`),
                callApi(`${baseUrl}/leads/?aggregate[Count]=id&aggregate[Sum]=lead_treatments__custom_price&group_by[lead_treatments__treatment]&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}${pSuffix}`),
                // Ungrouped total: counts unique leads (no double-counting multi-treatment leads, includes archived)
                callApi(`${baseUrl}/leads/?aggregate[Count]=id&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}${pSuffix}`),
            ]);

            // Multi-practice /treatments/ returns an array of responses; merge and
            // dedupe by treatment_item id (same offering, different practice rows).
            const treatmentDetails = Array.isArray(treatmentDetailsRes)
                ? (() => {
                    const seen = new Map();
                    treatmentDetailsRes.forEach((res) => {
                        (res?.data?.results || []).forEach((t) => {
                            const key = t.treatment_item?.id || t.id;
                            if (!seen.has(key)) seen.set(key, t);
                        });
                    });
                    return Array.from(seen.values());
                })()
                : treatmentDetailsRes?.data?.results || [];
            const treatmentRevenue = treatmentRevenueRes?.data?.data || [];
            // A newer request superseded this one while it was in flight → drop it.
            if (!isLatestDispatch('treatmentData', token)) return;

            // Build treatment list with merged data
            const treatmentList = treatmentDetails.map((t) => {
                const revenueData = treatmentRevenue.find((r) => r.lead_treatments__treatment === t.id);
                return {
                    id: t.id,
                    name: t.treatment_item?.name || 'Unknown',
                    category: t.treatment_item?.treatment_category?.name || 'Other',
                    categoryId: t.treatment_item?.treatment_category?.id,
                    color: t.colour || '#6366f1',
                    price: t.price || 0,
                    leads: revenueData?.count_id || 0,
                    revenue: revenueData?.sum_lead_treatments__custom_price || 0,
                };
            });

            dispatch(setTreatmentList(treatmentList));
            dispatch(setTreatmentTotalLeads(totalLeadsRes?.data?.data?.count_id || 0));

            // If a specific treatment is selected, set its KPIs
            if (treatmentId) {
                const selectedTreatment = treatmentList.find((t) => t.id === treatmentId);
                if (selectedTreatment) {
                    dispatch(setTreatmentKpis({
                        leads: selectedTreatment.leads,
                        revenue: selectedTreatment.revenue,
                        avgValue: selectedTreatment.leads > 0
                            ? (selectedTreatment.revenue / selectedTreatment.leads).toFixed(0)
                            : 0,
                    }));
                }
            }

            dispatch(setFetchKey({ key: '_treatmentFetchKey', value: requestKey }));
            dispatch(setLoading(false));
        } catch (error) {
            console.error('Failed to fetch treatment data', error);
            if (!isLatestDispatch('treatmentData', token)) return;
            failDispatch('treatmentData', token);
            dispatch(setLoading(false));
            dispatch(setError(error.message));
        }
    };
}

export function fetchTrendsData(practiceIdOrIds, dateRange) {
    // Wires Trends to the real /reports/trends/ endpoint. Response shape:
    //   { trend: [{month,label,leads,consultations,treatments},...],
    //     yoy: {leads,consultations,treatments,revenue → {current,previous,change}},
    //     range: {start,end} }
    // Seasonality / forecast / milestones remain placeholder until their
    // respective backend endpoints ship — the UI keeps a "demo data"
    // banner for those three sections.
    const ids = toPracticeIds(practiceIdOrIds);
    const practiceId = ids[0];
    const pInQuery = ids.length > 1 ? `&practice__in=${ids.join(',')}` : '';
    return async (dispatch) => {
        if (!practiceId) return;
        if (!hasFeatureAccess('analytics-reports')) return;
        dispatch(setLoading(true));

        // Default to last 12 months when no range is supplied.
        const end = dateRange?.end ? String(dateRange.end).split(' ')[0] : new Date().toISOString().slice(0, 10);
        let start = dateRange?.start ? String(dateRange.start).split(' ')[0] : null;
        if (!start) {
            const d = new Date(end);
            d.setFullYear(d.getFullYear() - 1);
            start = d.toISOString().slice(0, 10);
        }

        try {
            const res = await callApi(
                `/api/v1/practices/${practiceId}/reports/trends/?start=${start}&end=${end}${pInQuery}`
            );
            // 404 or 5xx → fall back to empty state + log so we don't ship
            // placeholder numbers that look real.
            const data = res?.data;
            if (!data || typeof data !== 'object') {
                throw new Error(`Unexpected trends response: ${res?.status || 'no status'}`);
            }

            dispatch(setTrendData(data.trend || []));

            // Map the yoy object into the array shape the existing
            // YearOverYearChart component consumes.
            const yoy = data.yoy || {};
            const yoyRows = [
                ['Leads', yoy.leads],
                ['Consultations', yoy.consultations],
                ['Treatments', yoy.treatments],
                ['Revenue', yoy.revenue],
            ]
                .filter(([, v]) => v && typeof v === 'object')
                .map(([metric, v]) => ({
                    metric,
                    current: v.current ?? 0,
                    previous: v.previous ?? 0,
                    change: v.change ?? null,
                }));
            dispatch(setYoyData(yoyRows));

            // Seasonality / forecast / milestones are optional backend
            // fields that may or may not be populated depending on
            // deploy state. When present, wire them through; otherwise
            // clear to avoid stale placeholder data. Gating the dispatch
            // on field presence means the day the backend starts
            // returning these fields, they flow through automatically
            // without a frontend change. Without the gate, the prior
            // blanket `setSeasonality([])` would clobber any real
            // backend-provided data.
            dispatch(setSeasonality(Array.isArray(data.seasonality) ? data.seasonality : []));
            dispatch(setForecast(Array.isArray(data.forecast) ? data.forecast : []));
            dispatch(setMilestones(Array.isArray(data.milestones) ? data.milestones : []));

            dispatch(setLoading(false));
        } catch (error) {
            console.error('Failed to fetch trends data', error);
            // Empty the four series rather than showing numbers that
            // look real but aren't. UI component should render its empty
            // state.
            dispatch(setTrendData([]));
            dispatch(setYoyData([]));
            dispatch(setSeasonality([]));
            dispatch(setForecast([]));
            dispatch(setMilestones([]));
            dispatch(setLoading(false));
            dispatch(setError(error.message));
        }
    };
}

// Fetch lead IDs for a specific archive reason
export function fetchArchivedLeadIds(practiceId, reason) {
    return async (dispatch) => {
        try {
            const encodedReason = encodeURIComponent(reason);
            let leadIds = [];

            // Use the /archived/ endpoint with archived_reason__in filter
            // This is the same endpoint used by ArchiveLeadTable
            const archivedResponse = await callApi(
                `/api/v1/practices/${practiceId}/archived/?is_archived=true&archived_reason__in=${encodedReason}&limit=1000`
            );
            leadIds = (archivedResponse?.data?.results || []).map((l) => l.id);

            // Format the title nicely
            const formattedReason = formatArchiveReasonLabel(reason);

            dispatch(setLeadPopup({
                open: true,
                practiceId,
                leadIds,
                title: `Archived Leads: ${formattedReason}`,
            }));
        } catch (error) {
            console.error('Failed to fetch archived lead IDs', error);
            dispatch(updateError({ message: 'Failed to fetch lead details', success: false }));
        }
    };
}

// Fetch WhatsApp data
export function fetchWhatsAppData(practiceId, businessId, phoneId, dateRange) {
    return async (dispatch) => {
        if (!hasFeatureAccess('analytics-reports')) return;
        dispatch(setWhatsAppLoading(true));

        try {
            // If no WhatsApp credentials, return empty state
            if (!businessId || !phoneId) {
                dispatch(setWhatsAppData({
                    totalConversations: 0,
                    activeConversations: 0,
                    closedConversations: 0,
                    avgResponseTime: 0,
                    within24hr: 0,
                    closeReasons: [],
                    responseTimeDistribution: [
                        { bucket: 'lt1h', label: '< 1 hour', count: 0 },
                        { bucket: '1to4h', label: '1-4 hours', count: 0 },
                        { bucket: '4to24h', label: '4-24 hours', count: 0 },
                        { bucket: 'gt24h', label: '> 24 hours', count: 0 },
                    ],
                    leadConversion: {
                        totalWhatsAppLeads: 0,
                        consultationsBooked: 0,
                        treatmentStarted: 0,
                    },
                }));
                dispatch(setWhatsAppLoading(false));
                return;
            }

            const baseUrl = `/api/v1/practices/${practiceId}`;
            const waBaseUrl = `${baseUrl}/whatsapp-business-accounts/${businessId}/whatsapp-phones/${phoneId}`;

            // Fetch threads and leads data in parallel
            const [threadsRes, whatsAppLeadsRes] = await Promise.all([
                callApi(`${waBaseUrl}/whatsapp-message-threads/?is_archived_included=true&limit=1000`),
                callApi(`${baseUrl}/reports/leads/?lead_source__type__in=whatsapp,WHATSAPP,Whatsapp&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}&aggregate[Count]=id`),
            ]);

            const threads = threadsRes?.data?.results || [];
            const whatsAppLeadsCount = whatsAppLeadsRes?.data?.data?.count_id || 0;

            // Filter threads by date range
            const filteredThreads = threads.filter((t) => {
                const createdTime = new Date(t.created_time);
                const startDate = new Date(dateRange.start);
                const endDate = new Date(dateRange.end);
                return createdTime >= startDate && createdTime <= endDate;
            });

            // Calculate metrics
            const totalConversations = filteredThreads.length;
            const closedThreads = filteredThreads.filter((t) => t.is_closed);
            const closedConversations = closedThreads.length;
            const activeConversations = totalConversations - closedConversations;

            // Group by close reasons
            const closeReasonMap = {};
            closedThreads.forEach((t) => {
                const reason = t.metadata?.close_reason || 'Other';
                if (!closeReasonMap[reason]) {
                    closeReasonMap[reason] = { reason, label: reason, count: 0, threadIds: [] };
                }
                closeReasonMap[reason].count += 1;
                closeReasonMap[reason].threadIds.push(t.id);
            });
            const closeReasons = Object.values(closeReasonMap).sort((a, b) => b.count - a.count);

            // Calculate response times (from messages if available)
            // For now, use placeholder metrics - actual implementation would require message-level data
            const avgResponseTime = 0; // Would calculate from first response times
            const within24hr = totalConversations > 0 ? 85 : 0; // Placeholder percentage

            const responseTimeDistribution = [
                { bucket: 'lt1h', label: '< 1 hour', count: Math.floor(totalConversations * 0.4) },
                { bucket: '1to4h', label: '1-4 hours', count: Math.floor(totalConversations * 0.3) },
                { bucket: '4to24h', label: '4-24 hours', count: Math.floor(totalConversations * 0.15) },
                { bucket: 'gt24h', label: '> 24 hours', count: Math.floor(totalConversations * 0.15) },
            ];

            dispatch(setWhatsAppData({
                totalConversations,
                activeConversations,
                closedConversations,
                avgResponseTime,
                within24hr,
                closeReasons,
                responseTimeDistribution,
                leadConversion: {
                    totalWhatsAppLeads: whatsAppLeadsCount,
                    consultationsBooked: Math.floor(whatsAppLeadsCount * 0.6), // Placeholder
                    treatmentStarted: Math.floor(whatsAppLeadsCount * 0.3), // Placeholder
                },
            }));

            dispatch(setWhatsAppLoading(false));
        } catch (error) {
            console.error('Failed to fetch WhatsApp data', error);
            dispatch(setWhatsAppLoading(false));
            dispatch(updateError({ message: 'Failed to load WhatsApp data', success: false }));
        }
    };
}

// Helper to close lead popup
export function closeLeadPopup() {
    return (dispatch) => {
        dispatch(setLeadPopup({
            open: false,
            practiceId: null,
            leadIds: [],
            title: '',
        }));
    };
}

// Fetch lead IDs by KPI type (Total Leads, Consultations Booked, etc.)
export function fetchLeadIdsByKpiType(practiceId, kpiType, dateRange) {
    return async (dispatch) => {
        try {
            const baseUrl = `/api/v1/practices/${practiceId}`;
            let leadIds = [];
            let title = '';

            switch (kpiType) {
                case 'totalLeads': {
                    // All leads in date range
                    const response = await callApi(
                        `${baseUrl}/leads/?created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}&limit=1000&fields={id}`
                    );
                    leadIds = (response?.data?.results || []).map((l) => l.id);
                    title = 'Total Leads';
                    break;
                }
                case 'consultationsBooked': {
                    // Leads with BOOKED or ATTENDED appointments
                    const response = await callApi(
                        `${baseUrl}/leads/?appointments__status__in=BOOKED,ATTENDED&appointments__datetime__gte=${dateRange.start}&appointments__datetime__lte=${dateRange.end}&limit=1000&fields={id}`
                    );
                    leadIds = (response?.data?.results || []).map((l) => l.id);
                    title = 'Consultations Booked';
                    break;
                }
                case 'attended': {
                    // Leads with ATTENDED appointments
                    const response = await callApi(
                        `${baseUrl}/leads/?appointments__status=ATTENDED&appointments__datetime__gte=${dateRange.start}&appointments__datetime__lte=${dateRange.end}&limit=1000&fields={id}`
                    );
                    leadIds = (response?.data?.results || []).map((l) => l.id);
                    title = 'Attended Consultations';
                    break;
                }
                case 'treatmentStarted': {
                    // Leads with TREATMENT_STARTED status
                    const response = await callApi(
                        `${baseUrl}/leads/?lead_treatments__status=TREATMENT_STARTED&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}&limit=1000&fields={id}`
                    );
                    leadIds = (response?.data?.results || []).map((l) => l.id);
                    title = 'Treatment Started';
                    break;
                }
                case 'lostLeads': {
                    // /archived/ silently ignores date filters — paginate + client-side
                    // filter by archived_at (see fetchArchivedLeadsInPeriod helper).
                    const archived = await fetchArchivedLeadsInPeriod(practiceId, dateRange);
                    leadIds = archived.map((l) => l.id);
                    title = 'Lost Leads';
                    break;
                }
                default:
                    break;
            }

            dispatch(setLeadPopup({
                open: true,
                practiceId,
                leadIds,
                title,
            }));
        } catch (error) {
            console.error('Failed to fetch lead IDs by KPI type', error);
            dispatch(updateError({ message: 'Failed to fetch lead details', success: false }));
        }
    };
}

// Fetch lead IDs by funnel stage
export function fetchLeadIdsByFunnelStage(practiceId, stage, dateRange) {
    return async (dispatch) => {
        try {
            const baseUrl = `/api/v1/practices/${practiceId}`;
            let leadIds = [];
            const title = stage;

            switch (stage) {
                case 'Total Leads': {
                    const response = await callApi(
                        `${baseUrl}/leads/?created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}&limit=1000&fields={id}`
                    );
                    leadIds = (response?.data?.results || []).map((l) => l.id);
                    break;
                }
                case 'Consultations Booked': {
                    const response = await callApi(
                        `${baseUrl}/leads/?appointments__status__in=BOOKED,ATTENDED&appointments__datetime__gte=${dateRange.start}&appointments__datetime__lte=${dateRange.end}&limit=1000&fields={id}`
                    );
                    leadIds = (response?.data?.results || []).map((l) => l.id);
                    break;
                }
                case 'Attended': {
                    const response = await callApi(
                        `${baseUrl}/leads/?appointments__status=ATTENDED&appointments__datetime__gte=${dateRange.start}&appointments__datetime__lte=${dateRange.end}&limit=1000&fields={id}`
                    );
                    leadIds = (response?.data?.results || []).map((l) => l.id);
                    break;
                }
                case 'Treatment Started': {
                    const response = await callApi(
                        `${baseUrl}/leads/?lead_treatments__status=TREATMENT_STARTED&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}&limit=1000&fields={id}`
                    );
                    leadIds = (response?.data?.results || []).map((l) => l.id);
                    break;
                }
                default:
                    break;
            }

            dispatch(setLeadPopup({
                open: true,
                practiceId,
                leadIds,
                title,
            }));
        } catch (error) {
            console.error('Failed to fetch lead IDs by funnel stage', error);
            dispatch(updateError({ message: 'Failed to fetch lead details', success: false }));
        }
    };
}

// Fetch lead IDs by treatment category
export function fetchLeadIdsByTreatment(practiceId, treatmentCategoryName, dateRange) {
    return async (dispatch) => {
        try {
            const baseUrl = `/api/v1/practices/${practiceId}`;
            const encodedCategoryName = encodeURIComponent(treatmentCategoryName);

            // Try the treatment category filter first
            let response = await callApi(
                `${baseUrl}/leads/?lead_treatments__treatment__treatment_item__treatment_category__name=${encodedCategoryName}&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}&limit=1000`
            );
            let leadIds = (response?.data?.results || []).map((l) => l.id);

            // If no results, try filtering by treatment name directly (some treatments might not have categories)
            if (leadIds.length === 0) {
                response = await callApi(
                    `${baseUrl}/leads/?lead_treatments__treatment__treatment_item__name__icontains=${encodedCategoryName}&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}&limit=1000`
                );
                leadIds = (response?.data?.results || []).map((l) => l.id);
            }

            dispatch(setLeadPopup({
                open: true,
                practiceId,
                leadIds,
                title: `${treatmentCategoryName} Leads`,
            }));
        } catch (error) {
            console.error('Failed to fetch lead IDs by treatment', error);
            dispatch(updateError({ message: 'Failed to fetch lead details', success: false }));
        }
    };
}

// Fetch lead IDs by source type
export function fetchLeadIdsBySource(practiceId, sourceType, dateRange) {
    return async (dispatch) => {
        try {
            const baseUrl = `/api/v1/practices/${practiceId}`;
            const encodedSourceType = encodeURIComponent(sourceType);

            const response = await callApi(
                `${baseUrl}/leads/?lead_source__type=${encodedSourceType}&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}&limit=1000&fields={id}`
            );
            const leadIds = (response?.data?.results || []).map((l) => l.id);

            // Format source type label
            const formattedSource = sourceType
                .replace(/_/g, ' ')
                .replace(/\b\w/g, (c) => c.toUpperCase());

            dispatch(setLeadPopup({
                open: true,
                practiceId,
                leadIds,
                title: `${formattedSource} Leads`,
            }));
        } catch (error) {
            console.error('Failed to fetch lead IDs by source', error);
            dispatch(updateError({ message: 'Failed to fetch lead details', success: false }));
        }
    };
}

// Fetch lead IDs by referral
export function fetchLeadIdsByReferral(practiceId, referralName, dateRange) {
    return async (dispatch) => {
        try {
            const baseUrl = `/api/v1/practices/${practiceId}`;
            const encodedReferral = encodeURIComponent(referralName);

            const response = await callApi(
                `${baseUrl}/leads/?lead_source__referral=${encodedReferral}&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}&limit=1000&fields={id}`
            );
            const leadIds = (response?.data?.results || []).map((l) => l.id);

            dispatch(setLeadPopup({
                open: true,
                practiceId,
                leadIds,
                title: `${referralName} Referral Leads`,
            }));
        } catch (error) {
            console.error('Failed to fetch lead IDs by referral', error);
            dispatch(updateError({ message: 'Failed to fetch lead details', success: false }));
        }
    };
}

// Fetch lead IDs by practitioner
export function fetchLeadIdsByPractitioner(practiceId, practitionerId, practitionerName) {
    return async (dispatch) => {
        try {
            const baseUrl = `/api/v1/practices/${practiceId}`;

            const response = await callApi(
                `${baseUrl}/leads/?assigned_to=${practitionerId}&limit=1000&fields={id}`
            );
            const leadIds = (response?.data?.results || []).map((l) => l.id);

            dispatch(setLeadPopup({
                open: true,
                practiceId,
                leadIds,
                title: `${practitionerName}'s Leads`,
            }));
        } catch (error) {
            console.error('Failed to fetch lead IDs by practitioner', error);
            dispatch(updateError({ message: 'Failed to fetch lead details', success: false }));
        }
    };
}

// Helper to transform API response to monthly format
const transformTreatmentStatsToMonthly = (apiData) => {
    const result = [];
    // API returns: { "2024": { "1": { NEW: {...}, CONSULTATION_BOOKED: {...}, ... }, "2": {...}, ... }, "2025": {...} }
    if (apiData && typeof apiData === 'object') {
        Object.entries(apiData).forEach(([year, months]) => {
            if (months && typeof months === 'object') {
                Object.entries(months).forEach(([month, data]) => {
                    result.push({
                        year: parseInt(year, 10),
                        month: parseInt(month, 10),
                        data: data || {},
                    });
                });
            }
        });
    }
    // Sort by year and month descending (most recent first)
    result.sort((a, b) => {
        if (a.year !== b.year) return b.year - a.year;
        return b.month - a.month;
    });
    return result;
};

// Fetch monthly enquiries data for a specific treatment
export function fetchMonthlyEnquiries(practiceId, treatmentId, dateRange) {
    return async (dispatch) => {
        if (!hasFeatureAccess('analytics-reports')) return;
        dispatch(setMonthlyEnquiriesLoading(true));

        try {
            // Build API URL
            let url = `/api/v1/practices/${practiceId}/reports/lead-treatment-statistics/`;
            const params = [];

            if (treatmentId) {
                params.push(`treatment=${treatmentId}`);
            }
            if (dateRange?.start) {
                // Extract just the date part (YYYY-MM-DD) without time
                const startDate = dateRange.start.split(' ')[0];
                params.push(`time_after=${startDate}`);
            }
            if (dateRange?.end) {
                // Add one day so the end date is inclusive — the API treats time_before as exclusive
                const endDate = dayjs(dateRange.end.split(' ')[0]).add(1, 'day').format('YYYY-MM-DD');
                params.push(`time_before=${endDate}`);
            }
            if (params.length > 0) {
                url += `?${params.join('&')}`;
            }

            const response = await callApi(url);

            if (response?.status === 200) {
                const monthlyData = transformTreatmentStatsToMonthly(response.data);
                dispatch(setMonthlyEnquiries({
                    data: monthlyData,
                    selectedTreatmentId: treatmentId,
                    isLoading: false,
                }));
            } else {
                dispatch(setMonthlyEnquiries({
                    data: [],
                    isLoading: false,
                }));
            }
        } catch (error) {
            dispatch(setMonthlyEnquiries({
                data: [],
                isLoading: false,
            }));
        }
    };
}

// Fetch treatment funnel data using state_transition_params (consistent with Overview funnel).
// treatmentIds: array of practice treatment IDs to filter by, or null for all treatments.
export function fetchTreatmentFunnelData(practiceIdOrIds, dateRange, treatmentIds = null) {
    const ids = toPracticeIds(practiceIdOrIds);
    const practiceId = ids[0];
    const pSuffix = practiceInSuffix(ids);
    return async (dispatch) => {
        if (!practiceId) return;
        if (!hasFeatureAccess('analytics-reports')) return;
        // Same scope already fetched / in flight → don't re-issue (7 reports/leads calls).
        const dispatchKey = `${ids.join(',')}|${dateRange.start}|${dateRange.end}|${(treatmentIds || []).join(',')}`;
        const token = beginDispatch('treatmentFunnel', dispatchKey);
        if (token === null) return;
        // Clear stale data immediately so category switches don't flash old numbers
        dispatch(setTreatmentFunnelData(null));
        // An EMPTY id list means "this scope has no treatments" → match nothing (zeros),
        // NOT "no filter". null/undefined is the unfiltered "All" scope.
        if (Array.isArray(treatmentIds) && treatmentIds.length === 0) {
            dispatch(setTreatmentFunnelData({
                totalLeads: 0, consultationsBooked: 0, consultationsAttended: 0, treatmentStarted: 0, completed: 0, fta: 0, archived: 0,
            }));
            return;
        }
        const baseUrl = `/api/v1/practices/${practiceId}/reports`;
        const treatmentFilter = treatmentIds?.length
            ? `&lead_treatments__treatment__in=${treatmentIds.join(',')}`
            : '';

        // Booked / Started / Closed stay on the transition basis (§10); Attended and
        // FTA are appointment-dated and come from the appointment-stages endpoint.
        const statuses = [
            'CONSULTATION_BOOKED',
            'TREATMENT_STARTED',
            'CLOSED',
        ];

        try {
            const funnelCalls = statuses.map((status) => {
                const params = encodeURIComponent(JSON.stringify({
                    state: status,
                    timestamp_gte: dateRange.start,
                    timestamp_lte: dateRange.end,
                }));
                return callApi(`${baseUrl}/leads/?group_by[lead_treatments__state_transition_logs__state]&aggregate[Count]=id&state_transition_params=${params}${treatmentFilter}${pSuffix}`);
            });

            const totalLeadsCall = callApi(
                `${baseUrl}/leads/?aggregate[Count]=id&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}${treatmentFilter}${pSuffix}`
            );
            // Archived count via the original fast group_by query. Yes, this is
            // "created_at in period AND archived" rather than "archived_at in period"
            // — but it's fast, consistent with the rest of the funnel (which also
            // uses created_at/state_transition semantics), and the By Treatment
            // funnel was working fine this way before.
            const archivedCall = callApi(
                `${baseUrl}/leads/?group_by[is_archived]&aggregate[Count]=id&is_archived=true&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}${treatmentFilter}${pSuffix}`
            );

            const apptCall = fetchAppointmentStageCounts(ids, {
                start: dateRange.start, end: dateRange.end, treatmentIds: treatmentIds?.length ? treatmentIds : null,
            });

            const [bookedRes, startedRes, closedRes, totalRes, archivedRes, apptRes] =
                await Promise.all([...funnelCalls, totalLeadsCall, archivedCall, apptCall]);
            // Superseded while in flight (scope/date changed) → never apply stale numbers.
            if (!isLatestDispatch('treatmentFunnel', token)) return;
            // A failed appointment-stage read must not render as 0 (same rule as Overview).
            if (!apptRes?.ok) throw new Error('By Treatment funnel: appointment-stage counts request failed');
            const archivedCount = (archivedRes?.data?.data || []).find((x) => x.is_archived)?.count_id || 0;

            const extractCount = (res, state) =>
                (res?.data?.data || []).find(
                    (x) => x.lead_treatments__state_transition_logs__state === state
                )?.count_id || 0;

            dispatch(setTreatmentFunnelData({
                totalLeads: totalRes?.data?.data?.count_id || 0,
                consultationsBooked: extractCount(bookedRes, 'CONSULTATION_BOOKED'),
                consultationsAttended: apptRes.counts[APPOINTMENT_STAGE.ATTENDED],
                treatmentStarted: extractCount(startedRes, 'TREATMENT_STARTED'),
                completed: extractCount(closedRes, 'CLOSED'),
                fta: apptRes.counts[APPOINTMENT_STAGE.FTA],
                archived: archivedCount || 0,
            }));
        } catch (error) {
            if (!isLatestDispatch('treatmentFunnel', token)) return;
            failDispatch('treatmentFunnel', token);
            dispatch(setTreatmentFunnelData(null));
        }
    };
}

// ── By Treatment: single bulk lead-treatment-statistics call ─────────────────
// Contract (BE): GET /practices/{pk}/reports/lead-treatment-statistics/
//   ?all_treatments=true&count=distinct_leads&practice__in=..&time_after=..&time_before=..
// → { [treatmentItemId]: { [year]: { [month]: { [STATUS]: { actual } } } } }
// Kept in ONE place so a contract change only touches these two functions.
export function buildTreatmentStatisticsRequest(practiceIds, dateRange) {
    const [primary] = practiceIds;
    // group=total → distinct leads over the WHOLE range ({item:{STATUS:{actual}}}), so a lead
    // that moves in two months is counted once (summing months double-counted).
    const params = ['all_treatments=true', 'count=distinct_leads', 'group=total'];
    if (practiceIds.length > 1) params.push(`practice__in=${practiceIds.join(',')}`);
    if (dateRange?.start) params.push(`time_after=${dateRange.start.split(' ')[0]}`);
    if (dateRange?.end) {
        // time_before is exclusive — add a day so the end date is inclusive.
        params.push(`time_before=${dayjs(dateRange.end.split(' ')[0]).add(1, 'day').format('YYYY-MM-DD')}`);
    }
    return `/api/v1/practices/${primary}/reports/lead-treatment-statistics/?${params.join('&')}`;
}

const STATUS_TOTAL_KEYS = ['NEW', 'CONSULTATION_BOOKED', 'CONSULTATION_ATTENDED', 'TREATMENT_STARTED', 'CLOSED'];

// One treatment item's range totals. Preferred shape (group=total): {STATUS:{actual}} — taken
// as-is (distinct over the range). Legacy shape {year:{month:{STATUS:{actual}}}} (BE without
// group=total) falls back to summing months, which can over-count leads that move twice.
function totalsFromItemData(itemData) {
    const totals = { NEW: 0, CONSULTATION_BOOKED: 0, CONSULTATION_ATTENDED: 0, TREATMENT_STARTED: 0, CLOSED: 0 };
    const isFlat = Object.keys(itemData).some((k) => STATUS_TOTAL_KEYS.includes(k) || (itemData[k] && typeof itemData[k] === 'object' && 'actual' in itemData[k]));
    if (isFlat) {
        STATUS_TOTAL_KEYS.forEach((status) => { totals[status] = Number(itemData[status]?.actual) || 0; });
        return totals;
    }
    Object.values(itemData).forEach((yearData) => {
        if (!yearData || typeof yearData !== 'object') return;
        Object.values(yearData).forEach((monthData) => {
            if (!monthData || typeof monthData !== 'object') return;
            Object.entries(monthData).forEach(([status, statusData]) => {
                if (Object.prototype.hasOwnProperty.call(totals, status) && statusData?.actual) {
                    totals[status] += statusData.actual;
                }
            });
        });
    });
    return totals;
}

export function parseTreatmentStatistics(byItem, treatments) {
    const rows = [];
    (treatments || []).forEach((treatment) => {
        const treatmentItemId = treatment.treatment_item?.id;
        const itemData = treatmentItemId != null ? byItem?.[treatmentItemId] : null;
        if (!itemData || typeof itemData !== 'object') return;
        const totals = totalsFromItemData(itemData);
        rows.push({
            id: treatment.id,
            treatmentId: treatment.id,
            treatmentItemId,
            treatmentName: treatment.treatment_item?.name || 'Unknown',
            color: treatment.colour || '#6366f1',
            ...totals,
            total: totals.NEW,
        });
    });
    return rows;
}

// Stale-response guard for the By Treatment thunks. Each dispatch takes a token; a
// response is applied ONLY if its token is still the latest for that thunk, so an
// older in-flight request (A) resolving after a newer one (B) can never overwrite B.
// A dispatch whose key equals the latest requested key is collapsed (the page effects
// re-fire on unrelated deps); a different key — including a return to an earlier key
// while a newer request is still in flight — always issues a fresh request.
const dispatchState = {};
function beginDispatch(name, key, { dedupe = true } = {}) {
    const st = dispatchState[name] || (dispatchState[name] = { token: 0, key: null });
    if (dedupe && st.key === key) return null;
    st.token += 1;
    st.key = key;
    return st.token;
}
function isLatestDispatch(name, token) {
    return dispatchState[name]?.token === token;
}
function failDispatch(name, token) {
    // Release the key so a retry isn't deduped away — but only if still the latest.
    const st = dispatchState[name];
    if (st && st.token === token) st.key = null;
}
function clearDispatchKey(name) {
    const st = dispatchState[name];
    if (st) { st.token += 1; st.key = null; }
}

// Fetch treatment status summary for all treatments
export function resetTreatmentReportDedupe() {
    // Bumps the tokens too, so anything still in flight is discarded on unmount.
    clearDispatchKey('treatmentFunnel');
    clearDispatchKey('treatmentData');
    clearDispatchKey('treatmentStatus');
}

// Activity-mode "Enquiries" column = DISTINCT leads CREATED in the range per
// treatment item (the same source as the By Treatment chips), not the count of
// NEW state-log transitions (imports/integrations often have no NEW log, so that
// undercounted: 32 vs 152). One grouped call, summed per treatment item across
// every selected practice's PracticeTreatment.
export const buildEnquiriesByTreatmentUrl = (practiceIds, dateRange) =>
    `/api/v1/practices/${practiceIds[0]}/reports/leads/?aggregate[Count]=id&group_by[lead_treatments__treatment]&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}${practiceInSuffix(practiceIds)}`;

export const applyEnquiryCounts = (rows, enquiryRows, treatments) => {
    const itemOfTreatment = {};
    (treatments || []).forEach((t) => { itemOfTreatment[t.id] = t.treatment_item?.id; });
    const perItem = {};
    (enquiryRows || []).forEach((r) => {
        const item = itemOfTreatment[r.lead_treatments__treatment];
        if (item == null) return;
        perItem[item] = (perItem[item] || 0) + (r.count_id || 0);
    });
    return rows.map((r) => {
        const enquiries = perItem[r.treatmentItemId] || 0;
        return { ...r, NEW: enquiries, total: enquiries };
    });
};

export function fetchTreatmentStatusSummary(practiceIdOrIds, treatments, dateRange) {
    // Activity mode: ONE bulk call to /reports/lead-treatment-statistics/
    // (all_treatments=true&count=distinct_leads&practice__in) — see
    // buildTreatmentStatisticsRequest. Groups by TreatmentItem ID (not PracticeTreatment ID)
    // to avoid split/dropped rows.
    const ids = toPracticeIds(practiceIdOrIds);
    const practiceId = ids[0];
    return async (dispatch) => {
        if (!practiceId) return;
        if (!hasFeatureAccess('analytics-reports')) return;
        const token = beginDispatch('treatmentStatus', 'status', { dedupe: false });
        dispatch(setTreatmentStatusSummaryLoading(true));

        try {
            // ONE call for every treatment (BE contract: all_treatments=true,
            // count=distinct_leads, group=total, practice__in). Replaces ~1 request per treatment.
            const request = buildTreatmentStatisticsRequest(ids, dateRange);
            const [response, enquiryRes] = await Promise.all([
                callApi(request),
                callApi(buildEnquiriesByTreatmentUrl(ids, dateRange)).catch(() => null),
            ]);
            if (!isLatestDispatch('treatmentStatus', token)) return;
            const byItem = response?.status === 200 && response.data ? response.data : {};
            const enquiryRows = enquiryRes?.data?.data;
            const seenItems = new Set();
            const parsed = parseTreatmentStatistics(byItem, treatments);
            // If the enquiry query failed keep the BE value rather than showing 0s.
            const results = (Array.isArray(enquiryRows) ? applyEnquiryCounts(parsed, enquiryRows, treatments) : parsed).filter((r) => {
                if (seenItems.has(r.treatmentItemId)) return false;
                seenItems.add(r.treatmentItemId);
                return true;
            });
            // Activity mode counts leads moving INTO each status during the period,
            // so a treatment with 0 new enquiries can still legitimately have non-
            // zero bookings/attendances from leads that enquired earlier — keep
            // those rows.
            const validResults = results.filter((r) => (r.NEW || 0) + (r.CONSULTATION_BOOKED || 0)
                + (r.CONSULTATION_ATTENDED || 0) + (r.TREATMENT_STARTED || 0) + (r.CLOSED || 0) > 0);

            dispatch(setTreatmentStatusSummary({
                data: validResults,
                isLoading: false,
            }));
        } catch (error) {
            if (!isLatestDispatch('treatmentStatus', token)) return;
            dispatch(setTreatmentStatusSummary({
                data: [],
                isLoading: false,
            }));
        }
    };
}

// Cohort mode: runs a separate API call per status using state_transition_params +
// created_at filtering — same approach as CategorySummaryTable's "Current month" toggle,
// but scoped to the user's selected date range instead of hardcoded current month.
export function fetchTreatmentStatusSummaryCohort(practiceId, treatments, dateRange) {
    return async (dispatch) => {
        if (!hasFeatureAccess('analytics-reports')) return;
        dispatch(setTreatmentStatusSummaryLoading(true));

        try {
            const states = ['NEW', 'CONSULTATION_BOOKED', 'CONSULTATION_ATTENDED', 'TREATMENT_STARTED', 'CLOSED'];
            const baseUrl = `/api/v1/practices/${practiceId}/reports/leads/`;
            const groupBy = 'group_by[lead_treatments__treatment]&group_by[lead_treatments__state_transition_logs__state]';
            const agg = 'aggregate[Count]=id&aggregate[Sum]=lead_treatments__custom_price';

            const statePromises = states.map(async (state) => {
                const createdFilter = `&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}`;

                let url;
                if (state === 'NEW') {
                    // Every lead starts as NEW on creation — the created_at filter alone
                    // is sufficient. Using state_transition_params for NEW misses leads
                    // that lack a proper NEW transition log entry.
                    const newGroupBy = 'group_by[lead_treatments__treatment]';
                    url = `${baseUrl}?${newGroupBy}&${agg}${createdFilter}`;
                } else {
                    const stateParams = encodeURIComponent(JSON.stringify({
                        state,
                        timestamp_gte: dateRange.start,
                        timestamp_lte: dateRange.end,
                    }));
                    url = `${baseUrl}?${groupBy}&${agg}&state_transition_params=${stateParams}${createdFilter}`;
                }

                try {
                    const response = await callApi(url);
                    const data = response?.data?.data || response?.data || [];
                    const items = Array.isArray(data) ? data : [];
                    if (state === 'NEW') {
                        // No state_transition_logs grouping — inject the state for merging
                        return items.map((item) => ({
                            ...item,
                            lead_treatments__state_transition_logs__state: 'NEW',
                        }));
                    }
                    return items.filter((item) => item.lead_treatments__state_transition_logs__state === state);
                } catch {
                    return [];
                }
            });

            const stateResults = await Promise.all(statePromises);
            const mergedData = stateResults.flat();

            const grouped = {};
            mergedData.forEach((item) => {
                const treatmentId = item.lead_treatments__treatment;
                if (!grouped[treatmentId]) grouped[treatmentId] = [];
                grouped[treatmentId].push(item);
            });

            const treatmentMap = {};
            treatments.forEach((t) => { treatmentMap[t.id] = t; });

            const validResults = Object.entries(grouped).map(([treatmentIdStr, stateItems]) => {
                const treatmentId = Number(treatmentIdStr);
                const treatment = treatmentMap[treatmentId];
                if (!treatment) return null;

                const getCount = (st) => stateItems.find(
                    (item) => item.lead_treatments__state_transition_logs__state === st
                )?.count_id || 0;

                const counts = {
                    NEW: getCount('NEW'),
                    CONSULTATION_BOOKED: getCount('CONSULTATION_BOOKED'),
                    CONSULTATION_ATTENDED: getCount('CONSULTATION_ATTENDED'),
                    TREATMENT_STARTED: getCount('TREATMENT_STARTED'),
                    CLOSED: getCount('CLOSED'),
                };

                const total = counts.NEW;

                return {
                    id: treatment.id,
                    treatmentId: treatment.id,
                    treatmentItemId: treatment.treatment_item?.id,
                    treatmentName: treatment.treatment_item?.name || 'Unknown',
                    color: treatment.colour || '#6366f1',
                    ...counts,
                    total,
                };
            }).filter((r) => r !== null && r.total > 0);

            dispatch(setTreatmentStatusSummary({
                data: validResults,
                isLoading: false,
            }));
        } catch (error) {
            dispatch(setTreatmentStatusSummary({
                data: [],
                isLoading: false,
            }));
        }
    };
}

// Pure aggregator — sums a processed practitioner-data array (as returned by
// fetchOnePracticeClinicianData) into team-wide totals + derived rates.
// Shared by both the single-practice path and the multi-practice union path
// so the rate-calculation formulas can never drift between the two.
const computeClinicianTeamTotals = (processedData) => {
    const teamTotals = {
        leads: 0,
        consultationsBooked: 0,
        consultationsAttended: 0,
        treatmentStarted: 0,
        revenue: 0,
    };
    processedData.forEach((p) => {
        teamTotals.leads += p.totals.leads;
        teamTotals.consultationsBooked += p.totals.consultationsBooked;
        teamTotals.consultationsAttended += p.totals.consultationsAttended;
        teamTotals.treatmentStarted += p.totals.treatmentStarted;
        teamTotals.revenue += p.totals.revenue;
    });
    teamTotals.showUpRate = teamTotals.consultationsBooked > 0
        ? ((teamTotals.consultationsAttended / teamTotals.consultationsBooked) * 100).toFixed(1)
        : '0';
    teamTotals.conversionRate = teamTotals.consultationsAttended > 0
        ? ((teamTotals.treatmentStarted / teamTotals.consultationsAttended) * 100).toFixed(1)
        : '0';
    return teamTotals;
};

// Fetches + processes clinician conversion data for EXACTLY ONE practice.
// Extracted verbatim from the pre-multi-practice implementation of
// fetchClinicianConversionData so the single-practice call site below can
// remain byte-behaviorally identical to before, while the multi-practice
// union path (fetchClinicianConversionData, isAllPractices && ids.length > 1)
// can fan this same logic out per selected practice and merge the results.
// Attribution maps (puIdToIdx, userIdToPuId, getPracIndices) are rebuilt
// fresh on every call — i.e. per practice — so a treatment fetched for
// practice A can only ever be credited to practice A's own practitioners.
//
// Returns one of:
//   { status: 'superseded' }                              — isCurrent() went false mid-flight
//   { status: 'no_access' }                                — 401/403 on /practice-user/
//   { status: 'no_data', notOk?: true, httpStatus? }        — 0 practitioners (incl. when
//                                                              /practice-user/ resolved !ok —
//                                                              `notOk`/`httpStatus` let the
//                                                              caller distinguish a genuine
//                                                              empty practice from a fetch
//                                                              failure for allPractices-slot
//                                                              error surfacing, matching the
//                                                              previous inline behaviour)
//   { status: 'ok', data, treatments }                      — processed practitioner rows
// Throws (does not catch) on a hard failure from the paged flat-endpoint
// stages (5xx / network / zero-progress) or a superseded response mid-page —
// callers are responsible for try/catching this, exactly as the previous
// inline implementation relied on the thunk's own try/catch.
// Clinician-conversion stage pages are among the heaviest requests in the app
// (500 rows each with transition logs + appointments; 1-4 minutes server-side
// for large practices). Incident 2026-10-06: the All Practices Overview fanned
// these out for every practice x 4 stages at once, and each page that hit
// callApi's 10s default timeout was retried twice (ECONNABORTED is retriable)
// while the server kept computing the abandoned original — exhausting the
// backend's request workers and freezing the CRM for every practice for ~10
// minutes. So: wait long enough for the real answer, never auto-retry, and cap
// how many of these run at once (see CLINICIAN_* below).
export const CLINICIAN_STAGE_REQUEST_OPTIONS = { timeout: 180000, maxRetries: 0 };
// Stages fetched concurrently within one practice (4 stages total).
export const CLINICIAN_STAGE_CONCURRENCY = 2;
// Practices fetched concurrently in the multi-practice (All Practices) union.
export const CLINICIAN_PRACTICE_CONCURRENCY = 1;

// Run ``worker`` over ``items`` with at most ``limit`` in flight, preserving
// input order in the result. A rejection propagates (like Promise.all) once
// the in-flight workers settle; workers check their own abort flags.
export async function mapWithConcurrency(items, limit, worker) {
    const results = new Array(items.length);
    let next = 0;
    const runners = Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, async () => {
        while (next < items.length) {
            const index = next;
            next += 1;
            // eslint-disable-next-line no-await-in-loop
            results[index] = await worker(items[index], index);
        }
    });
    await Promise.all(runners);
    return results;
}

// Server-side tracker (dentalcrm reports/clinician-conversion). Returns the same
// { status, data, treatments } this module used to build in the browser from
// paged /lead-treatments-flat/ stages — one light request per practice instead
// of 4+ heavy ones (the fan-out that froze production on 2026-10-06).
// Resolves to null ONLY when the endpoint isn't deployed yet (404 on the route),
// so the browser path below stays as a fallback during rollout; any other
// failure throws/returns a status rather than falling back to the heavy path.
export const SERVER_CLINICIAN_REQUEST_OPTIONS = { timeout: 120000, maxRetries: 0 };

async function fetchServerClinicianData(practiceId, dateRange, isCurrent) {
    const params = new URLSearchParams({ start: dateRange.start, end: dateRange.end });
    const res = await callApi(
        `/api/v1/practices/${practiceId}/reports/clinician-conversion/?${params.toString()}`,
        'GET',
        null,
        false,
        SERVER_CLINICIAN_REQUEST_OPTIONS,
    );
    if (!isCurrent()) return { status: 'superseded' };
    if (res?.status === 404) return null;
    if (res?.status === 401 || res?.status === 403) return { status: 'no_access' };
    if (res?.ok === false || !res?.data) {
        throw new Error(`Failed to load clinician conversion data (status ${res?.status ?? 0}).`);
    }
    if (res.data.status === 'no_data') return { status: 'no_data' };
    // Popup details carry this caller's practiceId (same value/type the browser
    // path used), not the server's integer.
    const tagDetails = (metrics) => ({
        ...metrics,
        consultationsBookedDetails: (metrics.consultationsBookedDetails || []).map((d) => ({ ...d, practiceId })),
        consultationsAttendedDetails: (metrics.consultationsAttendedDetails || []).map((d) => ({ ...d, practiceId })),
        treatmentStartedDetails: (metrics.treatmentStartedDetails || []).map((d) => ({ ...d, practiceId })),
    });
    const data = (res.data.data || []).map((row) => ({
        ...row,
        totals: tagDetails(row.totals || {}),
        treatments: Object.fromEntries(
            Object.entries(row.treatments || {}).map(([name, metrics]) => [name, tagDetails(metrics)])
        ),
    }));
    return { status: 'ok', data, treatments: res.data.treatments || [] };
}

async function fetchOnePracticeClinicianData(practiceId, dateRange, isCurrent) {
    const server = await fetchServerClinicianData(practiceId, dateRange, isCurrent);
    if (server) return server;
    if (!isCurrent()) return { status: 'superseded' };

    const baseUrl = `/api/v1/practices/${practiceId}/reports`;

    // First fetch all practitioners (per-practice scope — the caller is
    // responsible for fanning this out across multiple practices when a
    // union is needed; this function never queries more than one practice).
    const practitionersRes = await callApi(`${baseUrl}/practice-user/`);
    if (!isCurrent()) return { status: 'superseded' };

    // Handle permission errors gracefully — user may not have access to this practice.
    if (practitionersRes?.status === 403 || practitionersRes?.status === 401) {
        return { status: 'no_access' };
    }

    const practitionersRaw = practitionersRes?.data;
    const allPractitioners = Array.isArray(practitionersRaw)
        ? practitionersRaw
        : (practitionersRaw?.results || []);

    // Dentists only (Practitioner as primary or additional role)
    const practitioners = allPractitioners.filter((p) => hasPractitionerRole(p));

    if (practitioners.length === 0) {
        // Any other resolved failure (5xx, network-level {ok:false,status:0}) on the
        // practice-user fetch also lands here (practitionersRaw is undefined in that
        // case) — `notOk`/`httpStatus` let the caller surface it distinctly for the
        // All Practices slot while the default slot's silent-empty behaviour is
        // unaffected (it never inspects these fields).
        return practitionersRes?.ok === false
            ? { status: 'no_data', notOk: true, httpStatus: practitionersRes?.status ?? 0 }
            : { status: 'no_data' };
    }

    // Use the flat endpoint with state_transition_params for correct date filtering
    // This filters by WHEN a lead treatment actually transitioned to each status
    const practitionersToFetch = practitioners;
    const flatBaseUrl = `/api/v1/practices/${practiceId}/lead-treatments-flat`;
    // appointments{assignees,status,datetime}: status drops FTA/cancelled
    // consults from the funnel; datetime lets us attribute a treatment to
    // its LATEST appointment's clinician (not every dentist the lead saw).
    const flatFields = 'fields={id,assigned_to,assignees,lead{id,first_name,last_name,created_at},treatment{treatment_item{name}},status,price,custom_price,contacted_at,lead_contacted_counts{count,status},leadtreatment_specification,state_transition_logs,appointments{assignees,status,datetime}}';

    // Fetch leads that transitioned to each status within the date range.
    // IMPORTANT: We do NOT filter by current status — a lead attended in Feb
    // that has since moved to TREATMENT_STARTED should still count as "attended"
    // for the practitioner who saw them. state_transition_params alone handles
    // the date filtering.
    const statusQueries = [
        { state: 'CONSULTATION_BOOKED' },
        { state: 'CONSULTATION_ATTENDED' },
        { state: 'TREATMENT_STARTED' },
        { state: 'CLOSED' },
    ];

    // Shared paged-stage helper — replaces the old hard-coded limit=1000 single
    // request per stage (which silently truncated practices with >1000 matching
    // treatments in range). Paginates sequentially within a stage (follow
    // count/offset until every row is collected), while the 4 stage chains run
    // in parallel (max 4 concurrent — one per state). The deployed backend clamps
    // this endpoint's page size to 500 (DRF ExtendedLimitOffsetPagination
    // max_limit) regardless of the requested `limit`, so PAGE_SIZE=500 is
    // requested to keep offsets deterministic, and termination/advancement are
    // driven by the actual response — never by the requested page size — so a
    // clamped response can never be mistaken for "last page". ALL-OR-NOTHING: any
    // page failure (`!ok`), or a page that returns zero rows before we've reached
    // the reported total (a "zero-progress" / inconsistent page), throws — the
    // caller turns that into a slot error / per-practice skip with NO partial
    // data/key commit.
    const PAGE_SIZE = 500;
    // Shared abort flag — when any sibling stage throws (page failure,
    // zero-progress, superseded), every other sibling's while-loop checks this
    // at the top of its next iteration and bails out immediately instead of
    // continuing to paginate toward a UI that has already failed. Bounded: at
    // most one more in-flight callApi per sibling after the failure. Scoped to
    // THIS practice's 4 stages only — a failure fetching practice A's stages
    // never aborts practice B's in-flight stages when fanned out by the caller.
    let siblingFailed = false;
    const fetchPagedStage = async (state) => {
        const stateParams = encodeURIComponent(JSON.stringify({
            state,
            timestamp_gte: dateRange.start,
            timestamp_lte: dateRange.end,
        }));
        const buildUrl = (offset) =>
            `${flatBaseUrl}/?state_transition_params=${stateParams}&${flatFields}&ordering=-created_at&limit=${PAGE_SIZE}&offset=${offset}`;
        let offset = 0;
        let total = null;
        const all = [];
        // eslint-disable-next-line no-constant-condition
        while (true) {
            if (siblingFailed) throw new Error('__sibling_aborted__');
            // eslint-disable-next-line no-await-in-loop
            const res = await callApi(buildUrl(offset), 'GET', null, false, CLINICIAN_STAGE_REQUEST_OPTIONS);
            if (!isCurrent()) throw new Error('__superseded__');
            if (res?.ok === false) {
                throw new Error(`Failed to load ${state} leads (offset ${offset}, status ${res?.status ?? 0}).`);
            }
            const raw = res?.data;
            const rows = Array.isArray(raw) ? raw : (raw?.results || []);
            const pageTotal = Array.isArray(raw) ? rows.length : (raw?.count ?? rows.length);
            if (offset === 0) total = pageTotal;
            if (rows.length === 0 && all.length < (total || 0)) {
                throw new Error(`Zero-progress page for ${state} leads at offset ${offset} (expected ${total}, got ${all.length}).`);
            }
            all.push(...rows);
            // Terminate ONLY when we've collected the reported total, or the page
            // came back empty (array-shaped response / no more rows). Never on
            // rows.length < PAGE_SIZE — a server-side clamp (e.g. 500-row cap) can
            // return a short page that is NOT the last page.
            if (all.length >= (total ?? all.length) || rows.length === 0) break;
            // Advance by the actual returned page length, not the requested
            // PAGE_SIZE — a clamped response returns fewer rows than requested.
            offset += rows.length;
        }
        return all;
    };

    const [bookedItems, attendedItems, startedItems, closedItems] = await mapWithConcurrency(
        statusQueries,
        CLINICIAN_STAGE_CONCURRENCY,
        async ({ state }) => {
            try {
                return await fetchPagedStage(state);
            } catch (e) {
                siblingFailed = true;
                throw e;
            }
        },
    );
    if (!isCurrent()) return { status: 'superseded' };

    // Build practice_user ID -> practitioner index map for attribution
    const puIdToIdx = {};
    practitionersToFetch.forEach((p, idx) => { puIdToIdx[p.id] = idx; });
    // Also map by user ID for appointment assignees
    const userIdToPuId = {};
    practitionersToFetch.forEach((p) => {
        if (p.user?.id) userIdToPuId[p.user.id] = p.id;
    });

    // Helper: get practitioner indices from a lead treatment record
    const getPracIndices = (lt) => {
        const indices = new Set();
        // Check assigned_to (may be integer practice_user_id or object)
        if (lt.assigned_to) {
            const puId = typeof lt.assigned_to === 'object' ? lt.assigned_to.id : lt.assigned_to;
            if (puIdToIdx[puId] !== undefined) indices.add(puIdToIdx[puId]);
        }
        // Check assignees array
        if (lt.assignees?.length > 0) {
            lt.assignees.forEach((a) => {
                const puId = typeof a === 'object' ? a.id : a;
                if (puIdToIdx[puId] !== undefined) indices.add(puIdToIdx[puId]);
            });
        }
        // Check appointment assignees — "latest appointment wins".
        // A lead may have seen one dentist first without proceeding, then a
        // later appointment with a different dentist who actually took the
        // treatment forward. Credit only the MOST-RECENT (by datetime)
        // appointment's clinician, not every dentist the lead ever saw (which
        // double-counted a single treatment across two practitioners).
        // NOTE: the flat endpoint does not expose appointment type, so "latest"
        // is across all appointments (consultation or treatment) — i.e. the
        // most recent clinician the lead saw for this treatment. Cancelled / FTA
        // appointments are ignored when choosing the latest, since the patient
        // didn't actually attend then; fall back to the latest overall only if
        // every appointment is cancelled/FTA.
        if (lt.appointments?.length > 0) {
            const valid = lt.appointments.filter((a) => !DEAD_APPT_STATUSES.has(a.status));
            const pool = valid.length > 0 ? valid : lt.appointments;
            // Pick the latest DATED appointment. Undated appointments never win
            // (they'd otherwise grab attribution by array order); `>=` makes a
            // same-datetime tie resolve to the later array entry deterministically.
            let latestAppt = null;
            let latestTs = null;
            pool.forEach((a) => {
                const ts = parseTimestamp(a.datetime);
                if (!ts) return;
                if (!latestTs || ts >= latestTs) {
                    latestAppt = a;
                    latestTs = ts;
                }
            });
            // If no appointment had a parseable datetime, fall back to the last
            // one so attribution still resolves.
            if (!latestAppt) latestAppt = pool[pool.length - 1];
            if (latestAppt?.assignees?.length > 0) {
                latestAppt.assignees.forEach((a) => {
                    const puId = typeof a === 'object' ? a.id : a;
                    if (puIdToIdx[puId] !== undefined) indices.add(puIdToIdx[puId]);
                    // Also try matching via user ID
                    const userId = typeof a === 'object' ? a.user?.id || a.user : null;
                    if (userId && userIdToPuId[userId] !== undefined) {
                        indices.add(puIdToIdx[userIdToPuId[userId]]);
                    }
                });
            }
        }
        return indices;
    };

    // Helper: extract treatment name from flat endpoint format
    const getTreatmentName = (lt) => {
        if (typeof lt.treatment === 'object') {
            return lt.treatment?.treatment_item?.name || lt.treatment?.name || 'Other';
        }
        return lt.treatment || 'Other';
    };

    // A consultation that was booked but then failed-to-attend or cancelled
    // (and never went on to attend or start treatment) must NOT count in the
    // funnel — the report should only show booked consultations that were
    // attended or are still upcoming. Only applies when the treatment's
    // highest reached stage is CONSULTATION_BOOKED; anything that attended/
    // started stays (it eventually showed up). A still-live BOOKED/ATTENDED
    // appointment keeps it in (an upcoming or rebooked consult), so we only
    // drop it when every appointment is cancelled / failed-to-attend.
    const DEAD_APPT_STATUSES = new Set(['CANCELLED', 'FAILED_TO_ATTEND']);
    const isFailedOrCancelledConsult = (lt, highestState) => {
        if (highestState !== 'CONSULTATION_BOOKED') return false;
        const appts = lt.appointments || [];
        if (appts.length === 0) return false;
        const hasLive = appts.some((a) => a.status === 'BOOKED' || a.status === 'ATTENDED');
        const hasDead = appts.some((a) => DEAD_APPT_STATUSES.has(a.status));
        return hasDead && !hasLive;
    };

    // Parse DD-MM-YYYY HH:mm:ss or ISO format to Date object
    const parseTimestamp = (str) => {
        if (!str) return null;
        const parts = str.match(/^(\d{2})-(\d{2})-(\d{4})\s+(\d{2}):(\d{2}):(\d{2})/);
        if (parts) return new Date(parts[3], parts[2] - 1, parts[1], parts[4], parts[5], parts[6]);
        const d = new Date(str);
        return Number.isNaN(d.getTime()) ? null : d;
    };

    // Helper: get the transition timestamp for a specific state from state_transition_logs
    const getTransitionTimestamp = (lt, targetState) => {
        if (!lt.state_transition_logs?.length) return '';
        // Find the most recent transition to the target state
        const transition = lt.state_transition_logs
            .filter((log) => log.state === targetState)
            .sort((a, b) => (parseTimestamp(b.timestamp) || 0) - (parseTimestamp(a.timestamp) || 0))[0];
        return transition?.timestamp || '';
    };

    // Helper: build lead detail for popup
    const getLeadDetail = (lt, queryState) => {
        const leadObj = typeof lt.lead === 'object' ? lt.lead : {};
        // Use the transition timestamp for the queried state (e.g., when it became TREATMENT_STARTED)
        // Falls back to lead created_at if no transition log available
        const transitionDate = getTransitionTimestamp(lt, queryState);
        return {
            practiceId,
            leadId: leadObj.id || lt.lead,
            leadName: `${leadObj.first_name || ''} ${leadObj.last_name || ''}`.trim() || 'Unknown',
            treatment: getTreatmentName(lt),
            status: lt.status,
            price: parseFloat(lt.custom_price || lt.price || 0),
            teethCount: lt.leadtreatment_specification?.teeth_count || 0,
            timesContacted: getReportLeadContactCount(lt),
            createdAt: transitionDate || leadObj.created_at || lt.contacted_at || '',
        };
    };

    const initMetrics = () => ({
        leads: 0,
        consultationsBooked: 0, consultationsBookedDetails: [],
        consultationsAttended: 0, consultationsAttendedDetails: [],
        treatmentStarted: 0, treatmentStartedDetails: [],
        revenue: 0, teethCount: 0,
    });

    // Initialize practitioner data
    const practitionerData = practitionersToFetch.map((p) => ({
        id: p.id,
        userId: p.user?.id,
        name: `${p.user?.first_name || ''} ${p.user?.last_name || ''}`.trim() || 'Unknown',
        avatar: p.user?.avatar,
        email: p.user?.email,
        treatments: {},
        totals: initMetrics(),
        _seenLeadIds: new Set(), // Track unique leads across status queries
        _seenTreatmentLeadIds: {}, // Track unique leads per treatment
    }));

    // "Unassigned" catch-all for leads not attributed to any Practitioner
    const unassignedData = {
        id: 'unassigned',
        userId: null,
        name: 'Unassigned',
        avatar: null,
        email: null,
        treatments: {},
        totals: initMetrics(),
        _seenLeadIds: new Set(),
        _seenTreatmentLeadIds: {},
        _isUnassigned: true,
    };

    const treatmentNames = new Set();

    // Each funnel metric maps to the lead-treatment state whose transition
    // date that metric's popup should show. A treatment that reached
    // TREATMENT_STARTED is counted in booked + attended + started, but each
    // column must show ITS OWN stage date — not the highest stage's date.
    // (Bug: a single detail dated at the highest stage was reused across all
    // three columns, so e.g. the Consult Booked popup showed the
    // treatment-started date instead of the booked date.)
    const METRIC_STATE = {
        consultationsBooked: 'CONSULTATION_BOOKED',
        consultationsAttended: 'CONSULTATION_ATTENDED',
        treatmentStarted: 'TREATMENT_STARTED',
    };

    // Helper to add a lead treatment to a practitioner's metrics.
    // Builds a per-stage detail (correct transition date) for each metric.
    const addToMetrics = (pracData, tName, lt, metricKeys) => {
        if (!pracData.treatments[tName]) pracData.treatments[tName] = initMetrics();
        const td = pracData.treatments[tName];
        // leadId/price/teeth are stage-independent — derive once.
        const baseDetail = getLeadDetail(lt, null);
        const detailForStage = (mk) => getLeadDetail(lt, METRIC_STATE[mk]);
        metricKeys.forEach((mk) => {
            if (mk === 'consultationsBooked') {
                const detail = detailForStage(mk);
                td.consultationsBooked += 1;
                td.consultationsBookedDetails.push(detail);
                pracData.totals.consultationsBooked += 1;
                pracData.totals.consultationsBookedDetails.push(detail);
            } else if (mk === 'consultationsAttended') {
                const detail = detailForStage(mk);
                td.consultationsAttended += 1;
                td.consultationsAttendedDetails.push(detail);
                pracData.totals.consultationsAttended += 1;
                pracData.totals.consultationsAttendedDetails.push(detail);
            } else if (mk === 'treatmentStarted') {
                const detail = detailForStage(mk);
                const price = detail.price || 0;
                const teethCount = detail.teethCount || 0;
                td.treatmentStarted += 1;
                td.treatmentStartedDetails.push(detail);
                td.revenue += price;
                pracData.totals.treatmentStarted += 1;
                pracData.totals.treatmentStartedDetails.push(detail);
                pracData.totals.revenue += price;
                if (teethCount > 0) {
                    td.teethCount += teethCount;
                    pracData.totals.teethCount += teethCount;
                }
            }
        });
        // Deduplicate lead counts — a lead appearing in multiple status queries
        // (e.g., booked + attended + started all in this date range) should only count once
        const leadKey = `${baseDetail.leadId}-${tName}`;
        if (!pracData._seenTreatmentLeadIds[tName]) pracData._seenTreatmentLeadIds[tName] = new Set();
        if (!pracData._seenTreatmentLeadIds[tName].has(leadKey)) {
            pracData._seenTreatmentLeadIds[tName].add(leadKey);
            td.leads += 1;
        }
        if (!pracData._seenLeadIds.has(baseDetail.leadId)) {
            pracData._seenLeadIds.add(baseDetail.leadId);
            pracData.totals.leads += 1;
        }
    };

    // Client-side date validation: verify transition actually happened within date range
    // This catches edge cases where the API may return stale results
    const rangeStart = new Date(dateRange.start);
    const rangeEnd = new Date(dateRange.end);
    const isTransitionInRange = (lt, targetState) => {
        const ts = getTransitionTimestamp(lt, targetState);
        if (!ts) return true; // If no logs, trust the API filter
        const transDate = parseTimestamp(ts);
        if (!transDate) return true; // If unparseable, trust the API filter
        return transDate >= rangeStart && transDate <= rangeEnd;
    };

    // Deduplicate across responses: since we no longer filter by current status,
    // a lead that went booked→attended→started in one period appears in multiple
    // response arrays. We track each lead-treatment by ID and keep only its
    // highest funnel stage to avoid double-counting.
    //
    // Funnel priority (highest wins):
    //   4 = CLOSED      → counts: booked + attended
    //   3 = STARTED     → counts: booked + attended + started
    //   2 = ATTENDED    → counts: booked + attended
    //   1 = BOOKED      → counts: booked
    const FUNNEL_PRIORITY = { CONSULTATION_BOOKED: 1, CONSULTATION_ATTENDED: 2, TREATMENT_STARTED: 3, CLOSED: 4 };
    const FUNNEL_METRICS = {
        CONSULTATION_BOOKED: ['consultationsBooked'],
        CONSULTATION_ATTENDED: ['consultationsBooked', 'consultationsAttended'],
        TREATMENT_STARTED: ['consultationsBooked', 'consultationsAttended', 'treatmentStarted'],
        CLOSED: ['consultationsBooked', 'consultationsAttended'],
    };

    // Build dedupe map: ltId -> { lt, highestState, priority }.
    // Per-stage details are derived later in addToMetrics (each funnel column
    // needs its own stage date), so we don't precompute a detail here.
    const dedupeMap = new Map();
    const processItems = (items, queryState) => {
        items.filter((lt) => isTransitionInRange(lt, queryState)).forEach((lt) => {
            const ltId = lt.id;
            const existing = dedupeMap.get(ltId);
            const priority = FUNNEL_PRIORITY[queryState] || 0;
            if (!existing || priority > existing.priority) {
                dedupeMap.set(ltId, {
                    lt,
                    highestState: queryState,
                    priority,
                });
            }
        });
    };

    processItems(bookedItems, 'CONSULTATION_BOOKED');
    processItems(attendedItems, 'CONSULTATION_ATTENDED');
    processItems(startedItems, 'TREATMENT_STARTED');
    processItems(closedItems, 'CLOSED');

    // Now process each unique lead-treatment once at its highest funnel stage
    dedupeMap.forEach(({ lt, highestState }) => {
        // Drop booked consultations that ended FTA / cancelled (never attended).
        if (isFailedOrCancelledConsult(lt, highestState)) return;
        const indices = getPracIndices(lt);
        const tName = getTreatmentName(lt);
        const metricKeys = FUNNEL_METRICS[highestState] || ['consultationsBooked'];
        treatmentNames.add(tName);
        if (indices.size === 0) {
            addToMetrics(unassignedData, tName, lt, metricKeys);
        } else {
            indices.forEach((idx) => addToMetrics(practitionerData[idx], tName, lt, metricKeys));
        }
    });

    // Build treatment list from collected names
    const treatmentList = Array.from(treatmentNames).map((tName) => ({
        id: tName,
        name: tName,
        color: '#6366f1',
    }));

    // If no treatments found, use "All Treatments" as default
    if (treatmentList.length === 0) {
        treatmentList.push({ id: 'all', name: 'All Treatments', color: '#6366f1' });
    }

    // Append "Unassigned" row if it captured any leads
    if (unassignedData._seenLeadIds.size > 0) {
        practitionerData.push(unassignedData);
    }

    // Calculate rates and format final data
    const processedData = practitionerData
        .map((p) => {
            const showUpRate = p.totals.consultationsBooked > 0
                ? ((p.totals.consultationsAttended / p.totals.consultationsBooked) * 100).toFixed(1)
                : '0';
            const conversionRate = p.totals.consultationsAttended > 0
                ? ((p.totals.treatmentStarted / p.totals.consultationsAttended) * 100).toFixed(1)
                : '0';

            // Calculate rates for each treatment
            const treatmentsWithRates = {};
            Object.entries(p.treatments).forEach(([tName, data]) => {
                const catShowUpRate = data.consultationsBooked > 0
                    ? ((data.consultationsAttended / data.consultationsBooked) * 100).toFixed(1)
                    : '0';
                const catConversionRate = data.consultationsAttended > 0
                    ? ((data.treatmentStarted / data.consultationsAttended) * 100).toFixed(1)
                    : '0';
                treatmentsWithRates[tName] = {
                    ...data,
                    showUpRate: catShowUpRate,
                    conversionRate: catConversionRate,
                };
            });

            // Strip temporary tracking properties before storing in Redux
            const { _seenLeadIds, _seenTreatmentLeadIds, ...cleanP } = p;
            return {
                ...cleanP,
                treatments: treatmentsWithRates,
                totals: {
                    ...p.totals,
                    showUpRate,
                    conversionRate,
                },
            };
        })
        .filter((p) => p.totals.leads > 0)
        .sort((a, b) => {
            // Keep "Unassigned" row at the bottom
            if (a._isUnassigned) return 1;
            if (b._isUnassigned) return -1;
            return b.totals.revenue - a.totals.revenue;
        });

    if (!isCurrent()) return { status: 'superseded' };
    return { status: 'ok', data: processedData, treatments: treatmentList };
}

// Fetch clinician conversion data for the Clinician Conversion Tracker
// Uses /api/v1/practices/{practice_pk}/reports/practice-user/{id}/leads/ endpoint
// Fetches lead treatments for each practitioner and aggregates by status
// Also stores lead IDs for each cell to enable click-to-view-details functionality
// Module-scoped per-slot request tokens — the stale-response guard. Every
// call to fetchClinicianConversionData increments the token for its slot and
// captures it; every dispatch after an `await` is gated on that token still
// being current. A slower earlier fetch (e.g. practice A) that resolves AFTER
// a newer fetch for the same slot (practice B, or a date change) has started
// is a "superseded" fetch — it dispatches NOTHING further, so it can never
// clobber the newer request's loading/data/error/key state. Two independent
// slots ('default' = By Practitioner, 'allPractices' = All Practices tracker)
// have independent counters so they never block each other.
const _clinicianFetchTokens = { default: 0, allPractices: 0 };

// practiceNames: optional { [practiceId]: displayName } map used ONLY by the
// multi-practice union path (isAllPractices && ids.length > 1) to tag each
// merged row with a human-readable practiceName. The caller (
// AllPracticesClinicianSection) already computes masked/display practice
// names for its own dropdown/labels — passing them in here keeps demo-mode
// masking a UI-layer concern instead of duplicating it in this slice. Falls
// back to the raw id (stringified) when no name is supplied for a given id.
export function fetchClinicianConversionData(practiceIdOrIds, dateRange, { slot, practiceNames } = {}) {
    const ids = toPracticeIds(practiceIdOrIds);
    const practiceId = ids[0];
    const isAllPractices = slot === 'allPractices';
    // Multi-practice union — All Practices slot ONLY, 2+ selected practices.
    // The By Practitioner ('default') slot keeps its pre-existing single-
    // practice-only behaviour untouched (see fetchOnePracticeClinicianData's
    // call site below), even though its caller already passes a multi-id
    // array — that mismatch is a pre-existing, separately-scoped condition
    // this task does not touch.
    const isUnion = isAllPractices && ids.length > 1;
    const requestKey = isUnion
        ? `${ids.join(',')}|${dateRange?.start}|${dateRange?.end}`
        : `${practiceId}|${dateRange?.start}|${dateRange?.end}`;
    return async (dispatch) => {
        if (!practiceId) return;
        // The legacy permission snapshot is scoped to the header's current
        // practice, so it cannot authorize this page's independent practice
        // dropdown. Let the existing endpoint enforce access for this slot;
        // its 401/403 path renders the section's explicit error state.
        if (!isAllPractices && !hasFeatureAccess('analytics-reports')) return { skipped: true };

        const tokenKey = isAllPractices ? 'allPractices' : 'default';
        _clinicianFetchTokens[tokenKey] += 1;
        const myToken = _clinicianFetchTokens[tokenKey];
        const isCurrent = () => _clinicianFetchTokens[tokenKey] === myToken;

        // Slot-scoped dispatch helpers — every call is token-gated so a
        // superseded fetch (an older request whose response arrives after a
        // newer one started) dispatches nothing at all.
        const dispatchEmptyOrData = (payload) => {
            if (!isCurrent()) return;
            if (isAllPractices) {
                dispatch(setAllPracticesClinicianConversion({ ...payload, error: null, errorRequestKey: null }));
            } else {
                dispatch(setClinicianConversion(payload));
            }
        };
        const dispatchSlotError = (message) => {
            if (!isCurrent()) return;
            if (isAllPractices) {
                dispatch(setAllPracticesClinicianConversion({
                    data: [], treatments: [], teamTotals: {}, isLoading: false,
                    error: message, errorRequestKey: requestKey,
                }));
                // Invalidate the committed fetch key ATOMICALLY with the data wipe.
                // A failure is the ONLY path that wipes slot data without committing a
                // matching key (success/empty always replace data AND commit their key
                // together). Without nulling the key here, a PRIOR success's key would
                // survive this failure and make a fallback selection look "fresh" over
                // the now-empty data → false "No Conversion Data" with no refetch. With
                // the key nulled, fetchKey !== desiredKey on the fallback, so the
                // section refetches (showing loading, not stale empty). Doing it HERE
                // (on error) rather than at request START avoids a mid-flight key→null
                // that would spuriously re-trigger the fetch effect on every switch.
                dispatch(setFetchKey({ key: '_allPracticesClinicianFetchKey', value: null }));
            } else {
                // Default slot. In practice this else is unreachable today: every
                // dispatchSlotError call site (the 401/403 branch above, the
                // /practice-user/ 5xx branch, and the outer catch below) is guarded
                // by isAllPractices, so dispatchSlotError is never invoked for the
                // default slot. The default slot's REAL failure semantics live
                // elsewhere in this thunk:
                //   - 401/403 and a non-2xx /practice-user/ response fall through to
                //     the empty-practitioners branch → silent empty (data:[], no
                //     per-slot `error` field, isLoading:false), and DOES commit the
                //     fetch key.
                //   - A flat-STAGE failure (5xx/network, via the shared paged helper)
                //     now throws all-or-nothing; the outer catch's default-slot
                //     branch dispatches this same empty shape AND a global snackbar
                //     via updateError (the thunk's only updateError dispatch — not
                //     silent), and does NOT commit `_clinicianFetchKey`. Deliberate,
                //     shared with the allPractices slot.
                // This else just mirrors the empty-data shape defensively in case a
                // future call site ever reaches it with isAllPractices=false.
                dispatch(setClinicianConversion({ data: [], treatments: [], teamTotals: {}, isLoading: false }));
            }
        };
        const commitFetchKey = () => {
            if (!isCurrent()) return;
            const key = isAllPractices ? '_allPracticesClinicianFetchKey' : '_clinicianFetchKey';
            dispatch(setFetchKey({ key, value: requestKey }));
        };

        if (isAllPractices) {
            dispatch(setAllPracticesClinicianConversionLoading(true));
            // Clear this slot's own prior error immediately — a NEW current request
            // (this one) shouldn't leave a stale error card showing under its own
            // loading state. A superseded fetch never reaches here with a stale
            // token, so this can't clobber a newer request's error. Also clear any
            // prior partial-coverage info so a NEW selection never shows stale
            // "couldn't load" practices from a previous fetch while loading.
            dispatch(setAllPracticesClinicianConversion({
                error: null, errorRequestKey: null, failedPractices: [],
            }));
        } else {
            dispatch(setClinicianConversionLoading(true));
        }

        // ---- Multi-practice union path (All Practices slot, 2+ ids) ----
        if (isUnion) {
            try {
                // One practice at a time (CLINICIAN_PRACTICE_CONCURRENCY) — see the
                // 2026-10-06 incident note on CLINICIAN_STAGE_REQUEST_OPTIONS.
                const settled = await mapWithConcurrency(ids, CLINICIAN_PRACTICE_CONCURRENCY, async (pid) => {
                    try {
                        if (!isCurrent()) return { pid, status: 'superseded' };
                        const result = await fetchOnePracticeClinicianData(pid, dateRange, isCurrent);
                        return { pid, ...result };
                    } catch (e) {
                        // Isolate a single practice's hard failure (5xx / network /
                        // zero-progress on its own paged stages) — it must NOT fail
                        // the whole union; the other selected practices' data is
                        // still valid and should still render. Logged for
                        // diagnostics; surfaced to the user only if EVERY practice
                        // fails (see below).
                        if (e?.message !== '__superseded__' && e?.message !== '__sibling_aborted__') {
                            console.error(`Failed to fetch clinician conversion data for practice ${pid}`, e);
                        }
                        return { pid, status: 'error', message: e?.message };
                    }
                });
                if (!isCurrent()) return; // superseded — dispatch nothing further

                const okResults = settled.filter((r) => r.status === 'ok');

                // Coverage summary — for every selected practice that did NOT
                // contribute ok data, classify a SAFE reason: 'no_access' → 'access';
                // 'error' or ('no_data' && notOk, a resolved hard failure) → 'error'.
                // A 'no_data' && !notOk practice is authorized-but-genuinely-empty —
                // NOT a failure (it's honestly covered-with-zero; the Practice-scope
                // filter above already surfaces it as an option). NEVER include any
                // inaccessible practice's data — reason string only, no data leak.
                const failedPractices = settled
                    .filter((r) => r.status !== 'ok' && !(r.status === 'no_data' && !r.notOk))
                    .map((r) => ({
                        practiceId: r.pid,
                        practiceName: practiceNames?.[r.pid] ?? String(r.pid),
                        reason: r.status === 'no_access' ? 'access' : 'error',
                    }));
                const coveredCount = settled.filter(
                    (r) => r.status === 'ok' || (r.status === 'no_data' && !r.notOk)
                ).length;

                if (okResults.length === 0) {
                    // Every selected practice failed to yield data. Distinguish a
                    // genuine "nobody has clinician data" (no_access/no_data only)
                    // from at least one real fetch failure (error/notOk) so the
                    // section shows an error card (retryable) rather than a
                    // misleading silent "No Conversion Data" empty state.
                    const anyHardFailure = settled.some(
                        (r) => r.status === 'error' || (r.status === 'no_data' && r.notOk)
                    );
                    if (anyHardFailure) {
                        dispatchSlotError('Failed to load clinician conversion data for the selected practices.');
                        return;
                    }
                    dispatchEmptyOrData({
                        data: [], treatments: [], teamTotals: {}, isLoading: false,
                        failedPractices, selectedCount: ids.length, coveredCount,
                    });
                    commitFetchKey();
                    return;
                }

                // Union: concat (never merge-by-identity) — row identity is
                // practice + practitioner, so the SAME practice_user.id space
                // from two different practices must stay as two separate rows,
                // and even a same-named practitioner working at two practices
                // must never collapse into one.
                const unionData = [];
                const treatmentNameSet = new Set();
                okResults.forEach(({ pid, data, treatments }) => {
                    const pName = practiceNames?.[pid] ?? String(pid);
                    data.forEach((row) => {
                        unionData.push({ ...row, practiceId: pid, practiceName: pName });
                    });
                    treatments.forEach((t) => treatmentNameSet.add(t.name));
                });
                const treatmentList = Array.from(treatmentNameSet).map((tName) => ({
                    id: tName,
                    name: tName,
                    color: '#6366f1',
                }));
                if (treatmentList.length === 0) {
                    treatmentList.push({ id: 'all', name: 'All Treatments', color: '#6366f1' });
                }

                const teamTotals = computeClinicianTeamTotals(unionData);

                if (!isCurrent()) return; // superseded — dispatch nothing further
                dispatchEmptyOrData({
                    data: unionData,
                    treatments: treatmentList,
                    teamTotals,
                    isLoading: false,
                    failedPractices,
                    selectedCount: ids.length,
                    coveredCount,
                });
                commitFetchKey();
            } catch (error) {
                // Defensive — fetchOnePracticeClinicianData failures are already
                // caught per-practice above, so this only guards a genuinely
                // unexpected error in the merge/aggregation step itself.
                if (error?.message === '__superseded__' || !isCurrent()) return;
                console.error('Failed to merge clinician conversion data across practices', error);
                dispatchSlotError('Failed to load clinician conversion data for the selected practices.');
            }
            return;
        }

        // ---- Single-practice path (unchanged behaviour) ----
        try {
            const result = await fetchOnePracticeClinicianData(practiceId, dateRange, isCurrent);
            if (result.status === 'superseded') return; // dispatch nothing further

            if (result.status === 'no_access') {
                // All Practices slot surfaces this as a visible error card (401/403
                // after a token refresh means genuinely no access) rather than a
                // silent empty state.
                if (isAllPractices) {
                    dispatchSlotError('You do not have access to this practice’s clinician data.');
                } else {
                    dispatchEmptyOrData({ data: [], treatments: [], teamTotals: {}, isLoading: false });
                }
                return;
            }

            if (result.status === 'no_data') {
                // Any other resolved failure (5xx, network-level {ok:false,status:0}) on
                // the practice-user fetch — All Practices surfaces it; default slot
                // preserves its existing behaviour (silent empty + committed key).
                if (isAllPractices && result.notOk) {
                    dispatchSlotError(`Failed to load practitioners (status ${result.httpStatus ?? 0}).`);
                    return;
                }
                // Intentional empty state (genuinely no practitioners) — commit the fetch
                // key so this doesn't refetch-loop on every render; errors above never
                // commit a key, so they remain distinguishable/retryable.
                dispatchEmptyOrData({ data: [], treatments: [], teamTotals: {}, isLoading: false });
                commitFetchKey();
                return;
            }

            // result.status === 'ok'
            const teamTotals = computeClinicianTeamTotals(result.data);
            if (!isCurrent()) return; // superseded — dispatch nothing further
            dispatchEmptyOrData({
                data: result.data,
                treatments: result.treatments,
                teamTotals,
                isLoading: false,
            });
            commitFetchKey();
        } catch (error) {
            if (error?.message === '__superseded__' || !isCurrent()) return; // dispatch nothing
            console.error('Failed to fetch clinician conversion data', error);
            if (isAllPractices) {
                // '__sibling_aborted__' is the sentinel a sibling stage throws when it
                // bails out after a co-sibling's real failure — it should never itself
                // become the user-facing message (in practice Promise.all always
                // settles on the genuine first failure first, since that failure is
                // what set siblingFailed; this is a defensive fallback only).
                const message = (error?.message && error.message !== '__sibling_aborted__')
                    ? error.message
                    : 'Failed to load clinician conversion data.';
                dispatchSlotError(message);
            } else {
                // Default slot preserves its existing failure semantics — empty data,
                // no per-slot error field, global snackbar. Only the stale-response
                // guard (isCurrent gating above) is new for this slot.
                dispatch(setClinicianConversion({
                    data: [],
                    treatments: [],
                    teamTotals: {},
                    isLoading: false,
                }));
                dispatch(updateError({ message: 'Failed to load clinician conversion data', success: false }));
            }
        }
    };
}

// Fetch TCO/User performance data for the TCO Performance Tracker
// Shows TCOs and Users (non-dentists) with their booking performance
// Also stores lead IDs for each metric to enable click-to-view-details functionality
export function fetchTcoPerformanceData(practiceIdOrIds, dateRange) {
    // NOTE: per-user drilldown (/practice-user/{id}/leads/) is scoped to a
    // single practice. For multi-practice users we fetch the primary practice's
    // TCO/User leads; practice-user list union across practices is handled via
    // practice__in. Full cross-practice TCO metrics need lead-level aggregation
    // that doesn't exist on this endpoint yet.
    const ids = toPracticeIds(practiceIdOrIds);
    const practiceId = ids[0];
    const practiceUserQuery = ids.length > 1 ? `?practice__in=${ids.join(',')}` : '';
    return async (dispatch) => {
        if (!practiceId) return;
        if (!hasFeatureAccess('analytics-reports')) return;
        dispatch(setTcoPerformanceLoading(true));
        const baseUrl = `/api/v1/practices/${practiceId}/reports`;

        try {
            // Fetch all practice users (single-practice scope — the
            // practice__in query param was retired when the analytics
            // header's selector was hidden pending option D).
            const practitionersRes = await callApi(`${baseUrl}/practice-user/`);

            // Handle permission errors gracefully — user may not have access to this practice
            if (practitionersRes?.status === 403 || practitionersRes?.status === 401) {
                dispatch(setTcoPerformance({ data: [], teamTotals: {}, isLoading: false }));
                return;
            }

            const practitionersRaw = practitionersRes?.data;
            const allPractitioners = Array.isArray(practitionersRaw)
                ? practitionersRaw
                : (practitionersRaw?.results || []);

            // Filter to only include TCOs and Users (not Practitioners/Dentists)
            const tcoUsers = allPractitioners.filter((p) =>
                p.role === 'TCO' || p.role === 'USER' || p.role === 'ADMIN'
            );

            if (tcoUsers.length === 0) {
                dispatch(setTcoPerformance({
                    data: [],
                    teamTotals: {},
                    isLoading: false,
                }));
                return;
            }

            // Fetch leads for each TCO/User in parallel (limit to first 20)
            const usersToFetch = tcoUsers.slice(0, 20);
            const leadsPromises = usersToFetch.map((p) => {
                const practiceUserId = p.id;
                return callApi(
                    `${baseUrl}/practice-user/${practiceUserId}/leads/?created_at__gte=${encodeURIComponent(dateRange.start)}&created_at__lte=${encodeURIComponent(dateRange.end)}&limit=1000`
                ).catch(() => ({ data: { results: [] } }));
            });

            // Fetch lead notes data: unique leads contacted per user + channel breakdown
            const notesCountUrl = `${baseUrl}/leads/?group_by[lead_notes__created_by]&aggregate[Count]=id&created_at__gte=${encodeURIComponent(dateRange.start)}&created_at__lte=${encodeURIComponent(dateRange.end)}`;
            const notesChannelUrl = `${baseUrl}/leads/?group_by[lead_notes__created_by][lead_notes__contacted_via]&aggregate[Count]=id&created_at__gte=${encodeURIComponent(dateRange.start)}&created_at__lte=${encodeURIComponent(dateRange.end)}`;

            const [leadsResponses, notesCountRes, notesChannelRes] = await Promise.all([
                Promise.all(leadsPromises),
                callApi(notesCountUrl).catch(() => null),
                callApi(notesChannelUrl).catch(() => null),
            ]);

            // Build mapping from user ID → practice_user ID
            const userIdToPracticeUserId = {};
            usersToFetch.forEach((p) => {
                if (p.user?.id) userIdToPracticeUserId[p.user.id] = p.id;
            });

            // Parse notes count: [{ lead_notes__created_by: 42, count_id: 15 }, ...]
            const notesCountData = notesCountRes?.data?.data || notesCountRes?.data || [];
            const notesCountByPracticeUser = {};
            if (Array.isArray(notesCountData)) {
                notesCountData.forEach((item) => {
                    const userId = item.lead_notes__created_by;
                    const count = item.count_id || 0;
                    if (userId != null) {
                        const practiceUserId = userIdToPracticeUserId[userId];
                        if (practiceUserId) {
                            notesCountByPracticeUser[practiceUserId] = (notesCountByPracticeUser[practiceUserId] || 0) + count;
                        }
                    }
                });
            }

            // Parse channel breakdown: [{ lead_notes__created_by: 42, lead_notes__contacted_via: "PHONE", count_id: 8 }, ...]
            const notesChannelData = notesChannelRes?.data?.data || notesChannelRes?.data || [];
            const contactMethodsByPracticeUser = {};
            if (Array.isArray(notesChannelData)) {
                notesChannelData.forEach((item) => {
                    const userId = item.lead_notes__created_by;
                    const channel = item.lead_notes__contacted_via;
                    const count = item.count_id || 0;
                    if (userId != null && channel) {
                        const practiceUserId = userIdToPracticeUserId[userId];
                        if (practiceUserId) {
                            if (!contactMethodsByPracticeUser[practiceUserId]) {
                                contactMethodsByPracticeUser[practiceUserId] = {};
                            }
                            contactMethodsByPracticeUser[practiceUserId][channel] = (contactMethodsByPracticeUser[practiceUserId][channel] || 0) + count;
                        }
                    }
                });
            }

            // Process lead data for each TCO/User
            const userData = [];

            usersToFetch.forEach((user, index) => {
                const practiceUserId = user.id;
                const name = `${user.user?.first_name || ''} ${user.user?.last_name || ''}`.trim() || 'Unknown';
                const leadsRaw = leadsResponses[index]?.data;
                const leadTreatments = Array.isArray(leadsRaw) ? leadsRaw : (leadsRaw?.results || []);

                // Initialize user stats with lead ID arrays
                const userStats = {
                    id: practiceUserId,
                    name,
                    role: user.role,
                    avatar: user.user?.avatar,
                    email: user.user?.email,
                    // Metrics for TCOs with lead IDs
                    patientsContacted: 0,
                    patientsContactedIds: [],
                    consultationsBooked: 0,
                    consultationsBookedIds: [],
                    consultationsAttended: 0,
                    consultationsAttendedIds: [],
                    treatmentStarted: 0,
                    treatmentStartedIds: [],
                    revenue: 0,
                    // Contact activity from lead notes
                    leadsContacted: notesCountByPracticeUser[practiceUserId] || 0,
                    contactMethods: contactMethodsByPracticeUser[practiceUserId] || {},
                };

                // Process each lead treatment
                leadTreatments.forEach((lt) => {
                    const {status} = lt;
                    const price = parseFloat(lt.custom_price || lt.price || 0);
                    const leadId = lt.lead?.id || lt.lead_id;

                    // Count as patient contacted (any lead assigned)
                    userStats.patientsContacted += 1;
                    if (leadId && !userStats.patientsContactedIds.includes(leadId)) {
                        userStats.patientsContactedIds.push(leadId);
                    }

                    // CONSULTATION_BOOKED or further = patient booked in
                    if (['CONSULTATION_BOOKED', 'CONSULTATION_ATTENDED', 'TREATMENT_STARTED', 'CLOSED'].includes(status)) {
                        userStats.consultationsBooked += 1;
                        if (leadId && !userStats.consultationsBookedIds.includes(leadId)) {
                            userStats.consultationsBookedIds.push(leadId);
                        }
                    }

                    // CONSULTATION_ATTENDED or further
                    if (['CONSULTATION_ATTENDED', 'TREATMENT_STARTED', 'CLOSED'].includes(status)) {
                        userStats.consultationsAttended += 1;
                        if (leadId && !userStats.consultationsAttendedIds.includes(leadId)) {
                            userStats.consultationsAttendedIds.push(leadId);
                        }
                    }

                    // Only TREATMENT_STARTED counts toward treatmentStarted
                    // CLOSED leads already completed — don't inflate the started count
                    if (status === 'TREATMENT_STARTED') {
                        userStats.treatmentStarted += 1;
                        userStats.revenue += price;
                        if (leadId && !userStats.treatmentStartedIds.includes(leadId)) {
                            userStats.treatmentStartedIds.push(leadId);
                        }
                    }

                    // CLOSED leads still count their revenue
                    if (status === 'CLOSED') {
                        userStats.revenue += price;
                    }
                });

                userData.push(userStats);
            });

            // Calculate rates and format final data
            const processedData = userData
                .map((u) => {
                    const bookingRate = u.patientsContacted > 0
                        ? ((u.consultationsBooked / u.patientsContacted) * 100).toFixed(1)
                        : '0';
                    const showUpRate = u.consultationsBooked > 0
                        ? ((u.consultationsAttended / u.consultationsBooked) * 100).toFixed(1)
                        : '0';
                    const conversionRate = u.consultationsAttended > 0
                        ? ((u.treatmentStarted / u.consultationsAttended) * 100).toFixed(1)
                        : '0';

                    return {
                        ...u,
                        bookingRate,
                        showUpRate,
                        conversionRate,
                    };
                })
                .filter((u) => u.patientsContacted > 0)
                .sort((a, b) => b.consultationsBooked - a.consultationsBooked);

            // Calculate team totals
            const teamTotals = {
                patientsContacted: 0,
                consultationsBooked: 0,
                consultationsAttended: 0,
                treatmentStarted: 0,
                revenue: 0,
                leadsContacted: 0,
            };
            processedData.forEach((u) => {
                teamTotals.patientsContacted += u.patientsContacted;
                teamTotals.consultationsBooked += u.consultationsBooked;
                teamTotals.consultationsAttended += u.consultationsAttended;
                teamTotals.treatmentStarted += u.treatmentStarted;
                teamTotals.revenue += u.revenue;
                teamTotals.leadsContacted += u.leadsContacted;
            });
            teamTotals.bookingRate = teamTotals.patientsContacted > 0
                ? ((teamTotals.consultationsBooked / teamTotals.patientsContacted) * 100).toFixed(1)
                : '0';
            teamTotals.showUpRate = teamTotals.consultationsBooked > 0
                ? ((teamTotals.consultationsAttended / teamTotals.consultationsBooked) * 100).toFixed(1)
                : '0';
            teamTotals.conversionRate = teamTotals.consultationsAttended > 0
                ? ((teamTotals.treatmentStarted / teamTotals.consultationsAttended) * 100).toFixed(1)
                : '0';

            dispatch(setFetchKey({ key: '_tcoFetchKey', value: `${practiceId}|${dateRange.start}|${dateRange.end}` }));
            dispatch(setTcoPerformance({
                data: processedData,
                teamTotals,
                isLoading: false,
            }));
        } catch (error) {
            console.error('Failed to fetch TCO performance data', error);
            dispatch(setTcoPerformance({
                data: [],
                teamTotals: {},
                isLoading: false,
            }));
            dispatch(updateError({ message: 'Failed to load TCO performance data', success: false }));
        }
    };
}

// Name of a PracticeUser-shaped assignee. The API nests the person under
// `user` ({ id, user: { first_name, last_name } }), so reading first_name /
// full_name off the assignee itself always came back empty ("-" in the table).
export const resolveAssigneeName = (assignee) => {
    if (!assignee || typeof assignee !== 'object') return '';
    const person = assignee.user && typeof assignee.user === 'object' ? assignee.user : assignee;
    return (person.full_name
        || `${person.first_name || ''} ${person.last_name || ''}`.trim()
        || assignee.full_name
        || person.name
        || '').trim();
};

// Map PracticeUser id -> PracticeUser, for resolving assignees the API returns as plain ids.
export const buildAssigneeLookup = (users = []) => {
    const map = {};
    (users || []).forEach((u) => { if (u?.id != null) map[String(u.id)] = u; });
    return map;
};

// First assignee found on the lead (assignees, assigned_to), then on its treatments.
// The archived API returns plain ids (assigned_to: 517, assignees: [517]); those are
// resolved through `lookup` (PracticeUser id -> PracticeUser) when provided.
export const resolveLeadAssignee = (lead, lookup = {}) => {
    const candidates = [
        // Lead-level first: lead.assignees, then lead.assigned_to, then treatment-level.
        ...(lead?.assignees || []),
        lead?.assigned_to,
        ...(lead?.lead_treatments || []).flatMap((lt) => [lt?.assigned_to, ...(lt?.assignees || [])]),
    ].map((a) => ((typeof a === 'number' || typeof a === 'string') ? lookup?.[String(a)] : a));
    const found = candidates.find((a) => a && typeof a === 'object' && resolveAssigneeName(a));
    return found || null;
};

// Plain (non-object) assignee ids on a lead, so we know whether a users lookup is needed.
const plainAssigneeIds = (lead) => [
    ...(lead?.assignees || []),
    lead?.assigned_to,
    ...(lead?.lead_treatments || []).flatMap((lt) => [lt?.assigned_to, ...(lt?.assignees || [])]),
].filter((a) => typeof a === 'number' || typeof a === 'string');

// Archived date as a normalised ISO string (API sends "05-10-2026 18:44:43Z").
export const normalizeApiDateIso = (value) => {
    const parsed = parseApiDate(value);
    return parsed && parsed.isValid() ? parsed.toISOString() : null;
};

// Archived MoM delta. prev === null (previous-period fetch failed) -> no delta at all.
export const computeArchivedMomChange = (current, prev) => {
    if (prev === null || prev === undefined) return null;
    if (prev > 0) {
        const change = current - prev;
        const pct = ((change / prev) * 100).toFixed(1);
        let direction = 'flat';
        if (change > 0) direction = 'up';
        else if (change < 0) direction = 'down';
        return { count: change, pct: `${change >= 0 ? '+' : ''}${pct}%`, direction };
    }
    return current > 0 ? { count: current, pct: 'New', direction: 'up' } : null;
};

// Comparison-type change only: re-fetch JUST the previous-period archived count and
// recompute momChange against the already-loaded current count (no archived-list refetch).
export const fetchLostLeadsPreviousPeriod = (practiceIds, dateRange, comparisonType = 'period') => async (dispatch, getState) => {
    const ids = toPracticeIds(practiceIds);
    if (ids.length === 0) return;
    if (!hasFeatureAccess('analytics-reports')) return;
    const token = nextGroupedFetchToken('lostLeads');
    let prev = null;
    try {
        prev = await fetchArchivedCountByArchivedAt(ids, calculatePreviousPeriod(dateRange, comparisonType));
    } catch (error) {
        console.error('Failed to fetch previous-period archived count', error);
    }
    if (!isCurrentGroupedFetch('lostLeads', token)) return;
    const current = getState().analyticsReports.lostLeads;
    const currentCount = current?.kpis?.archivedCount ?? 0;
    dispatch(setLostLeadsData({ kpis: { ...current.kpis, momChange: computeArchivedMomChange(currentCount, prev) } }));
};

export const fetchLostLeadsData = (practiceIds, dateRange, comparisonType = 'period') => async (dispatch, getState) => {
    const ids = toPracticeIds(practiceIds);
    if (ids.length === 0) return false;
    if (!hasFeatureAccess('analytics-reports')) return false;
    const token = nextGroupedFetchToken('lostLeads');
    try {
        dispatch(setLostLeadsLoading(true));
        const startDate = dateRange.start?.split(' ')[0] || dateRange.start;
        const endDate = dateRange.end?.split(' ')[0] || dateRange.end;

        // Previous window: the SAME shared calculator the Overview uses (same
        // length as the current window, honours the comparison type), so the
        // delta here and on the Overview agree.
        const prevRange = calculatePreviousPeriod(dateRange, comparisonType);

        // Multi-practice strategy:
        //   - `/reports/*` endpoints accept practice__in → one request aggregates
        //     across all selected practices. Used for count/byReason/byTreatment/bySource.
        //   - `/archived/?limit=50` is under apps/leads/views.py (not a report view)
        //     and scopes by URL practice_pk only — we still fan out for the sample
        //     list. That's fine: it's 1 paginated request per practice, not 6.
        //   - summary endpoint for COUNT: practice__in is respected there too.
        const pSuffix = practiceInSuffix(ids);
        const primaryId = ids[0];
        const baseUrl = `/api/v1/practices/${primaryId}/reports`;

        // Partial render: allSettled so one failed sub-section doesn't blank
        // the whole Lost Leads page. Each piece is defaulted to a safe empty
        // value (0 / {}) and the thunk proceeds; sections that succeeded
        // render as usual, sections that failed show as "0" / empty.
        // Single archived_at source of truth: the list of leads ARCHIVED in the
        // period (fetchArchivedLeadsInPeriod paginates /archived/ with archived_at
        // bounds). Count, byReason/byTreatment/bySource, the detail table, trend
        // and avg-days are ALL derived from this one list, so every number on the
        // page agrees and matches the drilldown popup. (The old code mixed a
        // created_at summary count, created_at group_by breakdowns, and a 50-row
        // date-agnostic sample — three different bases that disagreed: count 36,
        // reasons 58, popup 209.)
        const settled = await Promise.allSettled([
            // [0] new leads created in period — the "Archive Rate" denominator
            callApi(`${baseUrl}/leads/?aggregate[Count]=id&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}${pSuffix}`),
            // [1] previous-period archived count (archived_at) for the MoM delta
            fetchArchivedCountByArchivedAt(ids, prevRange),
            // [2] archived-in-period lead list (archived_at), per-practice → concat
            Promise.all(ids.map((pid) => fetchArchivedLeadsInPeriod(pid, dateRange))),
        ]);
        // A newer request superseded this one while it was in flight.
        if (!isCurrentGroupedFetch('lostLeads', token)) return false;
        const pick = (result, fallback) => (result.status === 'fulfilled' ? result.value : fallback);
        // The archived list is the basis of every number on the page: if it failed,
        // say so (error toast) rather than rendering a page of zeros.
        if (settled[2].status === 'rejected') {
            console.error('[lost-leads] archived list failed', settled[2].reason);
            // The reason was previously console-only, so every Sentry report of
            // this landed with status/endpoint null and could not be triaged.
            const {reason} = settled[2];
            // Re-wrapped rather than passed raw, matching every other updateError
            // dispatcher here: errorHandler warns that a raw cause may be an
            // AxiosError carrying config.data (patient PII) and
            // config.headers.Authorization (JWT). The original stack is copied
            // across so Sentry still points at the real throw site.
            const safeCause = new Error(
                reason instanceof Error
                    ? reason.message
                    : String(reason?.message || reason || 'archived list rejected'),
            );
            if (reason instanceof Error && reason.stack) safeCause.stack = reason.stack;
            dispatch(updateError({
                message: 'Failed to load Lost Leads data',
                success: false,
                cause: safeCause,
                // debugInfo reads `endpoint` off this payload, not off `cause`.
                // `status` is deliberately omitted: the handler only captures 5xx
                // and network errors, so a real 4xx here would stop reaching Sentry.
                endpoint: reason?.endpoint ?? null,
            }));
            dispatch(setLostLeadsLoading(false));
            return;
        }
        const totalLeadsRes = pick(settled[0], { data: { data: {} } });
        // null = previous period unavailable -> momChange hidden (never compared against 0).
        const prevArchivedCount = settled[1].status === 'fulfilled' ? settled[1].value : null;
        const archivedLeads = settled[2].value.flat();
        const currentArchivedCount = archivedLeads.length;

        const failedSections = ['totalLeads', 'prevCount']
            .filter((_, i) => settled[i].status === 'rejected');
        if (failedSections.length > 0) {
            // eslint-disable-next-line no-console
            console.warn('[lost-leads] partial render, failed sections:', failedSections);
        }
        // The aggregate[Count]=id call (no group_by) returns the count as an
        // OBJECT at data.data.count_id — NOT an array, and NOT data.count. The
        // old dual-path (array[0].count_id || data.count) matched neither, so
        // totalLeadsCount was always 0 and the "Archive Rate" KPI rendered 0%.
        const totalLeadsRaw = totalLeadsRes?.data?.data;
        const totalLeadsCount = (Array.isArray(totalLeadsRaw) ? totalLeadsRaw[0]?.count_id : totalLeadsRaw?.count_id)
            ?? totalLeadsRes?.data?.count
            ?? 0;
        const VALID_ARCHIVE_REASONS_SET = new Set(VALID_ARCHIVE_REASONS);

        // byReason / byTreatment / bySource — all derived from the SAME archived_at
        // list, so their sums equal the archived count above and the drilldown
        // popup. (Previously these came from created_at group_by calls that
        // counted a different cohort and didn't reconcile with the count.)
        const { byReason, byTreatment, bySource } = summarizeArchivedLeads(archivedLeads);

        // Assignees arrive as plain PracticeUser ids: resolve them via the practice users
        // already in state, fetching the list only when some id is still unknown.
        let assigneeLookup = {};
        if (archivedLeads.some((l) => plainAssigneeIds(l).length > 0)) {
            // State lookup only for a single practice; multi-practice selections use the per-practice fetch.
            const inState = ids.length === 1 && getState ? selectPracticeUsersForPractice(getState(), ids[0]) : [];
            assigneeLookup = buildAssigneeLookup(inState);
            const unresolved = archivedLeads.some((l) => plainAssigneeIds(l).some((id) => !assigneeLookup[String(id)]));
            if (unresolved) {
                const lists = await Promise.all(ids.map(async (pid) => {
                    try {
                        const r = await callApi(`/api/v1/practices/${pid}/users/?fields={id,user{id,first_name,last_name}}&limit=500`);
                        if (r?.ok === false) throw new Error(`users ${r.status}`);
                        return r?.data?.results || [];
                    } catch (e) {
                        // eslint-disable-next-line no-console
                        console.warn('[lost-leads] practice users lookup failed; assignees may show "-":', e?.message || e);
                        return [];
                    }
                }));
                assigneeLookup = { ...buildAssigneeLookup(lists.flat()), ...assigneeLookup };
                // A newer request superseded this one while the users fetch was in flight.
                if (!isCurrentGroupedFetch('lostLeads', token)) return false;
            }
        }

        // Fix #6: Build byTeamMember from archived leads assigned_to field
        const teamMemberMap = {};
        archivedLeads.forEach((lead) => {
            const assignee = resolveLeadAssignee(lead, assigneeLookup);
            if (assignee) {
                const name = resolveAssigneeName(assignee) || 'Unknown';
                const key = assignee.id || name;
                if (!teamMemberMap[key]) {
                    teamMemberMap[key] = { name, count: 0, reasons: {} };
                }
                teamMemberMap[key].count += 1;
                // Find valid archive reason from notes first (matches API grouping), then fields
                const noteReason = lead.lead_notes?.find((n) => VALID_ARCHIVE_REASONS_SET.has(n.note))?.note;
                const reason = noteReason || lead.reason_for_archiving || lead.archived_reason;
                if (reason) {
                    teamMemberMap[key].reasons[reason] = (teamMemberMap[key].reasons[reason] || 0) + 1;
                }
            }
        });
        const byTeamMember = Object.values(teamMemberMap)
            .map((member) => {
                // Find the most common reason for this team member
                let topReason = '-';
                let topCount = 0;
                Object.entries(member.reasons).forEach(([reason, count]) => {
                    if (count > topCount) {
                        topCount = count;
                        topReason = formatArchiveReasonLabel(reason);
                    }
                });
                return { name: member.name, count: member.count, topReason };
            })
            .sort((a, b) => b.count - a.count);

        // Build detailed leads list — Fix #3: use archived_at for date
        const detailedLeads = archivedLeads.map((lead) => ({
            id: lead.id,
            name: `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'Unknown',
            // `treatment` on a lead_treatment is the name STRING (e.g. "Invisalign"),
            // not an object — so read it directly, with object/name fallbacks.
            treatment: (typeof lead.lead_treatments?.[0]?.treatment === 'string'
                ? lead.lead_treatments[0].treatment
                : (lead.lead_treatments?.[0]?.treatment?.name || lead.lead_treatments?.[0]?.treatment_name)) || '-',
            source: (typeof lead.lead_source === 'string' ? lead.lead_source : (lead.lead_source?.type || lead.lead_source?.name)) || UNKNOWN_SOURCE,
            reason: (() => { const r = resolveArchiveReason(lead, VALID_ARCHIVE_REASONS_SET); return r && VALID_ARCHIVE_REASONS_SET.has(r) ? r : NO_REASON_RECORDED; })(),
            date: normalizeApiDateIso(lead.archived_at) || normalizeApiDateIso(lead.created_at) || null,
            value: lead.lead_treatments?.reduce((sum, t) => sum + (parseFloat(t.custom_price ?? t.price) || 0), 0) || 0,
            assignedTo: resolveAssigneeName(resolveLeadAssignee(lead, assigneeLookup)) || '-',
        }));

        // Use summary API count (most accurate — not capped by limit), fallback to byReason sum, then archivedLeads.length
        const byReasonTotal = byReason.reduce((sum, r) => sum + r.count, 0);
        const totalLost = currentArchivedCount || byReasonTotal || archivedLeads.length;
        const topReasonItem = byReason[0];
        const lostRevenue = byTreatment.reduce((sum, t) => sum + (t.revenue || 0), 0);

        // Calculate real avgDaysToArchive from archived_at - created_at
        let avgDaysToArchive = 0;
        const validDays = [];
        archivedLeads.forEach((lead) => {
            if (lead.archived_at && lead.created_at) {
                const created = parseApiDate(lead.created_at);
                const archived = parseApiDate(lead.archived_at);
                if (created?.isValid() && archived?.isValid()) {
                    const diffDays = Math.max(0, archived.diff(created, 'day'));
                    validDays.push(diffDays);
                }
            }
        });
        if (validDays.length > 0) {
            avgDaysToArchive = Math.round(validDays.reduce((sum, d) => sum + d, 0) / validDays.length);
        }

        // Generate trend data using archived_at (when lead was lost)
        const trendMap = {};
        archivedLeads.forEach((lead) => {
            const dateField = lead.archived_at || lead.created_at;
            const parsed = parseApiDate(dateField);
            if (parsed?.isValid()) {
                const month = parsed.format('YYYY-MM');
                trendMap[month] = (trendMap[month] || 0) + 1;
            }
        });
        const trend = Object.entries(trendMap)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([month, count]) => ({
                month,
                label: dayjs(month, 'YYYY-MM').format('MMM YY'),
                count,
            }));

        // Fix #9: Archive rate = archived / total leads in period.
        // When the totalLeads call itself rejected (Promise.allSettled
        // partial failure), totalLeadsCount falls back to 0 — which would
        // render "0% archive rate" even when currentArchivedCount > 0,
        // misleading the user into thinking nothing was archived. Surface
        // null in that case so the UI can render "—"/"N/A" instead.
        const totalLeadsFailed = settled[0]?.status === 'rejected';
        let archiveRate;
        if (totalLeadsFailed && currentArchivedCount > 0) {
            archiveRate = null;
        } else if (totalLeadsCount > 0) {
            archiveRate = parseFloat(((currentArchivedCount / totalLeadsCount) * 100).toFixed(1));
        } else {
            archiveRate = 0;
        }

        // Fix #7: MoM comparison
        const momChange = computeArchivedMomChange(currentArchivedCount, prevArchivedCount);

        dispatch(setLostLeadsData({
            kpis: {
                totalLost,
                topReason: topReasonItem?.label || 'N/A',
                avgDaysToArchive,
                lostRevenue,
                archiveRate,
                // Both operands, so the UI can show "55 ÷ 99 new leads" instead of an
                // unexplained percentage (archived leads need not be new this period).
                archivedCount: currentArchivedCount,
                newLeadsCount: totalLeadsFailed ? null : totalLeadsCount,
                momChange,
            },
            byReason,
            byTreatment,
            bySource,
            byTeamMember,
            trend,
            detailedLeads,
        }));
        return true;
    } catch (error) {
        console.error('Failed to fetch lost leads data', error);
        dispatch(updateError({ message: 'Failed to load Lost Leads data', success: false }));
        dispatch(setLostLeadsLoading(false));
        return false;
    }
};

// ─── Click Tracking helpers ───────────────────────────────────────────────────

/**
 * Derives a human-readable page label from a raw pathname.
 * Strips query strings and hash fragments before splitting to avoid leaking
 * PII or session tokens that may appear in URL params.
 * e.g. "/treatments/invisalign?ref=ad" → "Treatments Page"
 *      "/"                             → "Home Page"
 *      ""  / null                      → "Home Page"
 */
export function getPageLabel(rawPathname) {
    if (!rawPathname) return 'Home Page';
    // Strip query string and hash fragment (PII / redirect risk)
    const clean = rawPathname.split('?')[0].split('#')[0];
    const segment = clean.split('/')?.[1] || '';
    if (!segment) return 'Home Page';
    const titled = segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase();
    return `${titled} Page`;
}

/**
 * Extracts a human-readable traffic source label from the full page URL.
 * Priority: gclid/gad_source → Google Ads, fbclid → Facebook Ads, utm_source → mapped/raw, else Direct/Organic.
 */
function getTrafficSource(currentUrl) {
    if (typeof currentUrl !== 'string' || !currentUrl) return 'Direct / Organic';
    try {
        const url = new URL(currentUrl);
        const params = new URLSearchParams(url.search);
        const pathname = (url.pathname || '').toLowerCase();

        // Google Ads signals (any of these wins):
        //   - gclid / gad_source on the URL (paid click ID)
        //   - landing page slug contains /ad/ or /ads/ (dedicated PPC landing page, gclid-stripped)
        if (params.get('gclid') || params.get('gad_source')) return 'Google Ads';
        if (/\/ads?\//.test(pathname)) return 'Google Ads';

        if (params.get('fbclid')) return 'Facebook Ads';
        const utmSource = params.get('utm_source');
        if (utmSource) {
            const map = { local: 'Local / GMB', organic: 'Organic', newsletter: 'Newsletter', email: 'Email' };
            return map[utmSource.toLowerCase()] ?? utmSource;
        }
        return 'Direct / Organic';
    } catch {
        return 'Direct / Organic';
    }
}

/** Normalises a single analytics-events API record into the canonical shape. */
/**
 * Returns a lowercase, bare hostname for any URL-ish input, or '' when input
 * doesn't parse. Strips protocol, port, `www.`, and trailing slashes so that
 * comparisons between `https://www.aurora-dental.co.uk/contact` and
 * `aurora-dental.co.uk` succeed. Used by the click-tracking cross-tenant
 * safety filter to detect events that originated on a different practice's
 * website.
 */
function normaliseHost(input) {
    if (!input || typeof input !== 'string') return '';
    const trimmed = input.trim();
    if (!trimmed) return '';
    // Accept both bare hostnames ("aurora-dental.co.uk") and full URLs
    // ("https://www.aurora-dental.co.uk/contact"). URL() throws on bare hosts,
    // so prefix a scheme as a fallback.
    let host = '';
    try {
        host = new URL(trimmed).hostname;
    } catch {
        try {
            host = new URL(`https://${trimmed}`).hostname;
        } catch {
            return '';
        }
    }
    return host.toLowerCase().replace(/^www\./, '');
}

function normaliseEvent(apiRecord, type) {
    const metadata = apiRecord.metadata || {};
    const rawPath = (metadata.pathname || '/').split('?')[0].split('#')[0];

    // Timestamp resolution:
    //   1. metadata.timeStamp is ISO 8601 (e.g. "2025-06-22T09:25:23.428Z") — preferred
    //   2. created_at is DD-MM-YYYY HH:mm:ss.microsZ (NOT ISO) — parse via regex fallback
    const { iso, ms } = resolveEventTimestamp(apiRecord);

    // Capture practice scoping from whichever field the BE serializer exposes.
    // Used by the cross-tenant safety filter in fetchClickTrackingData below.
    const rawPractice = apiRecord.practice ?? apiRecord.practice_id
        ?? apiRecord.data?.practice ?? apiRecord.data?.practice_id ?? null;

    return {
        id: String(apiRecord.id ?? apiRecord.data?.id),
        type,
        practice: rawPractice == null ? null : String(rawPractice),
        occurredAt: iso,                                            // always ISO string or ''
        occurredAtMs: ms,                                           // epoch ms or NaN
        createdAtHuman: apiRecord.created_at_human ?? apiRecord.data?.created_at_human ?? '',
        pagePath: rawPath,
        pageLabel: getPageLabel(rawPath),
        deviceType: String(metadata.browserInfo || '').includes('iPhone') ? 'iPhone' : 'Browser',
        clickCount: typeof metadata.clickCount === 'number' ? metadata.clickCount : 1,
        source: getTrafficSource(metadata.currentUrl),
        currentUrl: metadata.currentUrl || '',
    };
}

function resolveEventTimestamp(apiRecord) {
    // Preferred: metadata.timeStamp is ISO 8601
    const iso1 = apiRecord?.metadata?.timeStamp;
    if (typeof iso1 === 'string' && iso1) {
        const ms = new Date(iso1).getTime();
        if (Number.isFinite(ms)) return { iso: iso1, ms };
    }
    // Fallback: parse DD-MM-YYYY HH:mm:ss(.SSSSSS)Z from created_at (top-level or nested)
    const raw = apiRecord?.created_at ?? apiRecord?.data?.created_at;
    if (typeof raw === 'string') {
        const m = raw.match(/^(\d{2})-(\d{2})-(\d{4})\s+(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?Z?$/);
        if (m) {
            const [, dd, mm, yyyy, hh, mi, ss, micros] = m;
            const millis = micros ? micros.slice(0, 3).padEnd(3, '0') : '000';
            const isoStr = `${yyyy}-${mm}-${dd}T${hh}:${mi}:${ss}.${millis}Z`;
            const ms = new Date(isoStr).getTime();
            if (Number.isFinite(ms)) return { iso: isoStr, ms };
        }
    }
    return { iso: '', ms: NaN };
}

/**
 * Computes KPIs over a merged, date-filtered event array.
 *
 * KPI values are EVENT COUNTS (not sum of clickCount).
 * Top page / top source use alphabetical tiebreaker for deterministic rendering.
 *
 * `formSubmissionsByPage` is the optional page-level aggregate (array of
 * { pagePath, submissions, ... }) so we can compute Top Converting Page
 * without the previous broken pattern of stamping ``formSubmissions`` on
 * every event row.
 *
 * Exported so ClickTracking.jsx can recompute KPIs after client-side source filtering.
 */
export function computeClickTrackingKpis(events, formSubmissionsByPage = []) {
    const phoneClicks = events.filter((e) => e.type === 'PHONE').length;
    const whatsappClicks = events.filter((e) => e.type === 'WHATSAPP').length;
    const totalClicks = events.length;

    // Most-frequent page path → human label (alphabetical tiebreaker)
    const pageFreq = {};
    events.forEach((e) => {
        pageFreq[e.pagePath] = (pageFreq[e.pagePath] || 0) + 1;
    });

    let topPage = null;
    let topPageCount = 0;
    Object.entries(pageFreq)
        .sort(([pathA], [pathB]) => pathA.localeCompare(pathB))
        .forEach(([path, cnt]) => {
            if (cnt > topPageCount) {
                topPageCount = cnt;
                topPage = getPageLabel(path);
            }
        });

    // Most-frequent source label (alphabetical tiebreaker)
    const sourceFreq = {};
    events.forEach((e) => {
        sourceFreq[e.source] = (sourceFreq[e.source] || 0) + 1;
    });

    let topSource = null;
    let topSourceCount = 0;
    Object.entries(sourceFreq)
        .sort(([a], [b]) => a.localeCompare(b))
        .forEach(([src, cnt]) => {
            if (cnt > topSourceCount) {
                topSourceCount = cnt;
                topSource = src;
            }
        });

    // Page with highest form submissions — read from the standalone
    // page-level aggregate (NOT per-event, which double-counted).
    let topConvertingPage = null;
    let topConvCount = 0;
    [...formSubmissionsByPage]
        .sort((a, b) => (a.pagePath || '').localeCompare(b.pagePath || '')) // alphabetical tiebreaker
        .forEach((bucket) => {
            if ((bucket.submissions || 0) > topConvCount) {
                topConvCount = bucket.submissions || 0;
                topConvertingPage = getPageLabel(bucket.pagePath) || bucket.pageLabel;
            }
        });

    return { totalClicks, phoneClicks, whatsappClicks, topPage, topSource, topConvertingPage };
}

// ─── Click Tracking thunk ─────────────────────────────────────────────────────

/**
 * Fetches phone and WhatsApp click events in parallel for the given practice
 * and date range. Uses Promise.allSettled so a single-endpoint failure still
 * renders partial data.
 *
 * Date filter strategy: client-side only.
 * The analytics-events endpoint silently ignores `created_at__gte`/`lte`
 * params (returns HTTP 200 with all events regardless). Confirmed by
 * backend-testing task #3. We therefore fetch all events and filter by date
 * after merge.
 *
 * @param {number} practiceId
 * @param {{ start: string, end: string }} dateRange  'YYYY-MM-DD HH:mm:ss' strings
 */
export function fetchClickTrackingData(practiceId, { start, end, typeFilter = 'all' } = {}) {
    return async (dispatch, getState) => {
        if (!practiceId) return;

        // typeFilter scopes the server-side fetch: 'all' hits both
        // phone + whatsapp endpoints in parallel; 'phone' or 'whatsapp'
        // hits only the matching one. Keeps the dropdown filter
        // server-side rather than dropping events client-side after
        // wasted bandwidth.
        const normalizedType = ['all', 'phone', 'whatsapp'].includes(typeFilter)
            ? typeFilter
            : 'all';
        const fetchKey = `${practiceId}|${start}|${end}|${normalizedType}`;
        const current = getState().analyticsReports.clickTracking._fetchKey;
        if (current === fetchKey) return; // already up to date

        dispatch(clickTrackingLoading());

        const base = `/api/v1/practices/${practiceId}/analytics-events/`;

        // Date-range params — backend AnalyticsEventFilter now supports
        // created_at__gte / created_at__lte (apps/analytics/filters.py).
        // Passing them server-side means we no longer fall off the
        // pagination cliff (DRF default 50/page) on high-volume
        // practices — the date toggle would otherwise be cosmetic
        // because every "Last X" range would return the same most-
        // recent 50 events.
        //
        // ``limit=5000`` is a safety upper bound — bigger than any
        // realistic per-practice click volume in a 30-day window
        // (top performers do hundreds per month, not thousands).
        // If a practice ever exceeds this, the dashboard will still
        // render — just clamped to the most recent 5000, which is
        // plenty for trend visualisation.
        const dateParams = [];
        if (start) dateParams.push(`created_at__gte=${encodeURIComponent(start)}`);
        if (end) dateParams.push(`created_at__lte=${encodeURIComponent(end)}`);
        const dateQs = dateParams.length ? `&${dateParams.join('&')}` : '';

        // Build the list of click-event fetches conditional on
        // typeFilter. The leads query is unconditional — it powers
        // attribution computations regardless of which click type is
        // shown.
        const phonePromise = (normalizedType === 'all' || normalizedType === 'phone')
            ? callApi(`${base}?name=practice_phone_number_clicked&ordering=-created_at&limit=5000${dateQs}`)
            : Promise.resolve({ ok: true, data: { results: [] } });
        const waPromise = (normalizedType === 'all' || normalizedType === 'whatsapp')
            ? callApi(`${base}?name=practice_whatsapp_button_clicked&ordering=-created_at&limit=5000${dateQs}`)
            : Promise.resolve({ ok: true, data: { results: [] } });

        const [phoneResult, waResult, leadsResult] = await Promise.allSettled([
            phonePromise,
            waPromise,
            // created_at__gte/__lte are what LeadFilter actually declares;
            // created_after/created_before were silently dropped by django-filter,
            // so attribution was computed against the 1000 newest ALL-TIME leads.
            callApi(`/api/v1/practices/${practiceId}/leads/?limit=1000&ordering=-created_at&created_at__gte=${encodeURIComponent(start)}&created_at__lte=${encodeURIComponent(end)}&fields={lead_source}`),
        ]);

        const phoneOk = phoneResult.status === 'fulfilled' && phoneResult.value?.ok;
        const waOk = waResult.status === 'fulfilled' && waResult.value?.ok;

        if (!phoneOk && !waOk) {
            // Both click-event endpoints failed — surface a blocking error
            const msg = phoneResult.reason?.message || waResult.reason?.message || "Couldn't load click events";
            dispatch(clickTrackingError(msg));
            return;
        }

        // Normalise whichever click-event endpoints succeeded
        const phoneEvents = phoneOk
            ? (phoneResult.value.data?.results || []).map((r) => normaliseEvent(r, 'PHONE'))
            : [];
        const waEvents = waOk
            ? (waResult.value.data?.results || []).map((r) => normaliseEvent(r, 'WHATSAPP'))
            : [];

        // Cross-tenant safety filter (defense-in-depth).
        //
        // The endpoint URL contains the practiceId, but if the BE viewset is not
        // actually scoping AnalyticsEvent queries by practice (suspected — Aurora
        // Dental users have been seeing other practices' URLs in the Page tooltip),
        // we drop any record whose practice field doesn't match the requested
        // practiceId. If the BE doesn't serialize a practice field at all we have
        // no way to verify by practice id directly, so we fall back to a hostname
        // match against the practice's registered website (PracticeContact.website)
        // and drop events whose currentUrl host doesn't match. This catches the
        // observed WhatsApp leak: practices appear to share an AnalyticsTag code
        // in the tracking snippet, so events from other practices' websites land
        // under the wrong practice tag — the originating URL is the only signal
        // we have to detect this.
        const practiceIdStr = String(practiceId);
        const merged = [...phoneEvents, ...waEvents];
        const hasPracticeField = merged.some((e) => e.practice != null);
        if (!hasPracticeField && merged.length > 0) {
            // eslint-disable-next-line no-console
            console.warn(
                '[ClickTracking] AnalyticsEvent records have no practice field — falling back to currentUrl hostname match. Backend should expose tag.practice on the read serializer and reject cross-tag event creation.'
            );
        }
        const tenantSafeById = merged.filter((e) => e.practice == null || e.practice === practiceIdStr);

        // Hostname fallback — resolve the practice's registered website from the
        // already-loaded practice list and normalise to a bare hostname (strip
        // protocol, www., trailing slash).
        const practiceRow = (getState().practiceTreatmentList?.practiceTreatment?.results || [])
            .find((p) => String(p.id) === practiceIdStr);
        const expectedHost = normaliseHost(practiceRow?.contact?.website);
        const tenantSafe = expectedHost
            ? tenantSafeById.filter((e) => {
                const host = normaliseHost(e.currentUrl);
                if (!host) return true; // no URL captured → can't verify, keep
                return host === expectedHost;
            })
            : tenantSafeById;
        const droppedCount = merged.length - tenantSafe.length;
        if (droppedCount > 0) {
            // eslint-disable-next-line no-console
            console.warn(
                `[ClickTracking] Dropped ${droppedCount} cross-tenant event(s) from analytics-events response (expected practice ${practiceIdStr}, expected host ${expectedHost || 'unknown'}). Backend tracking snippet/tag association needs audit.`
            );
        }

        // Client-side date filter — defensive second pass. The server
        // now honours ``created_at__gte`` / ``created_at__lte`` (see
        // AnalyticsEventFilter in apps/analytics/filters.py), so the
        // happy path is already filtered upstream. This local pass
        // catches edge cases: events with malformed timestamps that
        // we'd rather keep visible, off-by-one boundaries between
        // wall-clock and stored UTC, etc.
        //
        // Bug fix retained: new Date('YYYY-MM-DD HH:mm:ss') returns
        // NaN in Safari — it only parses ISO 8601 with a T separator.
        // dayjs with customParseFormat handles both space- and
        // T-separated formats so the space format moment produces in
        // ClickTracking.jsx is read cross-browser.
        const rawStartMs = start ? dayjs(start, ['YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DD HH:mm:ss']).valueOf() : NaN;
        const rawEndMs = end ? dayjs(end, ['YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DD HH:mm:ss']).valueOf() : NaN;
        const startMs = Number.isFinite(rawStartMs) ? rawStartMs : 0;
        const endMs = Number.isFinite(rawEndMs) ? rawEndMs : Infinity;
        const allEvents = tenantSafe.filter((e) => {
            if (!Number.isFinite(e.occurredAtMs)) return true; // keep events with no parseable timestamp
            return e.occurredAtMs >= startMs && e.occurredAtMs <= endMs;
        });

        // Sort merged result by occurredAt descending (most recent first)
        allEvents.sort((a, b) => (Number.isFinite(b.occurredAtMs) ? b.occurredAtMs : 0)
                               - (Number.isFinite(a.occurredAtMs) ? a.occurredAtMs : 0));

        // Build pathname → form-submission count map from ONLINE_FORM leads.
        // Only leads where lead_source.metadata.url_checker is a valid URL are counted.
        const leadsOk = leadsResult.status === 'fulfilled' && leadsResult.value?.ok;
        const formSubmissionsByPath = {};
        if (leadsOk) {
            (leadsResult.value.data?.results || []).forEach((lead) => {
                const urlChecker = lead.lead_source?.metadata?.url_checker;
                if (!urlChecker) return;
                try {
                    const pathname = new URL(urlChecker).pathname.split('?')[0].split('#')[0];
                    if (pathname) {
                        formSubmissionsByPath[pathname] = (formSubmissionsByPath[pathname] || 0) + 1;
                    }
                } catch {
                    // malformed URL — skip silently
                }
            });
        }

        // Per-page aggregate — clicks AND form submissions by pagePath.
        // Lives as derived state OUTSIDE the per-event list so the click
        // events table stays click-level and the form-submission card
        // stays page-level. They join on pagePath but are NEVER smeared
        // into the same row. (Previous behaviour stamped each event with
        // formSubmissions = pageCount, which double-counted on sum and
        // mis-suggested per-click attribution.)
        const pageBuckets = {};
        allEvents.forEach((e) => {
            const path = e.pagePath || '/';
            if (!pageBuckets[path]) {
                pageBuckets[path] = {
                    pagePath: path,
                    pageLabel: e.pageLabel || path,
                    clicks: 0,
                    submissions: 0,
                };
            }
            pageBuckets[path].clicks += 1;
        });
        // Add submissions to existing buckets, or create bucket if a page
        // has submissions but no clicks (so it still surfaces in the card).
        Object.entries(formSubmissionsByPath).forEach(([path, count]) => {
            if (!pageBuckets[path]) {
                pageBuckets[path] = {
                    pagePath: path,
                    pageLabel: path,
                    clicks: 0,
                    submissions: 0,
                };
            }
            pageBuckets[path].submissions = count;
        });
        const formSubmissionsByPage = Object.values(pageBuckets)
            .map((b) => ({
                ...b,
                // Clicks-to-submission rate. Capped at 100 so a page with
                // more submissions than clicks (rare but possible when the
                // form lives on a page that doesn't have phone/WA CTAs)
                // doesn't render >100% — display logic just shows "—".
                conversionRate: b.clicks > 0 ? (b.submissions / b.clicks) : null,
            }))
            // Sort: most submissions first, then most clicks. Pages with
            // ZERO submissions sort to the bottom so the card always
            // leads with actionable info.
            .sort((a, b) => {
                if (b.submissions !== a.submissions) return b.submissions - a.submissions;
                return b.clicks - a.clicks;
            });

        const kpis = computeClickTrackingKpis(allEvents, formSubmissionsByPage);

        // Partial-fail warnings — surfaced via error field when data is still usable
        const warnings = [];
        if (phoneOk && !waOk) warnings.push("Couldn't load WhatsApp events — phone data shown.");
        else if (!phoneOk && waOk) warnings.push("Couldn't load phone events — WhatsApp data shown.");
        if (!leadsOk) warnings.push('Form submission counts unavailable — click data shown.');

        const partialError = warnings.length > 0 ? warnings.join(' ') : null;

        dispatch(clickTrackingSuccess({
            events: allEvents,
            kpis,
            _fetchKey: fetchKey,
            formSubmissionsByPage,
        }));

        // Non-blocking partial warning stored after success so components see data + warning
        if (partialError) {
            dispatch(clickTrackingError(partialError));
        }
    };
}

export default slice.reducer;
